// from baseApi.ts
import { IdentifiableModel } from 'cs-kevlar-react/lib/es/base'
export type Guid = string
interface IValidatableObject {}
export type OptionalWithAlternateValue<T, K> = { [P in keyof T]: T[P] | K }

// ../Nebula.DTO/IncidentFilterConfigurationV1.cs
export interface IncidentFilterConfigurationV1 {
    dispatchAgencyLabel: string;
    responderAgencyLabel: string;
    responderEmergencyResourceTypeLabel: string;
    statusLabel: string;
    jurisdiction1Label: string;
    jurisdiction2Label: string;
    jurisdiction3Label: string;
    priorityLabel: string;
}

// ../Nebula.DTO/MediaUploadKeyV1.cs
/**
 * Response to a {@link MediaUploadRequestV1} with a valid upload
 * Uri and an expiration date for the Uri.
 */
export interface MediaUploadKeyV1 {
    /**
     * The associated Media row ID.
     */
    mediaId: string;
    /**
     * The cloud storage provider where the media will be uploaded to.
     */
    storageProvider: CloudProviderV1;
    /**
     * A valid upload Uri the media can be uploaded to.
     */
    uploadUri: object;
    /**
     * The expiration date for the upload Uri.
     */
    expiresOn: string;
}

// ../Nebula.DTO/DocumentSubscriptionActionV1.cs
export enum DocumentSubscriptionActionV1 {
    None = 0,
    Add = 1,
    Update = 2,
    Remove = 3,
    Link = 4,
}

// ../Nebula.DTO/InterfaceMetadataV1.cs
export interface InterfaceMetadataCatalogV1 {
    metadata: InterfaceMetadataV1[];
}

// ../Nebula.DTO/InterfaceMetadataV1.cs
export interface InterfaceMetadataV1 {
    customerInterfaceId: string;
    enabledFeatures: InterfaceFeatureV1[];
    properties: Record<InterfacePropertyV1, string>;
}

// ../Nebula.DTO/DeviceFeatureV1.cs
export enum DeviceFeatureV1 {
    None = 0,
    GeneralAvailability = 1,
    AddNarrativeToCfs = 2,
    Statusing = 3,
    UserAssignToEmergencyResource = 4,
    MediaUpload = 5,
    CaseSearch = 6,
    PositionsAvl = 7,
    UserLogoff = 8,
    AgencySpecificGlobalConfiguration = 9,
    LinkedDocumentRequests = 10,
    /**
     * If present, the device supports the push notification payload that contains platform specific UI keys.
     */
    UiNotificationPayload = 11,
    /**
     * Whether the device supports the message center
     */
    UserNotificationMessages = 12,
    /**
     * Indicates the device supports the new reference type authentication
     * tokens (en lieu of JWT tokens).
     */
    SupportsReferenceAuthenticationTokens = 13,
    /**
     * Declares the device supports receiving interconnected CAD incidents
     */
    ReceiveInterconnectedCad_Incidents = 14,
    /**
     * Declares that the device knows how to support multiple connectors for the agency
     */
    SupportsMultipleConnectors = 15,
    /**
     * Declares that the device knows how to handle receiving device registration information
     */
    SupportsOnDeviceRegistration = 16,
    /**
     * Declares that the device supports files attachments to documents.
     */
    Files = 17,
    /**
     * Declares that the device supports adding media to incidents.
     */
    AddMediaToIncident = 18,
    /**
     * Declares that the device supports signature collection.
     */
    SignatureCollection = 19,
}

// ../Nebula.DTO/MediaProcessRequestV1.cs
export interface MediaProcessRequestV1 {
    mediaId: string;
    externalAgencyId: string;
    documentType: DocumentTypeV1;
    externalDocumentId: string;
    externalUserId: string;
    deviceName: String;
    description: string;
    comments: string;
    encryptionKey: Int8Array;
    md5Checksum: Int8Array;
    fileExtension: string;
    mediaType: MediaTypeV1;
}

// ../Nebula.DTO/EmergencyResourceTypeV1.cs
export interface EmergencyResourceTypeV1 extends BaseDocumentModelV1 {
    externalId: string;
    description: string;
    sortOrder: number;
}

// ../Nebula.DTO/AuthenticationResponseV1.cs
export interface AuthenticationResponseV1 extends SourcedPayload {
    authenticationResult: AuthenticationResultTypeV1;
    message: string;
    user: UserV1;
    deviceId: string;
    accessToken: string;
    authenticatedCustomerInterfaceIds: string[];
}

// ../Nebula.DTO/GeoCoordinateV1.cs
export interface GeoCoordinateV1 {
    latitude: number;
    longitude: number;
    validationType: GeoCoordinateValidationTypeV1;
}

// ../Nebula.DTO/UserV1.cs
export interface UserV1 extends BaseDocumentModelV1 {
    name: string;
    fullName: string;
    emailAddresses: EmailAddressV1[];
    phones: PhoneV1[];
    ownerAgencyId: string;
    emergencyResourceId?: string;
    previouslyAssignedEmergencyResourceId?: string;
    permissions: string[];
    capabilities: UserCapabilitiesV1[];
}

// ../Nebula.DTO/FileTypeV1.cs
export enum FileTypeV1 {
    Image = 0,
    Video = 1,
    Audio = 2,
    Document = 3,
    MapLayer = 4,
}

// ../Nebula.DTO/CloudMediaStatusRequestV1.cs
/**
 * A request for CloudMedia upload statuses.
 */
export interface CloudMediaStatusRequestV1 {
    /**
     * A dictionary of CloudMedia IDs and their corresponding upload status in Nebula.
     */
    mediaUploadStatus: Record<string, DeviceMediaUploadStatusV1>;
}

// ../Nebula.DTO/DeviceVersionV1.cs
export enum DeviceVersionV1 {
    /**
     * Initial GA release version
     */
    v1_0_0 = 0,
}

// ../Nebula.DTO/CustomerCertificateV1.cs
export interface CustomerCertificateV1 {
    customerCertificateId: string;
    certificateType: CertificateTypeV1;
    fingerprint: Int8Array;
    publicKey: Int8Array;
    issuedOn: string;
    expiresOn: string;
    principal?: string;
}

// ../Nebula.DTO/CustomerCertificateV1.cs
export enum CertificateTypeV1 {
    Interface,
    Device,
    IdentityServer,
}

// ../Nebula.DTO/NarrativeV1.cs
export interface NarrativeV1 {
    timeEntered: string;
    userId?: string;
    editedBy: string;
    message: string;
}

// ../Nebula.DTO/ServerCertificateChallengeResponseV1.cs
export interface ServerCertificateChallengeResponseV1 extends SourcedPayload {
    publicKey: Int8Array;
    challenge: string;
    confirmedChallenge: string;
}

// ../Nebula.DTO/DevicePropertyV1.cs
export enum DevicePropertyV1 {
    AppVersion = 0,
    DeviceModel = 1,
    DeviceManufacturer = 2,
    OsVersion = 3,
    AppBrandingType = 4,
}

// ../Nebula.DTO/DataPairV1.cs
export interface DataPairV1 {
    label: string;
    value: string;
    sortOrder?: number;
}

// ../Nebula.DTO/UserAssignmentResponseV1.cs
export interface UserAssignmentResponseV1 extends SourcedPayload {
    assignmentResult: boolean;
    message: string;
}

// ../Nebula.DTO/FileDownloadRequestV1.cs
export interface FileDownloadRequestV1 {
    fileDocumentId: string;
    preferThumbnail: boolean;
}

// ../Nebula.DTO/SubscriptionResult.cs
export enum SubscriptionResult {
    NoResults = 0,
    HasResults = 1,
}

// ../Nebula.DTO/PersonnelV1.cs
export interface PersonnelV1 {
    name: string;
    userId?: string;
}

// ../Nebula.DTO/FederatedAuthenticationRequestV1.cs
export interface FederatedAuthenticationRequestV1 {
    /**
     * Represents the response tokens based on parsing keys
     * from {@link FederatedAuthenticationProviderResponseV1} 
     * For example for authorization code flow it would contain
     * "code":"1234-124-fake-code"
     * In the case of implicit flow this could represent an access token
     * "access_token":"1232asdf123faketoken"
     */
    tokens: Record<string, string>;
    /**
     * Represents the code challenge prior to being SHA256 hashed. This is Base64 encoded.
     * Only valid for authorization code flow with PKCE.
     * The SHA256 base64 encoded version is part of the endpoint.
     */
    codeVerifier: string;
    /**
     * According to RFC 6749, 4.1.3. the redirect_uri parameter is required if it was included in the
     * authorization request. Since the authorization server requires the redirect URL during the
     * authorization request it must be sent also during the token request.
     * This adds an extra layer of security as the authorization server will enforce that they match.
     * Only valid for authorization code flow.
     */
    redirectUri: string;
}

// ../Nebula.DTO/AdditionalDataV1.cs
export interface AdditionalDataV1 {
    header: string;
    data: DataPairV1[];
    sortOrder?: number;
}

// ../Nebula.DTO/CustomerUserConfigurationV1.cs
export interface CustomerUserConfigurationV1 {
    customerUserId: string;
    customerDeviceId?: string;
    key: string;
    value: unknown;
    createdOn: string;
    updatedOn: string;
}

// ../Nebula.DTO/PushNotificationTypesV1.cs
export enum PushNotificationTypesV1 {
    /**
     * Used for sending arbitrary local/tray notification messages to Exeo clients
     */
    NotificationMessage = 0,
    /**
     * Used for sending an assigned incident notification
     */
    AssignedIncident = 1,
    /**
     * Used for sending an assigned emergency resource notification
     */
    AssignedEmergencyResource = 2,
    /**
     * Used for sending a notification when media has successfully been uploaded to
     * an agency
     */
    MediaStatus = 3,
    DeviceAvlTransmissionRequiresUnlock = 4,
    /**
     * Used to indicate that the user currently logged into the device has been
     * deleted
     */
    UserDeleted = 5,
    /**
     * Used to inform a device it has been deactivated and must transition to an
     * inactive state
     */
    DeviceDeactivated = 6,
    /**
     * Used to incidate that a photo has been added to a call for service
     */
    IncidentPhotoAdded = 7,
    /**
     * Used to notify that a specific number of units were assigned to an incident
     */
    SignificantUnitsAssignedToIncident = 8,
    /**
     * Used to notify that an incident was given a high priority
     */
    IncidentGivenHighPriority = 9,
    /**
     * Used to notify that an incident was given a status of importance
     */
    IncidentGivenImportantStatus = 10,
    /**
     * Used to notify that an emergency resource was given a status of importance
     */
    EmergencyResourceGivenImportantStatus = 11,
}

// ../Nebula.DTO/CaseSearchFilterV1.cs
export interface CaseSearchFilterV1 {
    caseNumber: string;
    incidentStartDate: string;
    incidentEndDate: string;
}

// ../Nebula.DTO/Jurisdiction1V1.cs
export interface Jurisdiction1V1 extends BaseDocumentModelV1 {
    name: string;
    abbreviation: string;
    sortOrder: number;
}

// ../Nebula.DTO/CloudMediaUploadKeyV1.cs
/**
 * Response for {@link CloudMediaUploadRequestV1} containing
 * the corresponding CloudMedia ID and a valid Uri to upload the
 * media file to.
 */
export interface CloudMediaUploadKeyV1 {
    /**
     * An ID for the created CloudMedia upload.
     */
    cloudMediaId: string;
    /**
     * A Uri the media can be uploaded to.
     */
    uploadUri: object;
    /**
     * The expiration date for the {@link UploadUri}.
     */
    expiresOn: string;
}

// ../Nebula.DTO/LinkTypeV1.cs
export enum LinkTypeV1 {
    Record = 0,
    Incident = 1,
    External = 2,
}

// ../Nebula.DTO/ConnectionStatusV1.cs
export enum ConnectionStatusV1 {
    None = 0,
    Disconnected = 1,
    Connected = 2,
}

// ../Nebula.DTO/NameRecordV1.cs
export interface NameRecordV1 {
    recordExternalId: string;
    name: string;
    type: string;
    sex: string;
    dateOfBirth: string;
    age: string;
    height: string;
    weight: string;
    hairColor: string;
    eyeColor: string;
    involvement: string;
    phone: string;
    location: string;
    driversLicense: string;
    additionalData: AdditionalDataV1[];
}

// ../Nebula.DTO/MediaSyncRequestV1.cs
export interface MediaSyncRequestV1 {
    mediaUploadStatus: Record<string, DeviceMediaUploadStatusV1>;
}

// ../Nebula.DTO/ResponseTimeV1.cs
export interface ResponseTimeV1 {
    description: string;
    timestamp: string;
}

// ../Nebula.DTO/IncidentEventV1.cs
export interface IncidentEventV1 {
    timestamp: string;
    title: string;
    description: string;
}

// ../Nebula.DTO/CloudMediaMetadataV1.cs
/**
 * Specify CloudMedia metadata.
 */
export interface CloudMediaMetadataV1 {
    /**
     * A user entered description of the cloud media
     */
    description: string;
    /**
     * User entered comments of the cloud media
     */
    comments: string;
}

// ../Nebula.DTO/PermissionScenariosV1.cs
export enum PermissionScenariosV1 {
    Read = 0,
    AddNarrativeToCfs = 1,
    InvolvementHistoryOpen = 2,
    InvolvementHistoryOpenDetails = 3,
    ExecuteQuery = 4,
    AddMediaToIncident = 5,
}

// ../Nebula.DTO/InterfacePublicKeyV1.cs
export interface InterfacePublicKeyV1 {
    customerInterfaceId: string;
    customerAgencyId: string;
    key: Int8Array;
}

// ../Nebula.DTO/LinkV1.cs
export interface LinkV1 {
    refId: string;
    type: LinkTypeV1;
    displayedType: string;
    description: string;
    customerAgencyId: string;
    customerId: string;
    interfaceId: string;
    createDateTime: string;
    location: string;
    assignment: string;
    numberDescription: string;
    incidentNumber: string;
    ownerAgencyId: string;
    permissions: Record<PermissionScenariosV1, PermissionRuleV1>;
}

// ../Nebula.DTO/AgencyV1.cs
export interface AgencyV1 extends BaseDocumentModelV1 {
    ori: string;
    name: string;
    abbreviation: string;
    type: string;
    isDispatchAgency: boolean;
    isResponderAgency: boolean;
    sortOrder: number;
}

// ../Nebula.DTO/DeviceRegistrationV1.cs
export interface DeviceRegistrationV1 {
    properties: Record<DevicePropertyV1, string>;
    capableFeatures: DeviceFeatureV1[];
    customerDeviceId?: string;
    licensedAgencyId?: string;
    licensedAgencyName: string;
    customerAgencyId?: string;
    customerAgencyName: string;
    customerDeviceName: string;
}

// ../Nebula.DTO/MediaUploadFromCloudMediaRequestV1.cs
/**
 * Request for creating a Media upload from a CloudMedia
 * upload.
 */
export interface MediaUploadFromCloudMediaRequestV1 {
    /**
     * The target CloudMedia ID from which to create the Media upload.
     */
    cloudMediaId: string;
    /**
     * The intended destination Customer Interface ID that will receive
     * the CloudMedia media.
     */
    customerInterfaceId?: string;
    /**
     * The document type of the document the media will be associated
     * with.
     */
    documentType: DocumentTypeV1;
    /**
     * The document ID of the document the media will be associated with.
     */
    documentId: string;
}

// ../Nebula.DTO/PushNotificationTokenV1.cs
export interface PushNotificationTokenV1 {
    token: string;
    clientType: ClientTypeV1;
}

// ../Nebula.DTO/PushNotificationTokenV1.cs
export enum ClientTypeV1 {
    iOS = 0,
    Android = 1,
}

// ../Nebula.DTO/FederatedAuthenticationDetailsRequestV1.cs
export interface FederatedAuthenticationDetailsRequestV1 {
    /**
     * An optional value included in the request that will also be returned in the auth code response.
     * It can be a string of any content that you wish. A randomly generated unique value is typically
     * used for preventing cross-site request forgery attacks. The value can also encode information about the
     * user's state in the app before the authentication request occurred, such as the page or view they were on.
     */
    state: string;
    /**
     * The redirect_uri of your app, where authentication responses can be sent and received by your app.
     * It must exactly match one of the redirect_uris registered in the IdP.
     */
    redirectUri: string;
}

// ../Nebula.DTO/CloudMediaSourceTypeV1.cs
/**
 * Enumeration for CloudMedia upload sources.
 */
export enum CloudMediaSourceTypeV1 {
    /**
     * An Exeo device.
     */
    ExeoDevice = 0,
}

// ../Nebula.DTO/EmergencyResourceLocationV1.cs
/**
 * @deprecated EmergencyResourceLocationV1 is deprecated. Use separate PositionV1 type instead.
 */
export interface EmergencyResourceLocationV1 {
    description: string;
    coordinate: GeoCoordinateV1;
}

// ../Nebula.DTO/GeoCoordinateValidationTypeV1.cs
export enum GeoCoordinateValidationTypeV1 {
    None = 0,
    Agency = 1,
    Google = 2,
}

// ../Nebula.DTO/StatusV1.cs
export interface StatusV1 extends BaseDocumentModelV1 {
    description: string;
    sortOrder: number;
    color: string;
    type: DocumentTypeV1;
}

// ../Nebula.DTO/CloudProviderV1.cs
/**
 * An enumeration of valid cloud providers
 */
export enum CloudProviderV1 {
    Unknown = 0,
    AWS = 1,
}

// ../Nebula.DTO/UnitLoginOptionV1.cs
export interface UnitLoginOptionV1 {
    id: string;
    unitIdentification: string;
}

// ../Nebula.DTO/CloudMediaUpdateRequestV1.cs
/**
 * A request to update a CloudMedia upload. This can be used to update
 * media data associated with the CloudMedia as well as CloudMedia
 * metadata.
 */
export interface CloudMediaUpdateRequestV1 {
    /**
     * The CloudMedia ID to update
     */
    cloudMediaId: string;
    /**
     * The updated MediaData
     */
    mediaData: unknown;
    /**
     * The updated CloudMedia metadata.
     */
    metadata: CloudMediaMetadataV1;
}

// ../Nebula.DTO/PhoneV1.cs
export interface PhoneV1 {
    number: string;
    type: string;
}

// ../Nebula.DTO/EmailAddressV1.cs
export interface EmailAddressV1 {
    email: string;
    type: string;
}

// ../Nebula.DTO/InterfacePublicKeyChangedResponseV1.cs
export interface InterfacePublicKeyChangedResponseV1 extends SourcedPayload {
    customerAgencyId: string;
    customerInterfaceId: string;
    publicKey: Int8Array;
}

// ../Nebula.DTO/MediaUploadLinkResponseV1.cs
export interface MediaUploadLinkResponseV1 {
    mediaId: string;
}

// ../Nebula.DTO/CaseV1.cs
export interface CaseV1 extends BaseDocumentModelV1 {
    description: string;
    caseNumber: string;
    location: string;
    assignment: string;
    incidentNumber: string;
    ownerAgencyId: string;
    permissions: Record<PermissionScenariosV1, PermissionRuleV1>;
}

// ../Nebula.DTO/SignatureCollectionRequestV1.cs
export interface SignatureCollectionRequestV1 extends BaseDocumentModelV1 {
    /**
     * Title of the document needing signed that will be presented in a list to the officer
     * and as the title for the person signing the document to know what they are signing (like "CITATION #525235235")
     */
    documentTitle: string;
    /**
     * Type of document that is going to be signed
     */
    documentType: string;
    /**
     * An explanation for the person signing the document to understand what they are signing
     */
    documentDescription: string;
    /**
     * Name of the person who is supposed to sign the document
     */
    signerName: string;
}

// ../Nebula.DTO/BulkFileDownloadKeysResponseV1.cs
export interface BulkFileDownloadKeysResponseV1 {
    downloadKeys: MediaDownloadKeyV1[];
    failedFileDocumentIds: string[];
}

// ../Nebula.DTO/FilterTypeV1.cs
export enum FilterTypeV1 {
    DispatchAgency = 1,
    ResponderAgency = 2,
    ResponderEmergencyResource = 3,
    ResponderJurisdiction1 = 4,
    ResponderJurisdiction2 = 5,
    ResponderJurisdiction3 = 6,
    Priority = 7,
    Agency = 8,
    EmergencyResourceType = 9,
    Jurisdiction1 = 10,
    Jurisdiction2 = 11,
    Jurisdiction3 = 12,
    Status = 13,
    IncidentPostion = 14,
    EmergencyResourcePosition = 15,
    User = 16,
    File = 17,
    UserPosition = 18,
}

// ../Nebula.DTO/HistoricalPersonnelV1.cs
export interface HistoricalPersonnelV1 {
    userId?: string;
    name: string;
    fullName: string;
}

// ../Nebula.DTO/PriorityV1.cs
export interface PriorityV1 extends BaseDocumentModelV1 {
    description: string;
    color: string;
    sortOrder: number;
}

// ../Nebula.DTO/EmergencyResourceV1.cs
export interface EmergencyResourceV1 extends BaseDocumentModelV1 {
    unitIdentification: string;
    typeId: string;
    statusId: string;
    statusUpdatedOn?: string;
    incidentId?: string;
    ownerAgencyId: string;
    jurisdiction1Id?: string;
    jurisdiction2Id?: string;
    jurisdiction3Id?: string;
    disciplineType: DisciplineTypeV1;
    incidentIdStacks: string[];
    /**
     * @deprecated Location is deprecated. Use separate PositionV1 type instead.
     */
    location: EmergencyResourceLocationV1;
    personnel: PersonnelV1[];
    additionalData: AdditionalDataV1[];
    permissions: Record<PermissionScenariosV1, PermissionRuleV1>;
}

// ../Nebula.DTO/HistoricalAssignmentV1.cs
export interface HistoricalAssignmentV1 {
    emergencyResource: HistoricalEmergencyResourceV1;
    assignedOn: string;
}

// ../Nebula.DTO/CloudMediaEncryptionKeysV1.cs
export interface CloudMediaEncryptionKeysV1 {
    /**
     * Map of Customer Interface IDs to Interface encryption keys.
     */
    interfaceKeys: Record<string, Int8Array>;
    /**
     * An optional encryption key from the device (should be encrypted using the
     * devices certificate).
     */
    deviceKey: Int8Array;
}

// ../Nebula.DTO/FederatedAuthenticationDetailsResponseV1.cs
export interface FederatedAuthenticationDetailsResponseV1 {
    isError: boolean;
    message: string;
    isFederatedAuthenticationRequired: boolean;
    provider: FederatedAuthenticationProviderResponseV1;
}

// ../Nebula.DTO/VehicleV1.cs
export interface VehicleV1 {
    recordExternalId: string;
    year: string;
    make: string;
    model: string;
    licensePlate: string;
    plateState: string;
    color: string;
    involvement: string;
    vin: string;
    registeredOwner: NameRecordV1;
    additionalData: AdditionalDataV1[];
}

// ../Nebula.DTO/ExpirationClaimTypesV1.cs
export interface ExpirationClaimTypesV1 {
}

// ../Nebula.DTO/ClientNotificationTypeV1.cs
export enum ClientNotificationTypeV1 {
    CentralSquare = 4,
    CentralSquareDev = 5,
}

// ../Nebula.DTO/ClientCertificateChallengeResponseV1.cs
export interface ClientCertificateChallengeResponseV1 extends SourcedPayload {
    confirmedChallenge: string;
}

// ../Nebula.DTO/InterfacePropertyV1.cs
export enum InterfacePropertyV1 {
    /**
     * Specifies the connector abstraction version used by the communicating
     * interface.
     */
    ConnectorAbstractionVersion = 1,
    /**
     * Specifies the OS the interface is running on.
     */
    OperatingSystem = 2,
    /**
     * Specifies the version of .NET the interface is using.
     */
    FrameworkDescription = 3,
    /**
     * Identifies the interface's host machine's local IP address.
     */
    LocalIpAddress = 4,
    /**
     * Identifies the interface's host machine's remote IP address.
     */
    RemoteIpAddress = 5,
    /**
     * Specifies the version of the product line's connector implementation.
     */
    ConnectorVersion = 6,
    /**
     * This is the version of the API that the connector abstraction is using.
     */
    ApiVersion = 7,
    /**
     * Specifies the vendor and version of software that the orginating system is using.
     * I.E. Enterprise CAD 1.4.5, Pro Suite CAD .4.5.6, Enterprise RMS 1.2.3 or Enterprise TTMS 1.8.9
     */
    VendorSoftwareVersion = 8,
}

// ../Nebula.DTO/AlertSourceTypeV1.cs
export enum AlertSourceTypeV1 {
    Phone = 0,
    Name = 1,
    Vehicle = 2,
    Location = 3,
}

// ../Nebula.DTO/DocumentTypeV1.cs
export enum DocumentTypeV1 {
    Incident = 0,
    EmergencyResource = 1,
    Jurisdiction1 = 2,
    Jurisdiction2 = 3,
    Jurisdiction3 = 4,
    Position = 5,
    Agency = 6,
    User = 7,
    FilterConfiguration = 8,
    Status = 9,
    Priority = 10,
    EmergencyResourceType = 11,
    Case = 12,
    Command = 13,
    Transition = 14,
    GlobalConfiguration = 15,
    Message = 16,
    QuerySpecification = 17,
    InterconnectedCadConfiguration = 18,
    File = 19,
    SignatureCollectionRequest = 20,
    MapLayer = 21,
}

// ../Nebula.DTO/PermissionRuleV1.cs
export interface PermissionRuleV1 {
    all: string[];
    or: string[];
}

// ../Nebula.DTO/HistoricalEmergencyResourceV1.cs
export interface HistoricalEmergencyResourceV1 {
    emergencyResourceId?: string;
    unitIdentification: string;
    type: string;
    disciplineType: DisciplineTypeV1;
    personnel: HistoricalPersonnelV1[];
}

// ../Nebula.DTO/PushNotificationV1.cs
export interface PushNotificationV1<T> {
    type: PushNotificationTypesV1;
}

// ../Nebula.DTO/FilterConfigurationV1.cs
export interface FilterConfigurationV1 extends BaseDocumentModelV1 {
    ownerAgencyId?: string;
    incidentFilterConfiguration: IncidentFilterConfigurationV1;
    emergencyResourceFilterConfiguration: EmergencyResourceFilterConfigurationV1;
}

// ../Nebula.DTO/IncidentV1.cs
export interface IncidentV1 extends BaseDocumentModelV1 {
    statusId: string;
    priorityId?: string;
    incidentCode: string;
    incidentCodeDescription: string;
    disposition: string;
    jurisdiction1Id?: string;
    jurisdiction2Id?: string;
    jurisdiction3Id?: string;
    responderEmergencyResourceIds: string[];
    responderAgenciesIds: string[];
    dispatchAgencyId: string;
    abbreviatedIncidentNumber: string;
    incidentNumber: string;
    disciplineType: DisciplineTypeV1;
    incidentDateTime: string;
    call: CallV1;
    location: LocationV1;
    narratives: NarrativeV1[];
    names: NameRecordV1[];
    vehicles: VehicleV1[];
    alerts: AlertV1[];
    links: LinkV1[];
    locationHistory: LocationHistoryV1[];
    responseTimes: ResponseTimeV1[];
    assignmentHistory: HistoricalAssignmentV1[];
    additionalData: AdditionalDataV1[];
    permissions: Record<PermissionScenariosV1, PermissionRuleV1>;
    /**
     * List of File IDs associated with the incident as "Media". This is purposefully a list to account
     * for...whatever weird situation might necessitate duplicate media on the incident.
     */
    mediaIds: string[];
    history: IncidentEventV1[];
}

// ../Nebula.DTO/CloudUploadKeyV1.cs
export interface CloudUploadKeyV1 {
    uploadUri: string;
    expiresOn: string;
}

// ../Nebula.DTO/PublicKeyResponseV1.cs
export interface PublicKeyResponseV1 extends SourcedPayload {
    nebula: Int8Array;
    agency: Int8Array;
    interfacePublicKeys: InterfacePublicKeyV1[];
    supportedProtocols: SecureConnectionProtocolV1[];
}

// ../Nebula.DTO/LocationHistoryV1.cs
export interface LocationHistoryV1 {
    occurredAt: string;
    type: string;
    sourceDescription: string;
    additionalData: AdditionalDataV1[];
    permissions: Record<PermissionScenariosV1, PermissionRuleV1>;
}

// ../Nebula.DTO/CloudMediaUploadRequestV1.cs
/**
 * A request to create a CloudMedia upload. Nebula responds with a
 * {@link CloudMediaUploadKeyV1} where the media file can be
 * uploaded to.
 */
export interface CloudMediaUploadRequestV1 {
    /**
     * If not null, this request is treated like requesting a new
     * {@link CloudMediaUploadKeyV1} and will update
     * {@link MediaData"/> and <see cref="Metadata}.
     */
    id?: string;
    /**
     * The type of media being uploaded.
     */
    mediaType: MediaTypeV1;
    /**
     * Source controlled JSON bag of data to associate with the upload.
     */
    mediaData: unknown;
    /**
     * Whether the media file is encrypted or not.
     */
    isEncrypted: boolean;
    /**
     * An encrypted key used to decrypt the media file.
     */
    encryptionKeys: CloudMediaEncryptionKeysV1;
    /**
     * An MD5 checksum for the encrypted file.
     */
    md5Checksum: Int8Array;
    /**
     * The source media file's original name.
     */
    fileName: string;
    /**
     * The source media file's extension.
     */
    fileExtension: string;
    /**
     * The source media file's size before encryption.
     */
    fileSize: number;
    /**
     * The source media file's creation data.
     */
    fileCreationDate: string;
    /**
     * Metadata associated with the media.
     */
    metadata: CloudMediaMetadataV1;
}

// ../Nebula.DTO/CloudMultiPartUploadKeyV1.cs
export interface CloudMultiPartUploadKeyV1 {
    resourceId: string;
    contentType: string;
    expiresOn: string;
    uploadId: string;
}

// ../Nebula.DTO/BaseDocumentModelV1.cs
export interface BaseDocumentModelV1 {
    id: string;
    customerAgencyId: string;
    customerId: string;
    interfaceId: string;
    createdOn: string;
    updatedAt: string;
    pushIndex?: number;
}

// ../Nebula.DTO/EmergencyResourceFilterConfigurationV1.cs
export interface EmergencyResourceFilterConfigurationV1 {
    agencyLabel: string;
    emergencyResourceTypeLabel: string;
    statusLabel: string;
    jurisdiction1Label: string;
    jurisdiction2Label: string;
    jurisdiction3Label: string;
}

// ../Nebula.DTO/LocationV1.cs
export interface LocationV1 {
    commonPlaceName: string;
    freeFormAddressText: string;
    city: string;
    state: string;
    crossStreets: string;
    nearestIntersection: string;
    notes: string;
    coordinate: GeoCoordinateV1;
    additionalData: AdditionalDataV1[];
    parseError: boolean;
}

// ../Nebula.DTO/CustomerAgencyV1.cs
export interface CustomerAgencyV1 {
    customerAgencyId: string;
    ori: string;
    name: string;
    uniqueIdentifier: string;
    type: string;
    licenseCount: number;
}

// ../Nebula.DTO/InterfaceFeatureV1.cs
export enum InterfaceFeatureV1 {
    None = 0,
    /**
     * Enables the General Availability feature set. Should always be provided.
     */
    GeneralAvailability = 1,
    /**
     * If present, declares that the interface supports adding narratives to Incidents.
     */
    AddNarrativeToCfs = 2,
    /**
     * If present, declares that the interface supports the Statusing feature set.
     */
    Statusing = 3,
    /**
     * If present, declares that the interface supports Emergency Resource assignment by
     * the user through the app.
     */
    UserAssignToEmergencyResource = 4,
    /**
     * If present, declares that the interface supports the Media Upload feature set.
     *
     * @deprecated Use the specific 'AcceptsXXMediaType' item types instead
     */
    MediaUpload = 5,
    /**
     * If present, declares that the interface supports the Case Search feature set.
     */
    CaseSearch = 6,
    /**
     * If present, declares that the interface supports pushing Position documents for EmergencyResources.
     */
    PositionsAvl = 7,
    /**
     * If present, declares that the interface supports User Logoff callbacks.
     */
    UserLogoff = 8,
    /**
     * If present, declares that the interface supports the Device AVL feature set.
     */
    DeviceAvlTransmission = 9,
    /**
     * If present, declares that the interface supports the Messages feature set.
     */
    UserNotificationMessages = 10,
    /**
     * If present, means that assignment commands should be restricted to those
     * with the {@link CommandCategoryV1.AssignIncident} category.
     */
    RespectsCommandCategoryForAssignedIncident = 11,
    /**
     * If present, declares that the interface supports the disabling of the Dispatch
     * feature set.
     */
    RespectsAllowDispatch = 12,
    /**
     * If present, declares that the interface supports the Query/NCIC feature set.
     *
     * @remarks
     * Use the more specific {@link NcicQuery"/> and <see cref="RecordQuery} features
     * as {@link Query} is considered obsolete but backwards-compatible.
     *
     * @deprecated Use NcicQuery or RecordQuery instead.
     */
    Query = 13,
    /**
     * If present, declares that the interface supports the Interconnected CAD
     * interchange of data.
     */
    InterconnectedCad_Incidents = 14,
    /**
     * If present, declares that the interface supports sending an incident's 
     * assignment history (emergency resource, personnel, etc.)
     */
    AllowViewingClosedCalls = 15,
    /**
     * If present, declares that the interface supports receiving photo media uploads
     * to be added to a case
     */
    AcceptsPhotoMediaType = 16,
    /**
     * If present, declares that the interface supports receiving audio recording media uploads
     * to be added to a case
     */
    AcceptsAudioRecordingMediaType = 17,
    /**
     * If present, declares that the interface supports receiving note media uploads
     * to be added to a case
     */
    AcceptsNoteMediaType = 18,
    /**
     * If present, declares that the interface supports receiving pending narratives to be added to an incident
     */
    AcceptsNarrativeMediaType = 19,
    /**
     * If present, declares that the interface provides dispatch data for the device to use on the Calls for Service, Units, and Map screens
     */
    Dispatch = 20,
    /**
     * If present, declares that the connector abstraction supports accepting an encrypted password and decrypting it before handing it to the connector implementation.
     */
    EndToEndAuthenticationEncryption = 21,
    /**
     * If present, declares that the interface supports querying NCIC.
     */
    NcicQuery = 22,
    /**
     * If present, declares that the interface supports querying records.
     */
    RecordQuery = 23,
    /**
     * If present, declares that the interface supports allowing a client to create incidents.
     */
    ClientCreateIncident = 24,
    /**
     * If present, declares that the interface supports updating its configuration at runtime based on requests
     * from Nebula.
     */
    RuntimeConfigurationOverride = 25,
    /**
     * If present, declares that the interface supports file documents that can be attached to other documents, a prerequisite feature for AddMediaToIncident.
     */
    Files = 26,
    /**
     * If present, declares that the interface supports CFS photos. Requires the Files feature.
     */
    AddMediaToIncident = 27,
    /**
     * If present, declares that the interface supports federated authentication.
     */
    ProvidesFederatedAuthenticationDetails = 28,
    /**
     * If present, declares that the interface supports collecting signatures.
     */
    SignatureCollection = 29,
    /**
     * If present, declares that the interface supports pushing Position documents for Users.
     */
    PositionsPlt = 30,
}

// ../Nebula.DTO/ServerCertificateChallengeV1.cs
export interface ServerCertificateChallengeV1 {
    publicKey: Int8Array;
    challenge: string;
}

// ../Nebula.DTO/ClientCertificateRenewalRequestV1.cs
export interface ClientCertificateRequestV1 {
}

// ../Nebula.DTO/ClientCertificateRenewalRequestV1.cs
export interface ClientCertificateResponseV1 extends SourcedPayload {
    status: ClientCertificateResponseStatusV1;
    certificate: Int8Array;
}

// ../Nebula.DTO/ClientCertificateRenewalRequestV1.cs
export enum ClientCertificateResponseStatusV1 {
    Successful,
    Failed,
}

// ../Nebula.DTO/FileUploadStatusV1.cs
export enum FileUploadStatusV1 {
    Pending = 0,
    Failed = 1,
    Uploaded = 2,
}

// ../Nebula.DTO/AuthenticationResultTypeV1.cs
export enum AuthenticationResultTypeV1 {
    Success = 1,
    InvalidLoginAttempt = -1,
    AccountDisabled = -2,
    AccountLocked = -3,
    PasswordExpired = -4,
    MultiFactorRequired = -5,
    UnknownUser = -6,
}

// ../Nebula.DTO/DisciplineTypeV1.cs
/**
 * Identifies the type discipline
 */
export enum DisciplineTypeV1 {
    Unknown = 0,
    Law = 1,
    Fire = 2,
    Ems = 3,
}

// ../Nebula.DTO/PushNotificationConfigurationV1.cs
export interface PushNotificationConfigurationV1 {
    notificationsEnabled: boolean;
    enabledTypes: Record<PushNotificationTypesV1, boolean>;
    clientNotificationType: ClientNotificationTypeV1;
}

// ../Nebula.DTO/FileV1.cs
export interface FileV1 extends BaseDocumentModelV1 {
    ownerAgencyExternalId: string;
    uploadStatus: FileUploadStatusV1;
    fileType: FileTypeV1;
    md5Checksum: Int8Array;
    fileExtension: string;
    description: string;
    comments: string;
    fileSizeInBytes?: number;
    height?: number;
    width?: number;
    lengthInMilliseconds?: number;
    sortOrder: number;
    permissions: Record<PermissionScenariosV1, PermissionRuleV1>;
}

// ../Nebula.DTO/UserCapabilitiesV1.cs
export enum UserCapabilitiesV1 {
    OptionalUserAssignment = 1,
    RequiredUserAssignment = 2,
    AllowMedia = 3,
    AllowMessages = 4,
    AllowDispatch = 5,
    /**
     * @deprecated Use AllowNcicQuery or AllowRecordQuery instead.
     */
    AllowQuery = 6,
    AllowNcicQuery = 7,
    AllowRecordQuery = 8,
    AllowIncidentCreation = 9,
    AllowIncidentMedia = 10,
    AllowSignatureCollection = 11,
}

// ../Nebula.DTO/CloudMultiPartKeyValidationV1.cs
/**
 * Combines the two different objects needed to complete a multipart upload request into one DTO.
 */
export interface CloudUploadMultiPartKeyValidationV1 {
    /**
     * The cloud upload key that owns the entire multipart upload.
     */
    uploadKey: CloudMultiPartUploadKeyV1;
    /**
     * The collection of part numbers -> etag so that we can verify the various parts that were uploaded are correct.
     */
    partEtags: Record<number, string>;
}

// ../Nebula.DTO/MediaUploadRequestV1.cs
export interface MediaUploadRequestV1 {
    documentType: DocumentTypeV1;
    documentId: string;
    mediaType: MediaTypeV1;
    description: string;
    comments: string;
    encryptionKey: Int8Array;
    md5Checksum: Int8Array;
    fileExtension: string;
    /**
     * Used to "replace" a previous request with a new one.
     */
    previousMediaId?: string;
    customerInterfaceId?: string;
    linkedMediaId?: string;
}

// ../Nebula.DTO/Jurisdiction2V1.cs
export interface Jurisdiction2V1 extends BaseDocumentModelV1 {
    name: string;
    abbreviation: string;
    sortOrder: number;
}

// ../Nebula.DTO/MediaUploadHistory.cs
export interface MediaUploadHistoryRequestV1 {
    range: string;
    lastIndex?: number;
    pageSize: number;
}

// ../Nebula.DTO/MediaUploadHistory.cs
export interface MediaUploadHistoryRangeV1 {
}

// ../Nebula.DTO/MediaUploadHistory.cs
export interface MediaUploadHistorySummaryV1 {
    uploadedOn: string;
    mediaType: MediaTypeV1;
    documentId: string;
    documentType: DocumentTypeV1;
    documentDescription: string;
}

// ../Nebula.DTO/MediaUploadHistory.cs
export interface MediaUploadHistoryResponseV1 {
    index: number;
    history: MediaUploadHistorySummaryV1[];
}

// ../Nebula.DTO/Jurisdiction3V1.cs
export interface Jurisdiction3V1 extends BaseDocumentModelV1 {
    name: string;
    abbreviation: string;
    sortOrder: number;
}

// ../Nebula.DTO/AlertV1.cs
export interface AlertV1 {
    sourceType: AlertSourceTypeV1;
    sourceDescription: string;
    description: string;
    alertType: string;
    additionalData: AdditionalDataV1[];
    permissions: Record<PermissionScenariosV1, PermissionRuleV1>;
}

// ../Nebula.DTO/MediaSyncResponseV1.cs
export interface MediaSyncResponseV1 {
    /**
     * A list of media IDs Nebula does not have media requests
     * for. These should be very rare and indicate a situation
     * where Nebula was likely purged of data.
     */
    unknownMediaIds: string[];
    /**
     * A list of media IDs Nebula associated with the Customer
     * Agency ID and the device ID that wasn't included in the
     * sync request. These IDs would have been treated as having
     * been canceled.
     */
    missingMediaIds: string[];
    /**
     * A list of {@link MediaStatusResponseV1} objects for each
     * valid and tracked media object.
     */
    statuses: Record<string, MediaStatusResponseV1>;
}

// ../Nebula.DTO/DocumentSubscriptionChangeV1.cs
export interface DocumentSubscriptionChangeV1<T> {
    document: T;
    action: DocumentSubscriptionActionV1;
}

// ../Nebula.DTO/DeviceActivationResponseV1.cs
export interface DeviceActivationResponseV1 extends SourcedPayload {
    activationReponseStatus: ActivationResponseStatusV1;
    deviceCertificate: CustomerCertificateV1;
    privateKeyDownloadUrl: string;
    downloadUrlExpiresOn?: string;
}

// ../Nebula.DTO/DeviceActivationResponseV1.cs
export enum ActivationResponseStatusV1 {
    Successful,
    ExpiredActivationPin,
    InvalidActivationPin,
    UnexpectedException,
}

// ../Nebula.DTO/MediaTypeV1.cs
export enum MediaTypeV1 {
    Image = 0,
    Video = 1,
    Audio = 2,
    Note = 3,
    Narrative = 4,
    AppDataBackup = 5,
    Signature = 6,
}

// ../Nebula.DTO/DeviceMediaUploadStatusV1.cs
export enum DeviceMediaUploadStatusV1 {
    Canceled = -2,
    Failed = -1,
    Unknown = 0,
    Uploading = 1,
    Uploaded = 2,
}

// ../Nebula.DTO/MediaStatusResponseV1.cs
/**
 * Contains the status and, if applicable, a failure reason for a Media
 * upload to a connector. This is returned during a
 * {@link MediaSyncRequestV1}.
 */
export interface MediaStatusResponseV1 extends SourcedPayload {
    /**
     * The Media upload ID.
     */
    mediaId?: string;
    /**
     * The status of the upload.
     */
    status: MediaStatusV1;
    /**
     * A failure reason if {@link Status} is
     * {@link MediaStatusV1.Failed}.
     */
    failureReason: String;
}

// ../Nebula.DTO/MediaStatusResponseV1.cs
/**
 * Media upload to connector status types.
 */
export enum MediaStatusV1 {
    /**
     * The upload failed; see {@link MediaStatusResponseV1.FailureReason}.
     */
    Failed = -1,
    /**
     * The upload hasn't started yet.
     */
    Pending = 0,
    /**
     * The upload has processed successfully.
     */
    Processed = 1,
}

// ../Nebula.DTO/StyleFieldV1.cs
export enum StyleFieldV1 {
    BackgroundColor,
    ForegroundColor,
}

// ../Nebula.DTO/FederatedAuthenticationProviderResponseV1.cs
export interface FederatedAuthenticationProviderResponseV1 extends SourcedPayload {
    /**
     * True if an error occurred while retrieving identity provider info
     */
    isError: boolean;
    /**
     * Error message
     */
    message: string;
    /**
     * Endpoint users with authenticate against
     */
    endpoint: string;
    /**
     * Single logout endpoint for users to end session on IdP (optional)
     */
    logoutEndpoint: string;
    /**
     * The parameter keys for the response (i.e. code, access_token, id_token, etc.)
     * included in the redirect URI.  These will be parsed and sent back as part of
     * the authentication request.
     * Typically the authorization flow will use "code" and implicit flow
     * would use "access_token".
     */
    tokenKeys: string[];
    /**
     * Represents the code challenge prior to being SHA256 hashed. This is Base64 encoded.
     * Only valid for authorization code flow with PKCE.
     * The SHA256 base64 encoded version is part of the endpoint.
     */
    codeVerifier: string;
}

// ../Nebula.DTO/CallV1.cs
export interface CallV1 {
    callDateTime: string;
    originatingPhoneNumber: string;
    location: LocationV1;
}

// ../Nebula.DTO/PositionV1.cs
export interface PositionV1 extends BaseDocumentModelV1 {
    ownerAgencyId: string;
    refId: string;
    type: DocumentTypeV1;
    /**
     * @deprecated EmergencyResourceTypeId is deprecated. Use TypeId on EmergencyResourceV1
     */
    emergencyResourceTypeId: string;
    /**
     * @deprecated DisciplineType is deprecated. Use DisciplineType on EmergencyResourceV1
     */
    disciplineType: DisciplineTypeV1;
    coordinate: GeoCoordinateV1;
    sortOrder: number;
    direction?: number;
    description: string;
}

// ../Nebula.DTO/CustomerInterfaceConnectionStatusV1.cs
export interface CustomerInterfaceConnectionStatusV1 {
    customerInterfaceId: string;
    connectionStatus: ConnectionStatusV1;
}

// ../Nebula.DTO/CloudMediaStatusResponseV1.cs
/**
 * Response for a {@link CloudMediaStatusRequestV1} containing the
 * statuses for all requested CloudMedia uploads.
 */
export interface CloudMediaStatusResponseV1 extends SourcedPayload {
    /**
     * A dictionary of CloudMedia upload statuses.
     */
    statuses: Record<string, CloudMediaStatusElementV1>;
}

// ../Nebula.DTO/CloudMediaStatusElementV1.cs
export interface CloudMediaStatusElementV1 {
    id: string;
    sourceType: CloudMediaSourceTypeV1;
    sourceId: string;
    status: DeviceMediaUploadStatusV1;
}

// ../Nebula.DTO/MediaDownloadKeyV1.cs
export interface MediaDownloadKeyV1 {
    mediaId: string;
    downloadUri: object;
    expiresOn: string;
}

// ../Nebula.DTO/Statusing/StatusingConfigurationV1.cs
export interface StatusingConfigurationV1 {
    defaultCommandFlowId: string;
    clientCreatedIncidentCommandFlowId?: string;
    commandFlows: CommandFlowV1[];
}

// ../Nebula.DTO/Statusing/CommandV1.cs
export interface CommandV1 {
    id: string;
    customerAgencyId: string;
    customerId: string;
    interfaceId: string;
    createdOn: string;
    updatedAt: string;
    statusIds: string[];
    name: string;
    description: string;
    sortOrder: number;
    isActive: boolean;
    categories: CommandCategoryV1[];
    defaultTransitionId: string;
    transitionIds: string[];
    styling: Record<StyleFieldV1, string>;
}

// ../Nebula.DTO/Statusing/FieldEntryTypeV1.cs
export enum FieldEntryTypeV1 {
    /**
     * Text field style widget
     */
    Input = 0,
    /**
     * Some widget allowing the selection of a single item
     */
    Select = 1,
    /**
     * Some widget allowing free-form text entry or the
     * selection of items filtered by the entered text
     */
    FreeFormSelect = 2,
    /**
     * Widget requiring entry of a date and time
     */
    Date = 3,
    /**
     * Widget requiring entry of a date without the time component
     */
    DateWithoutTime = 4,
    /**
     * Widget that allows the client to send back a geocoordinate with the client's location
     * or allows the user to freeform enter an address
     */
    Location = 5,
}

// ../Nebula.DTO/Statusing/TransitionTypeV1.cs
/**
 * Used to indicate how a given Transition is used.
 */
export enum TransitionTypeV1 {
    /**
     * This TransitionV1 is not currently used.
     */
    None = 0,
    /**
     * This TransitionV1 is used to perform a status change.
     */
    Statusing = 1,
    /**
     * This TransitionV1 is used to let a client create an incident.
     */
    ClientCreatedIncident = 2,
}

// ../Nebula.DTO/Statusing/FieldValueV1.cs
export interface FieldValueV1 {
    label: string;
    /**
     * The actual value.
     * Values with FieldDataType of Date must be in DateTimeOffset format. 
     * {@link DateSerializerSettings}
     */
    value: object;
}

// ../Nebula.DTO/Statusing/LocationValueV1.cs
/**
 * Defines a data type that can be either a geocoordinate or a free form address.
 */
export interface LocationValueV1 {
    valueType: LocationValueTypeV1;
    geoCoordinateValue: GeoCoordinateV1;
    freeFormLocationValue: string;
}

// ../Nebula.DTO/Statusing/ExecuteCommandResponseTypeV1.cs
export enum ExecuteCommandResponseTypeV1 {
    RequestTimedOut = -7,
    InvalidIncident = -6,
    InvalidEmergencyResource = -5,
    InvalidUser = -4,
    InvalidCommand = -3,
    InvalidTransition = -2,
    InvalidOrMissingTransitionFields = -1,
    UnknownError = 0,
    Success = 1,
}

// ../Nebula.DTO/Statusing/FieldDataTypeV1.cs
export enum FieldDataTypeV1 {
    String = 0,
    Double = 1,
    Integer = 2,
    /**
     * Date field, JSON formatted with the settings:
     * DateParseHandling = DateParseHandling.DateTimeOffset
     * DateFormatHandling = DateFormatHandling.IsoDateFormat
     * DateTimeZoneHandling = DateTimeZoneHandling.Utc = 3
     */
    Date = 3,
    /**
     * Date field without a corresponding time component
     * and formatted according to ISO 8601, i.e. 2020-01-02
     */
    DateWithoutTime = 4,
    /**
     * Allows the client to send back a geocoordinate with the client's location
     * or allows the user to freeform enter an address
     */
    Location = 5,
}

// ../Nebula.DTO/Statusing/TransitionFieldV1.cs
export interface TransitionFieldV1 {
    identifier: string;
    name: string;
    isDisplayed: boolean;
    sortOrder: number;
    isRequired: boolean;
    entryType: FieldEntryTypeV1;
    dataType: FieldDataTypeV1;
    defaultValue: FieldValueV1;
    options: FieldValueV1[];
}

// ../Nebula.DTO/Statusing/TransitionV1.cs
export interface TransitionV1 {
    id: string;
    customerAgencyId: string;
    customerId: string;
    interfaceId: string;
    createdOn: string;
    updatedAt: string;
    description: string;
    isActive: boolean;
    sortOrder: number;
    nextStatusId?: string;
    fields: TransitionFieldV1[];
    styling: Record<StyleFieldV1, string>;
    /**
     * Defaults to Statusing for backwards compatibility
     */
    transitionType: TransitionTypeV1;
}

// ../Nebula.DTO/Statusing/TransitionFieldResponseV1.cs
export interface TransitionFieldResponseV1 extends SourcedPayload {
    transitionFieldIdentifier: string;
    transitionFieldName: string;
    dataType: FieldDataTypeV1;
    value: FieldValueV1;
}

// ../Nebula.DTO/Statusing/ExecuteCommandRequestV1.cs
export interface ExecuteCommandRequestV1 {
    emergencyResourceId?: string;
    incidentId?: string;
    commandId: string;
    transitionId: string;
    fieldResponses: TransitionFieldResponseV1[];
    /**
     * The timezone that the client's (usually the field ops app) clock was set to when the request was made.
     * This is sometimes necessary for reconstructing the actual exact time a date was chosen by a user.
     */
    clientTimeZone: any;
    occurredAt: string;
}

// ../Nebula.DTO/Statusing/CommandFlowV1.cs
export interface CommandFlowV1 {
    id: string;
    commandIds: string[];
    transitionIds: string[];
    statusIdToDefaultCommandId: Record<string, string>;
}

// ../Nebula.DTO/Statusing/CommandCategoryV1.cs
export enum CommandCategoryV1 {
    AssignIncident = 0,
    AssignEmergencyResource = 1,
    RequiresIncident = 2,
    RequiresEmergencyResource = 3,
    RequiresAssignedIncident = 4,
    RequiresAssignedEmergencyResource = 5,
}

// ../Nebula.DTO/Statusing/LocationValueTypeV1.cs
export enum LocationValueTypeV1 {
    None = 0,
    FreeFormLocation = 1,
    Geocoordinate = 2,
}

// ../Nebula.DTO/Statusing/ExecuteCommandResponseV1.cs
export interface ExecuteCommandResponseV1 extends SourcedPayload {
    responseType: ExecuteCommandResponseTypeV1;
    failureMessage: string;
    missingTransitionFields: TransitionFieldV1[];
    invalidTransitionFieldResponses: TransitionFieldResponseV1[];
    originalTransitionFieldResponses: TransitionFieldResponseV1[];
}

// ../Nebula.DTO/Configuration/LicensedAgencyConfigurationV1.cs
export interface LicensedAgencyConfigurationV1 {
    deviceSessionLockTimeoutInMinutes?: number;
    incidentNotificationSignificantUnitsAssigned?: number;
    incidentNotificationHighPriorities: string[];
    incidentNotificationStatuses: string[];
    emergencyResourceNotificationStatuses: string[];
}

// ../Nebula.DTO/Messages/MessagePriorityV1.cs
export enum MessagePriorityV1 {
    None = 0,
    High = 10,
}

// ../Nebula.DTO/Messages/MessageReadReceiptV1.cs
export interface MessageReadReceiptV1 {
    messageId: string;
    readTime: string;
}

// ../Nebula.DTO/Messages/MessageV1.cs
export interface MessageV1 extends BaseDocumentModelV1 {
    expireAt?: string;
    priority: MessagePriorityV1;
    title: string;
    subTitle: string;
    shortMessage: string;
    messageBody: string;
    documentLinks: MessageDocumentLinkV1[];
    lastReadTime?: string;
}

// ../Nebula.DTO/Messages/MessageActionV1.cs
export interface MessageActionV1 {
    messageActionId: string;
    messageId: string;
    occurredAt: string;
    messageActionType: MessageActionTypeV1;
}

// ../Nebula.DTO/Messages/MessageDocumentLinkV1.cs
export interface MessageDocumentLinkV1 {
    documentType: DocumentTypeV1;
    documentId: string;
}

// ../Nebula.DTO/Messages/MessageActionTypeV1.cs
export enum MessageActionTypeV1 {
    Unknown = 0,
    Read = 1,
    Deleted = 2,
}

// ../Nebula.DTO/GlobalConfiguration/NcicConfigurationV1.cs
export interface NcicConfigurationV1 {
    /**
     * True if requires user to re-authenticate prior to making NCIC request.
     * If used in coordination with <seealso cref="AuthenticationDurationInSeconds"/>
     * then the user will only be required to re-authenticate if they haven't done so
     * within the defined duration.
     */
    requireAuthentication: boolean;
    /**
     * Duration in seconds before requiring user to re-authenticate prior to making NCIC request.
     */
    authenticationDurationInSeconds?: number;
}

// ../Nebula.DTO/GlobalConfiguration/GlobalConfigurationV1.cs
export interface GlobalConfigurationV1 {
    id: string;
    ownerAgencyId?: string;
    customerAgencyId: string;
    customerId: string;
    interfaceId: string;
    createdOn: string;
    updatedAt: string;
    allowAddNarrativeToCfs: boolean;
    allowAddMediaToIncident: boolean;
    allowDispatch: boolean;
    allowMediaUpload: boolean;
    allowCaseSearch: boolean;
    allowUserAssignment: boolean;
    allowStatusing: boolean;
    allowPostionAvl: boolean;
    allowPositionPlt: boolean;
    allowUserLogoff: boolean;
    allowDeviceAvlTransmission: boolean;
    allowUserNotificationMessages: boolean;
    /**
     * @deprecated Use AllowNcicQuery or AllowRecordQuery instead.
     */
    allowQuery: boolean;
    allowNcicQuery: boolean;
    allowRecordQuery: boolean;
    allowRecordQueryFromDispatch: boolean;
    allowViewingClosedCalls: boolean;
    allowClientIncidentCreation: boolean;
    allowSignatureCollection: boolean;
    statusing: StatusingConfigurationV1;
    authentication: AuthenticationConfigurationV1;
    ncic: NcicConfigurationV1;
    incidentClosedStatusIds: string[];
    defaultQuerySpecificationIds: Record<QueryAutoFillTypeV1, string>;
}

// ../Nebula.DTO/GlobalConfiguration/AuthenticationConfigurationV1.cs
export interface AuthenticationConfigurationV1 {
    allowLdapAuthentication: boolean;
    requireLdapAuthentication: boolean;
    agencyIdToLdapDomains: Record<string, string[]>;
    requireFederatedAuthentication: boolean;
}

// ../Nebula.DTO/Synchronization/DocumentBaselineManifestV1.cs
export interface DocumentBaselineManifestV1 {
    documentType: DocumentTypeV1;
    manifest: Record<string, number>;
}

// ../Nebula.DTO/Queries/QueryResponseStatusV1.cs
/**
 * Used to transmit periodic {@link QueryResponseV1} status updates
 * for a given {@link QueryRequestV1}.
 */
export interface QueryResponseStatusV1 {
    /**
     * An optional progress field indicating the current progress for the
     * response.
     */
    progress?: number;
    /**
     * Indicates if the state of the response's progress cannot be determined.
     */
    indeterminate: boolean;
    /**
     * Whether this is the final response for the request.
     */
    isFinished: boolean;
    /**
     * Indicates the overall success of the response. True indicates the
     * request was fielded wholly and successfully while false covers all
     * other cases.
     */
    result?: boolean;
    /**
     * An optional, associated error message that may be returned in the
     * event the response's {@link Result} indicates a failure.
     */
    error: string;
}

// ../Nebula.DTO/Queries/QueryResponsePayloadV1.cs
export interface QueryResponsePayloadV1 {
    /**
     * An indication of the format of the {@link Data}.
     */
    mimeType: string;
    /**
     * The data returned from a query request.
     */
    data: Int8Array;
}

// ../Nebula.DTO/Queries/QueryAutoFillTypeV1.cs
/**
 * Indicates different scan types to facilitate specifying specific
 * {@link QuerySpecificationV1} data to use when the client accesses
 * the query functionality via scanning functionality.
 */
export enum QueryAutoFillTypeV1 {
    None = 0,
    /**
     * Used for data that comes from a Drivers License scan.
     */
    DriversLicense = 1,
    /**
     * Used for data that comes from a Vehicle Identification Number scan.
     */
    VehicleIdentification = 2,
}

// ../Nebula.DTO/Queries/AutoFillValueV1.cs
/**
 * Enumeration to facilitate mapping of data from drivers license scans,
 * VIN scans, and other sources of data.
 */
export enum AutoFillValueV1 {
    None = 0,
    /**
     * The license number of a scanned drivers license.
     */
    DriversLicenseNumber = 1,
    /**
     * The VIN number from a VIN scan.
     */
    VehicleIdentificationNumber = 2,
    /**
     * Concatenated/joined full name consisting of, if available,
     * last name, first name, middle name.
     */
    FullName = 3,
    /**
     * Only the first name.
     */
    FirstName = 4,
    /**
     * Only the last name.
     */
    LastName = 5,
    /**
     * Only the middle name.
     */
    MiddleName = 6,
    /**
     * State, probably the two letter abreviation
     */
    State = 7,
    /**
     * Sex
     */
    Sex = 8,
    /**
     * Date of birth
     */
    DateOfBirth = 9,
}

// ../Nebula.DTO/Queries/QueryResponsePayloadMimeTypesV1.cs
export interface QueryResponsePayloadMimeTypesV1 {
}

// ../Nebula.DTO/Queries/QueryParameterResponseV1.cs
export interface QueryParameterResponseV1 extends SourcedPayload {
    /**
     * The {@link QueryParameterV1.Identifier} this response is
     * associated with.
     */
    queryParameterIdentifier: string;
    /**
     * The {@link QueryParameterV1.Name} this response is associated
     * with.
     */
    queryParameterName: string;
    /**
     * The data type this parameter response contains.
     */
    dataType: FieldDataTypeV1;
    /**
     * The value of the parameter response.
     */
    value: FieldValueV1;
    /**
     * Indicates that the parameter should be matched by phonetics.
     */
    usePhonetics: boolean;
}

// ../Nebula.DTO/Queries/QueryResponseTypeV1.cs
export enum QueryResponseTypeV1 {
    Invalid = 0,
    /**
     * Unformatted, raw text.
     */
    Raw = 1,
    /**
     * Corresponds to record response {@link PersonSummaryQueryResponseV1}.
     */
    RecordPersonSummaryQueryResponseV1 = 2,
    /**
     * Corresponds to record response {@link PersonDetailQueryResponseV1}.
     */
    RecordPersonDetailQueryResponseV1 = 3,
    /**
     * Corresponds to record response {@link OrganizationSummaryQueryResponseV1}.
     */
    RecordOrganizationSummaryQueryResponseV1 = 4,
    /**
     * Corresponds to record response {@link OrganizationDetailQueryResponseV1}.
     */
    RecordOrganizationDetailQueryResponseV1 = 5,
    /**
     * Corresponds to record response {@link VehicleSummaryQueryResponseV1}.
     */
    RecordVehicleSummaryQueryResponseV1 = 6,
    /**
     * Corresponds to record response {@link VehicleDetailQueryResponseV1}.
     */
    RecordVehicleDetailQueryResponseV1 = 7,
    /**
     * Corresponds to record response {@link AddressSummaryQueryResponseV1}.
     */
    RecordAddressSummaryQueryResponseV1 = 8,
    /**
     * Corresponds to record response {@link AddressDetailQueryResponseV1}.
     */
    RecordAddressDetailQueryResponseV1 = 9,
    /**
     * Corresponds to record response {@link CaseSummaryQueryResponseV1}.
     */
    RecordCaseSummaryQueryResponseV1 = 10,
    /**
     * Corresponds to record response {@link CaseDetailQueryResponseV1}.
     */
    RecordCaseDetailQueryResponseV1 = 11,
}

// ../Nebula.DTO/Queries/QueryParameterV1.cs
/**
 * Defines a parameter for a {@link QuerySpecificationV1}.
 */
export interface QueryParameterV1 {
    /**
     * An identifier that can be used by requesters and interfaces.
     */
    identifier: string;
    /**
     * The name of the parameter - intended for display to users.
     */
    name: string;
    /**
     * Whether the parameter is meant to be visible or treated as
     * hidden.
     */
    isDisplayed: boolean;
    /**
     * The sort order for the parameter.
     */
    sortOrder: number;
    /**
     * Whether the parameter is required or optional.
     */
    isRequired: boolean;
    /**
     * The entry type to be used by the client UX when requesting
     * a value for the parameter.
     */
    entryType: FieldEntryTypeV1;
    /**
     * The data type for the parameter.
     */
    dataType: FieldDataTypeV1;
    /**
     * Optional declaration of what data should be used to auto fill
     * this field. Multiple values can be specified and will be tried
     * in enumeration order until a valid value is found to populate.
     *
     * @remarks
     * The collection of values will be tried, in order, until a value that can
     * be filled in is found. Ensure you use an ordered collection such as 
     * {@link SortedSet{T}"/> or <see cref="SortedList{TKey, TValue}} if exploiting
     * this behavior.
     */
    autoFillValues: AutoFillValueV1[];
    /**
     * A default value for the parameter. Providing a default value may
     * cause a required field to be treated as optional.
     */
    defaultValue: FieldValueV1;
    /**
     * A collection of acceptable values to be provided and validated against
     * user input.
     */
    options: FieldValueV1[];
    /**
     * Indicates that the parameter supports phonetic matching.
     */
    isPhoneticsSupported: boolean;
}

// ../Nebula.DTO/Queries/QueryRequestV1.cs
export interface QueryRequestV1 {
    /**
     * The timezone that the client's (usually the field ops app) clock was set to when the request was made.
     * This is sometimes necessary for reconstructing the actual exact time a date was chosen by a user.
     */
    clientTimeZone: any;
    /**
     * A user-defined request ID that is used to track individual query
     * requests.
     */
    requestId: string;
    /**
     * The date the request was created on.
     */
    createdOn: string;
    /**
     * The date the request was last updated.
     */
    updatedOn: string;
    /**
     * A correlating {@link QuerySpecificationV1} ID identifying which
     * query the request is intended for.
     */
    specificationId: string;
    /**
     * The {@link IncidentV1} ID associated with the requester.
     */
    incidentId?: string;
    /**
     * The {@link EmergencyResourceV1} ID associated with the requester.
     */
    emergencyResourceId?: string;
    /**
     * A collection of {@link QueryParameterResponseV1}'s fulfilling
     * the associated {@link QuerySpecificationV1}'s parameter requirements.
     */
    parameters: QueryParameterResponseV1[];
    /**
     * The required response type from the query fielding interface. Must match
     * one of the defined {@link QueryResponseTypeV1} in the associated
     * {@link QuerySpecificationV1.SupportedResponseTypes}s.
     */
    requestedResponseType: QueryResponseTypeV1;
}

// ../Nebula.DTO/Queries/QueryCategoryV1.cs
/**
 * Used to categorize {@link QuerySpecificationV1} as fielding certain
 * types of requests.
 */
export enum QueryCategoryV1 {
    Invalid = 0,
    NCIC = 1,
    /**
     * @deprecated Use Record category instead
     */
    NameLookup = 2,
    Record = 3,
}

// ../Nebula.DTO/Queries/QueryResponseV1.cs
export interface QueryResponseV1 {
    /**
     * The {@link QueryRequestV1.RequestId} the response is associated
     * with.
     */
    queryRequestId: string;
    /**
     * The {@link QueryResponseTypeV1} format the payload adheres to. In
     * most, if not all, cases this should match the {@link QueryRequestV1.RequestedResponseType}.
     */
    responseType: QueryResponseTypeV1;
    /**
     * An encrypted payload containing some, or all, of the query request
     * as indicated by {@link Status}. May be null depending on the
     * status of the response.
     */
    payload: object;
    /**
     * Indicates the current state of the response, progress, whether this is
     * the final response, or whether an error occurred.
     */
    status: QueryResponseStatusV1;
}

// ../Nebula.DTO/Queries/QuerySpecificationV1.cs
/**
 * Defines a query an interface is capable of responding to.
 */
export interface QuerySpecificationV1 extends BaseDocumentModelV1 {
    /**
     * The name of the query.
     */
    name: string;
    /**
     * The description of the query.
     */
    description: string;
    /**
     * The category (type) of the query. Used for grouping queries in client
     * UX.
     */
    category: QueryCategoryV1;
    /**
     * The sub category of the query. Used to indicate the specific type of query.
     */
    subCategory: QuerySubCategoryV1;
    /**
     * The collection of required and optional fields for the query.
     */
    fields: QueryParameterV1[];
    /**
     * A collection of permission scenarios and associated permission rules
     * that define the permissions necessary for executing this query.
     */
    permissions: Record<PermissionScenariosV1, PermissionRuleV1>;
    /**
     * A collection of response types the sourcing interface is capable of
     * responding with. Query requests may indicate a response type that the
     * sourcer respond with from this list.
     */
    supportedResponseTypes: QueryResponseTypeV1[];
    /**
     * A key that ties a group of QuerySpecifications together, such as a group of 
     * "Gun Queries", "Vehicle Queries", "Boat Queries" or any other customer defined
     * grouping of queries.
     */
    queryTypeGroupKey: string;
    /**
     * The customer facing name of a set of grouped queries.
     * Queries in the same group must have the same {@link QueryTypeGroupKey}
     */
    queryTypeName: string;
    /**
     * The sort order for a query type.
     */
    queryTypeSortOrder?: number;
    /**
     * The name for a query subtype, for instance, a QueryType of "Vehicle Queries" may have 
     * a query subtype of "VIN" and a query subtype of "License Plate"
     */
    querySubTypeName: string;
    /**
     * The key to represent a group of query sub types.
     */
    querySubTypeGroupKey: string;
    /**
     * The sort order for a query sub type.
     */
    querySubTypeSortOrder?: number;
}

// ../Nebula.DTO/Queries/QuerySubCategoryV1.cs
export enum QuerySubCategoryV1 {
    Invalid = 0,
    /**
     * Record query for a list of person summaries.
     */
    RecordPersonSummary = 1,
    /**
     * Record query for a single person's details.
     */
    RecordPersonDetail = 2,
    /**
     * Record query for a list of organization summaries.
     */
    RecordOrganizationSummary = 3,
    /**
     * Record query for a single organization's details.
     */
    RecordOrganizationDetail = 4,
    /**
     * Record query for a list of vehicle summaries.
     */
    RecordVehicleSummary = 5,
    /**
     * Record query for a single vehicle's details.
     */
    RecordVehicleDetail = 6,
    /**
     * Record query for a a list of address summaries.
     */
    RecordAddressSummary = 7,
    /**
     * Record query for a single address's details.
     */
    RecordAddressDetail = 8,
    /**
     * Record query for a list of case summaries.
     */
    RecordCaseSummary = 9,
    /**
     * Record query for a single case's details.
     */
    RecordCaseDetail = 10,
}

// ../Nebula.DTO/Queries/Records/AddressDetailQueryResponseV1.cs
export interface AddressDetailQueryResponseV1 {
    result: AddressDetailV1;
}

// ../Nebula.DTO/Queries/Records/CaseAssociationV1.cs
export interface CaseAssociationV1 {
    type: string;
    case: CaseSummaryV1;
}

// ../Nebula.DTO/Queries/Records/OffenseAssociationV1.cs
export interface OffenseAssociationV1 {
    type: string;
    offense: OffenseSummaryV1;
}

// ../Nebula.DTO/Queries/Records/PersonDetailQueryResponseV1.cs
export interface PersonDetailQueryResponseV1 {
    result: PersonDetailV1;
}

// ../Nebula.DTO/Queries/Records/PersonAssociationV1.cs
export interface PersonAssociationV1 {
    type: string;
    person: PersonSummaryV1;
}

// ../Nebula.DTO/Queries/Records/CaseDetailQueryResponseV1.cs
export interface CaseDetailQueryResponseV1 {
    result: CaseDetailV1;
}

// ../Nebula.DTO/Queries/Records/OffenseSummaryV1.cs
export interface OffenseSummaryV1 {
    externalId: string;
    offenseNumber: string;
    description: string;
}

// ../Nebula.DTO/Queries/Records/PersonOffenseAssociationV1.cs
export interface PersonOffenseAssociationV1 {
    involvement: string;
    person: PersonSummaryV1;
    offense: OffenseSummaryV1;
}

// ../Nebula.DTO/Queries/Records/OrganizationVehicleOwnerAssociationV1.cs
export interface OrganizationVehicleOwnerAssociationV1 {
    organization: OrganizationSummaryV1;
}

// ../Nebula.DTO/Queries/Records/PersonSummaryV1.cs
export interface PersonSummaryV1 {
    externalId: string;
    name: string;
    dateOfBirth?: string;
    sex: string;
    age?: number;
    imageData: Int8Array;
    imageFormat: string;
    address: AddressSummaryV1;
    alertsCount?: number;
}

// ../Nebula.DTO/Queries/Records/OrganizationAssociationV1.cs
export interface OrganizationAssociationV1 {
    type: string;
    organization: OrganizationSummaryV1;
}

// ../Nebula.DTO/Queries/Records/PersonSummaryQueryResponseV1.cs
export interface PersonSummaryQueryResponseV1 {
    results: PersonSummaryV1[];
}

// ../Nebula.DTO/Queries/Records/AddressDetailV1.cs
export interface AddressDetailV1 {
    externalId: string;
    commonPlaceName: string;
    street: string;
    city: string;
    state: string;
    zip: string;
    alerts: AlertV1[];
    associatedPeople: PersonAssociationV1[];
    associatedOrganizations: OrganizationAssociationV1[];
    associatedAddresses: AddressAssociationV1[];
    associatedVehicles: VehicleAssociationV1[];
    associations: AssociationSummaryV1[];
    additionalData: AdditionalDataV1[];
}

// ../Nebula.DTO/Queries/Records/AddressSummaryQueryResponseV1.cs
export interface AddressSummaryQueryResponseV1 {
    results: AddressSummaryV1[];
}

// ../Nebula.DTO/Queries/Records/OrganizationDetailV1.cs
export interface OrganizationDetailV1 {
    externalId: string;
    name: string;
    address: AddressSummaryV1;
    phoneNumber: string;
    alerts: AlertV1[];
    associatedPeople: PersonAssociationV1[];
    associatedOrganizations: OrganizationAssociationV1[];
    associatedAddresses: AddressAssociationV1[];
    associatedVehicles: VehicleAssociationV1[];
    associations: AssociationSummaryV1[];
    additionalData: AdditionalDataV1[];
}

// ../Nebula.DTO/Queries/Records/CaseSummaryV1.cs
export interface CaseSummaryV1 {
    externalId: string;
    caseNumber: string;
    incidentCode: string;
    incidentDescription: string;
    reportedAt: string;
    disposition: string;
    primaryOfficer: string;
    investigator: string;
    alertsCount?: number;
}

// ../Nebula.DTO/Queries/Records/OrganizationSummaryV1.cs
export interface OrganizationSummaryV1 {
    externalId: string;
    name: string;
    address: AddressSummaryV1;
    alertsCount?: number;
    associatedPersonCount?: number;
    associatedPersonAlertsCount?: number;
    associationsCount?: number;
}

// ../Nebula.DTO/Queries/Records/VehicleAssociationV1.cs
export interface VehicleAssociationV1 {
    type: string;
    vehicle: VehicleSummaryV1;
}

// ../Nebula.DTO/Queries/Records/PersonDetailV1.cs
export interface PersonDetailV1 {
    externalId: string;
    name: string;
    dateOfBirth?: string;
    sex: string;
    age?: number;
    address: AddressSummaryV1;
    phoneNumber: string;
    imageData: Int8Array;
    imageFormat: string;
    ssn: string;
    oln: string;
    hairDescription: string;
    eyeDescription: string;
    heightDescription: string;
    weightDescription: string;
    ethnicityDescription: string;
    raceDescription: string;
    alerts: AlertV1[];
    associatedPeople: PersonAssociationV1[];
    associatedOrganizations: OrganizationAssociationV1[];
    associatedAddresses: AddressAssociationV1[];
    associatedVehicles: VehicleAssociationV1[];
    associatedCases: CaseAssociationV1[];
    associations: AssociationSummaryV1[];
    additionalData: AdditionalDataV1[];
}

// ../Nebula.DTO/Queries/Records/VehicleSummaryQueryResponseV1.cs
export interface VehicleSummaryQueryResponseV1 {
    results: VehicleSummaryV1[];
}

// ../Nebula.DTO/Queries/Records/VehicleDetailQueryResponseV1.cs
export interface VehicleDetailQueryResponseV1 {
    result: VehicleDetailV1;
}

// ../Nebula.DTO/Queries/Records/AddressAssociationV1.cs
export interface AddressAssociationV1 {
    type: string;
    address: AddressSummaryV1;
}

// ../Nebula.DTO/Queries/Records/CaseDetailV1.cs
export interface CaseDetailV1 {
    externalId: string;
    caseNumber: string;
    incidentCode: string;
    incidentDescription: string;
    incidentStartDate: string;
    incidentEndDate?: string;
    reportedAt: string;
    status: string;
    reportType: string;
    disposition: string;
    primaryOfficer: string;
    investigator: string;
    location: string;
    alerts: AlertV1[];
    associatedVehicles: VehicleAssociationV1[];
    associatedPeople: PersonAssociationV1[];
    associatedOffenses: OffenseAssociationV1[];
    associatedPersonOffenses: PersonOffenseAssociationV1[];
    associations: AssociationSummaryV1[];
    additionalData: AdditionalDataV1[];
}

// ../Nebula.DTO/Queries/Records/VehicleSummaryV1.cs
export interface VehicleSummaryV1 {
    externalId: string;
    licensePlateNumber: string;
    licensePlateState: string;
    registeredOwner: string;
    year: string;
    color: string;
    make: string;
    model: string;
    alertsCount?: number;
}

// ../Nebula.DTO/Queries/Records/PersonVehicleOwnerAssociationV1.cs
export interface PersonVehicleOwnerAssociationV1 {
    person: PersonSummaryV1;
}

// ../Nebula.DTO/Queries/Records/AddressSummaryV1.cs
export interface AddressSummaryV1 {
    externalId: string;
    commonPlaceName: string;
    street: string;
    city: string;
    state: string;
    zip: string;
    alertsCount?: number;
    associatedPersonCount?: number;
    associatedPersonAlertsCount?: number;
    associationsCount?: number;
}

// ../Nebula.DTO/Queries/Records/AssociationSummaryV1.cs
export interface AssociationSummaryV1 {
    date: string;
    title: string;
    description: string;
}

// ../Nebula.DTO/Queries/Records/AlertV1.cs
export interface AlertV1 {
    externalId: string;
    type: string;
    title: string;
    description: string;
    additionalData: AdditionalDataV1[];
}

// ../Nebula.DTO/Queries/Records/OrganizationDetailQueryResponseV1.cs
export interface OrganizationDetailQueryResponseV1 {
    result: OrganizationDetailV1;
}

// ../Nebula.DTO/Queries/Records/CaseSummaryQueryResponseV1.cs
export interface CaseSummaryQueryResponseV1 {
    results: CaseSummaryV1[];
}

// ../Nebula.DTO/Queries/Records/VehicleDetailV1.cs
export interface VehicleDetailV1 {
    externalId: string;
    licensePlateNumber: string;
    licensePlateState: string;
    licensePlateCounty: string;
    licensePlateExpirationYear: string;
    vehicleIdentificationNumber: string;
    year: string;
    color: string;
    make: string;
    model: string;
    additionalColors: string[];
    features: string[];
    registeredPersonOwners: PersonVehicleOwnerAssociationV1[];
    registeredOrganizationOwners: OrganizationVehicleOwnerAssociationV1[];
    associatedPeople: PersonAssociationV1[];
    associatedOrganizations: OrganizationAssociationV1[];
    associatedAddresses: AddressAssociationV1[];
    associatedVehicles: VehicleAssociationV1[];
    associatedCases: CaseAssociationV1[];
    associations: AssociationSummaryV1[];
    alerts: AlertV1[];
    additionalData: AdditionalDataV1[];
}

// ../Nebula.DTO/Queries/Records/OrganizationSummaryQueryResponseV1.cs
export interface OrganizationSummaryQueryResponseV1 {
    results: OrganizationSummaryV1[];
}

// ../Nebula.DTO/Subscriptions/FilterConfigurationResultV1.cs
/**
 * Details for a single Filter. Used largely for populating client UI.
 * Includes list of {@link FilterOptionV1} to provide options.
 */
export interface FilterConfigurationResultV1 {
    emergencyResourceFilterConfiguration: FilterResultV1[];
    incidentFilterConfiguration: FilterResultV1[];
}

// ../Nebula.DTO/Subscriptions/AgencySubscriptionV1.cs
export interface AgencySubscriptionV1 {
    id: string;
    frequency: SubscriptionFrequencyV1;
    filters: Record<number, string[]>;
}

// ../Nebula.DTO/Subscriptions/Jurisdiction2SubscriptionV1.cs
export interface Jurisdiction2SubscriptionV1 {
    id: string;
    frequency: SubscriptionFrequencyV1;
    filters: Record<number, string[]>;
}

// ../Nebula.DTO/Subscriptions/EmergencyResourceTypeSubscriptionV1.cs
export interface EmergencyResourceTypeSubscriptionV1 {
    id: string;
    frequency: SubscriptionFrequencyV1;
    filters: Record<number, string[]>;
}

// ../Nebula.DTO/Subscriptions/SubscriptionFilterDirectionV1.cs
export enum SubscriptionFilterDirectionV1 {
    Normal,
    Inverse,
}

// ../Nebula.DTO/Subscriptions/Jurisdiction3SubscriptionV1.cs
export interface Jurisdiction3SubscriptionV1 {
    id: string;
    frequency: SubscriptionFrequencyV1;
    filters: Record<number, string[]>;
}

// ../Nebula.DTO/Subscriptions/EmergencyResourceSubscriptionV1.cs
export interface EmergencyResourceSubscriptionV1 {
    id: string;
    frequency: SubscriptionFrequencyV1;
    filters: Record<number, string[]>;
    /**
     * Indicates the push index at which to resume the subscription from.
     */
    resumeAtPushIndex?: number;
}

// ../Nebula.DTO/Subscriptions/FilterOptionV1.cs
/**
 * Single Filter option. {@link Value"/> used for generating <see cref="FilterResultV1}.
 * This object can be ignored when building a connector.
 */
export interface FilterOptionV1 {
    label: string;
    value: string;
    sortOrder: number;
}

// ../Nebula.DTO/Subscriptions/CaseSubscriptionV1.cs
export interface CaseSubscriptionV1 {
    id: string;
    frequency: SubscriptionFrequencyV1;
    filters: Record<number, string[]>;
}

// ../Nebula.DTO/Subscriptions/StatusSubscriptionV1.cs
export interface StatusSubscriptionV1 {
    id: string;
    frequency: SubscriptionFrequencyV1;
    filters: Record<number, string[]>;
}

// ../Nebula.DTO/Subscriptions/FilterConfigurationSubscriptionV1.cs
export interface FilterConfigurationSubscriptionV1 {
    id: string;
    frequency: SubscriptionFrequencyV1;
}

// ../Nebula.DTO/Subscriptions/PrioritySubscriptionV1.cs
export interface PrioritySubscriptionV1 {
    id: string;
    frequency: SubscriptionFrequencyV1;
    filters: Record<number, string[]>;
}

// ../Nebula.DTO/Subscriptions/Jurisdiction1SubscriptionV1.cs
export interface Jurisdiction1SubscriptionV1 {
    id: string;
    frequency: SubscriptionFrequencyV1;
    filters: Record<number, string[]>;
}

// ../Nebula.DTO/Subscriptions/IncidentSubscriptionV1.cs
export interface IncidentSubscriptionV1 {
    id: string;
    frequency: SubscriptionFrequencyV1;
    filters: Record<number, string[]>;
    /**
     * Indicates the push index at which to resume the subscription from.
     */
    resumeAtPushIndex?: number;
}

// ../Nebula.DTO/Subscriptions/PositionSubscriptionV1.cs
export interface PositionSubscriptionV1 {
    id: string;
    frequency: SubscriptionFrequencyV1;
    filters: Record<number, string[]>;
    /**
     * Indicates the push index at which to resume the subscription from.
     */
    resumeAtPushIndex?: number;
}

// ../Nebula.DTO/Subscriptions/UserSubscriptionV1.cs
export interface UserSubscriptionV1 {
    id: string;
    frequency: SubscriptionFrequencyV1;
    filters: Record<number, string[]>;
}

// ../Nebula.DTO/Subscriptions/SubscriptionFrequencyV1.cs
/**
 * Desired frequency for updates to be pushed to a client
 * based on one of these models 
 * {@link IncidentSubscriptionV1} 
 * {@link EmergencyResourceSubscriptionV1} 
 * {@link FilterConfigurationSubscriptionV1} 
 * {@link PositionSubscriptionV1}.
 */
export enum SubscriptionFrequencyV1 {
    Low = 1,
    High = 2,
    RealTime = 3,
}

// ../Nebula.DTO/Subscriptions/FilterResultV1.cs
export interface FilterResultV1 {
    type: FilterTypeV1;
    label: string;
    options: FilterOptionV1[];
}

// ../Nebula.DTO/Subscriptions/Statusing/CommandSubscriptionV1.cs
export interface CommandSubscriptionV1 {
    id: string;
    frequency: SubscriptionFrequencyV1;
}

// ../Nebula.DTO/Subscriptions/Statusing/TransitionSubscription.cs
export interface TransitionSubscriptionV1 {
    id: string;
    frequency: SubscriptionFrequencyV1;
}

// ../Nebula.DTO/Subscriptions/GlobalConfiguration/GlobalConfigurationSubscriptionV1.cs
export interface GlobalConfigurationSubscriptionV1 {
    id: string;
    frequency: SubscriptionFrequencyV1;
}

// ../Nebula.DTO/IncidentAssignmentHistory/AgencyIncidentAssignmentSummaryV1.cs
export interface AgencyIncidentAssignmentSummaryV1 {
    index: number;
    incidentId: string;
    incidentNumber: string;
    incidentCode: string;
    incidentCodeDescription: string;
    incidentDateTime: string;
    incidentLocation: LocationV1;
}

// ../Nebula.DTO/IncidentAssignmentHistory/EmergencyResourceIncidentAssignmentHistoryRequestV1.cs
export interface EmergencyResourceIncidentAssignmentHistoryRequestV1 {
    range: IncidentAssignmentHistoryRangeV1;
    lastIndex: number;
    pageSize: number;
    emergencyResourceId: string;
    excludeOpenIncidents: boolean;
}

// ../Nebula.DTO/IncidentAssignmentHistory/IncidentDetailRequestV1.cs
export interface IncidentDetailRequestV1 {
    incidentId: string;
}

// ../Nebula.DTO/IncidentAssignmentHistory/IncidentAssignmentHistoryRangeV1.cs
export enum IncidentAssignmentHistoryRangeV1 {
    Last12Hours = 0,
    Last24Hours = 1,
}

// ../Nebula.DTO/IncidentAssignmentHistory/IncidentDetailResponseV1.cs
export interface IncidentDetailResponseV1 {
    detail: IncidentDetailV1;
}

// ../Nebula.DTO/IncidentAssignmentHistory/EmergencyResourceIncidentAssignmentSummaryV1.cs
export interface EmergencyResourceIncidentAssignmentSummaryV1 {
    index: number;
    emergencyResourceId: string;
    incidentId: string;
    incidentPriorityId?: string;
    incidentNumber: string;
    incidentCode: string;
    incidentCodeDescription: string;
    incidentDateTime: string;
    incidentLocation: LocationV1;
}

// ../Nebula.DTO/IncidentAssignmentHistory/EmergencyResourceIncidentAssignmentHistoryResponseV1.cs
export interface EmergencyResourceIncidentAssignmentHistoryResponseV1 {
    history: EmergencyResourceIncidentAssignmentSummaryV1[];
}

// ../Nebula.DTO/IncidentAssignmentHistory/AgencyIncidentAssignmentHistoryRequestV1.cs
export interface AgencyIncidentAssignmentHistoryRequestV1 {
    range: IncidentAssignmentHistoryRangeV1;
    lastIndex: number;
    pageSize: number;
    dispatchAgencyIds: string[];
    responderAgencyIds: string[];
    jurisdiction1Ids: string[];
    jurisdiction2Ids: string[];
    jurisdiction3Ids: string[];
    priorityIds: string[];
    excludeOpenIncidents: boolean;
}

// ../Nebula.DTO/IncidentAssignmentHistory/IncidentDetailV1.cs
export interface IncidentDetailV1 {
    incident: IncidentV1;
}

// ../Nebula.DTO/IncidentAssignmentHistory/UserIncidentAssignmentHistoryRequestV1.cs
export interface UserIncidentAssignmentHistoryRequestV1 {
    range: IncidentAssignmentHistoryRangeV1;
    lastIndex: number;
    pageSize: number;
    userId: string;
    excludeOpenIncidents: boolean;
}

// ../Nebula.DTO/IncidentAssignmentHistory/UserIncidentAssignmentSummaryV1.cs
export interface UserIncidentAssignmentSummaryV1 {
    index: number;
    emergencyResourceId: string;
    incidentId: string;
    incidentNumber: string;
    incidentCode: string;
    incidentCodeDescription: string;
    incidentDateTime: string;
    incidentLocation: LocationV1;
}

// ../Nebula.DTO/IncidentAssignmentHistory/UserIncidentAssignmentHistoryResponseV1.cs
export interface UserIncidentAssignmentHistoryResponseV1 {
    history: UserIncidentAssignmentSummaryV1[];
}

// ../Nebula.DTO/IncidentAssignmentHistory/AgencyIncidentAssignmentHistoryResponseV1.cs
export interface AgencyIncidentAssignmentHistoryResponseV1 {
    history: AgencyIncidentAssignmentSummaryV1[];
}

// ../Nebula.DTO/Avl/DeviceLocationV1.cs
export interface DeviceLocationV1 {
    timestamp: string;
    latitude: number;
    longitude: number;
    altitude: number;
    accuracy: number;
    altitudeAccuracy?: number;
    heading: number;
    speed: number;
}

// ../Nebula.DTO/Communication/RequestTokenV1.cs
export interface RequestTokenV1 {
    requestId: string;
}

// ../Nebula.DTO/Communication/SecureConnectionProtocolV1.cs
export enum SecureConnectionProtocolV1 {
    /**
     * Default secure connection protocol.
     */
    Unknown = 0,
    /**
     * Successor to the default, untracked protocol.
     * /// Supports:
     * - IV Rotation
     */
    v1_0_0 = 1,
}

// ../Nebula.DTO/Communication/SourcedPayload.cs
export interface SourcedPayload extends ISourcedPayload {
    payloadSource: FieldOpsSources;
}

// ../Nebula.DTO/ClientConfiguration/CustomerDeviceLoggingConfigurationsV1.cs
/**
 * Key: {@link CustomerDeviceConfigurationKeyV1.CustomerDeviceLogging}
 * Tracks device logging configuration
 */
export interface CustomerDeviceLoggingConfigurationV1 {
    verboseLoggingEnabled: boolean;
    enabledTypes: CustomerDeviceLoggingFeatureTypesV1[];
}

// ../Nebula.DTO/ClientConfiguration/CustomerDeviceLoggingConfigurationsV1.cs
export enum CustomerDeviceLoggingFeatureTypesV1 {
    AvlPlt = 0,
    Media = 1,
    NebulaConnection = 2,
    PushNotifications = 3,
    StateNcic = 4,
    RecordSearching = 5,
    DocumentSubscriptions = 6,
}

// ../Nebula.DTO/ClientConfiguration/CustomerDeviceConfigurationKeyV1.cs
export enum CustomerDeviceConfigurationKeyV1 {
    CustomerDeviceLogging = 0,
    CustomerDeviceCertificate = 1,
}

// ../Nebula.DTO/ClientConfiguration/LocationSharingConfigurationV1.cs
export interface LocationSharingConfigurationV1 {
    deviceLocationSharingIntervalInSeconds: number;
    forceOverrideAllDeviceConfigurations: boolean;
}

// ../Nebula.DTO/ClientConfiguration/CustomerDeviceCertificateConfigurationV1.cs
export interface CustomerDeviceCertificateConfigurationV1 {
    allowManualCertificateRenewal: boolean;
    certificateRenewalWindowInSecondsOverride?: number;
}

// ../Nebula.Api.Admin/Identity/WebAdminUser.cs
export interface WebAdminUser {
    id: string;
    externalId: string;
    name: string;
    fullName: string;
    isAdmin: boolean;
    isLicensed: boolean;
    supportUserId?: string;
    isSupport: boolean;
    customerUserId?: string;
    authenticationProviderType: AuthenticationProviderType;
    email: string;
    phone: string;
    securityStamp: string;
    accessToken: string;
    roles: string[];
    ownerAgencyId: string;
    isAssisting: boolean;
    licensedAgencyId?: string;
    licensedAgencyName: string;
    customerAgencyId?: string;
    customerAgencyName: string;
    emergencyResourceId?: string;
    permissions: SupportUserPermission[];
    scopes: Scope[];
    authorizedLicensedAgencies: string[];
}

// ../Nebula.Api.Admin/Models/PartnerAgencyViewModel.cs
/**
 * View model for a agency's partnered agency.
 */
export interface PartnerAgencyViewModel {
    id: string;
    customerAgencyName: string;
    customerAgencyId: string;
    agencyName: string;
    agencyId: string;
}

// ../Nebula.Api.Admin/Models/CustomerDatabaseConfigurationViewModel.cs
export interface ShardMigration {
    customerAgencyId: string;
    status: ShardMigrationStatus;
    message: string;
    sourceDatabaseShardId: string;
    destinationDatabaseShardId: string;
    createdOn?: string;
    startedOn?: string;
    updatedOn?: string;
    endedOn?: string;
}

// ../Nebula.Api.Admin/Models/CustomerDatabaseConfigurationViewModel.cs
export interface CustomerDatabaseConfigurationRequestViewModel {
    customerAgencyId: string;
    sourceDatabaseShardId: string;
    destinationDatabaseShardId: string;
}

// ../Nebula.Api.Admin/Models/CustomerDatabaseConfigurationViewModel.cs
export interface CustomerDatabaseConfigurationViewModel {
    customerAgencyId: string;
    databaseShardId: string;
    shardMigration: ShardMigration;
}

// ../Nebula.Api.Admin/Models/CustomerDatabaseConfigurationViewModel.cs
export interface CustomerDatabaseConfigurationViewModelResult extends ApiResult<CustomerDatabaseConfigurationViewModel> {
}

// ../Nebula.Api.Admin/Models/CustomerDatabaseConfigurationViewModel.cs
export enum ShardMigrationStatus {
    None = 0,
    Pending = 1,
    MigratingDocuments = 2,
    MigratingRelatedData = 3,
    CleaningUp = 4,
    Completed = 5,
    FaultedMigration = 6,
    FaultedCleanup = 7,
}

// ../Nebula.Api.Admin/Models/InterfaceStateChange.cs
export interface InterfaceStateChange {
    newState: InterfaceState;
    occurredOn: string;
    duration: any;
    durationDescription: string;
}

// ../Nebula.Api.Admin/Models/InterfaceHealthData.cs
export interface InterfaceHealthData {
    state: InterfaceGrainState;
    healthStatus: InterfaceHealthStatus;
    operationInfo: Record<InterfaceOperations, OperationStatus>;
}

// ../Nebula.Api.Admin/Models/CustomerManagementViewModel.cs
export interface CustomerManagementViewModel {
    productLines: any[];
    connectorTypes: any[];
    customers: CustomerAgencyModel[];
    productLineFilter: CustomerProductLine;
    connectorTypeFilter: ConnectorType;
}

// ../Nebula.Api.Admin/Models/PartnerAgencyConfigViewModel.cs
/**
 * View model for a agency's partnered agencies.
 */
export interface PartnerAgencyConfigViewModel {
    customerAgencyId: string;
    licensedAgencyId: string;
    agencyId: string;
    partnerAgencies: PartnerAgencyViewModel[];
}

// ../Nebula.Api.Admin/Models/DataSearchResultsViewModel.cs
export interface DataSearchResultsViewModel {
    type: string;
    id: string;
    agency: string;
    status: string;
    isActive: string;
    assignedToId: string;
    permissions: string;
    nebulaId: string;
}

// ../Nebula.Api.Admin/Models/UserViewModelExtensions.cs
export interface UserViewModelExtensions {
}

// ../Nebula.Api.Admin/Models/StackHealth.cs
export interface StackHealth {
    health: string;
    healthStatus: string;
    orleans: OrleansHealth;
    error: HealthError;
    versions: Record<string, string>;
    pushNotificationPlatformArns: Record<ClientNotificationType, Record<AmazonSnsPushPlatformTypes, string>>;
    cloudStorageSubscriptionStatus: CloudStorageSubscriptionStatus;
    grainTimeouts: Record<string, number>;
}

// ../Nebula.Api.Admin/Models/CustomerAdminViewModel.cs
export interface CustomerAdminViewModel {
    customerUserId: string;
    userId: string;
    name: string;
    fullName: string;
    phone: string;
    email: string;
    cadEmails: string[];
    licensedAgencyId: string;
    licensedAgencyName: string;
    customerAgencyId: string;
    isActive: boolean;
    authorizedLicensedAgencies: string[];
}

// ../Nebula.Api.Admin/Models/HealthError.cs
export interface HealthError {
    message: string;
    stackTrace: string;
}

// ../Nebula.Api.Admin/Models/CustomerCertificateStatus.cs
export enum CustomerCertificateStatus {
    Active = 0,
    Expired = 1,
    Revoked = 2,
    Replaced = 3,
    ActivePendingReplacement = 4,
}

// ../Nebula.Api.Admin/Models/ModelMappings.cs
export interface ModelMappings {
}

// ../Nebula.Api.Admin/Models/UserViewModel.cs
export interface UserViewModel {
    id: string;
    externalId: string;
    createdOn: string;
    updatedAt: string;
    name: string;
    fullName: string;
    email: string;
    emailAddresses: EmailAddress[];
    phone: string;
    phones: Phone[];
    ownerAgencyId: string;
    ownerAgencyExternalId: string;
    customerAgencyId: string;
    customerId: string;
    interfaceId: string;
    emergencyResourceId?: string;
    emergencyResourceExternalId: string;
    previouslyAssignedEmergencyResourceId?: string;
    permissions: string[];
    capabilities: UserCapabilities[];
    ownerAgencyName: string;
    isActive: boolean;
}

// ../Nebula.Api.Admin/Models/CustomerAgencyViewModel.cs
export interface CustomerAgencyViewModel {
    customerAgencyId: string;
    name: string;
    notes: string;
    productLine?: CustomerProductLine;
    connectorType?: ConnectorType;
    agencies: Agency[];
    licensedAgencies: LicensedAgency[];
    supportCreatedOn?: string;
    supportUpdatedOn?: string;
    createdBySupportUserId?: string;
    updatedBySupportUserId?: string;
    createdBySupportUserName: string;
    updatedBySupportUserName: string;
    databaseShardId?: number;
}

// ../Nebula.Api.Admin/Models/DataSearchViewModel.cs
export interface DataSearchViewModel extends IValidatableObject {
    customerAgencyId?: string;
    avalableTypes: DocumentTypeV1[];
    searchValue: string;
    selectedType?: DocumentTypeV1;
    results: DataSearchResultsViewModel[];
}

// ../Nebula.Api.Admin/Models/LicensedAgencyViewModel.cs
export interface LicensedAgencyViewModel {
    licensedAgencyId: string;
    customerAgencyId: string;
    documentId?: string;
    externalId: string;
    externalAgencyList: Agency[];
    name: string;
    licenseCount?: number;
    uniqueIdentifier: string;
    advancedAuthType: AdvancedAuthenticationType;
    notes: string;
    interconnectedCadConfiguration: any;
    globalConfiguration: GlobalConfiguration;
}

// ../Nebula.Api.Admin/Models/CustomerUserViewModel.cs
export interface CustomerUserViewModel {
    id?: string;
    userId?: string;
    name: string;
    fullName: string;
    externalPhoneList: string[];
    phone: string;
    externalEmailList: string[];
    email: string;
    customerAgencyId?: string;
    licensedAgencyId?: string;
    licensedAgencyName: string;
    capabilities: UserCapabilities[];
    permissions: string[];
    globalConfiguration: GlobalConfiguration;
    ownerAgencyExternalId: string;
    userAgencyGlobalConfiguration: GlobalConfiguration;
    isActive: boolean;
}

// ../Nebula.Api.Admin/Models/CustomerUserViewModelExtensions.cs
export interface CustomerUserViewModelExtensions {
}

// ../Nebula.Api.Admin/Models/CustomerCertificateViewModel.cs
export interface CustomerCertificateViewModel {
    customerCertificateId: string;
    fingerprint: string;
    issuedOn: string;
    expiresOn: string;
    revoked: boolean;
    replacementCustomerCertificateId?: string;
    pendingReplacement: boolean;
    status: CustomerCertificateStatus;
}

// ../Nebula.Api.Admin/Models/CustomerDeviceViewModel.cs
export interface CustomerDeviceViewModel {
    customerDeviceId: string;
    name: string;
    assignedUserId?: string;
    assignedUserName: string;
    sendPinToAssignedUser?: boolean;
    pinSentToAssignedUserSuccessfully?: boolean;
    customerAgencyId?: string;
    licensedAgencyId?: string;
    pin: string;
    pinExpiresOn?: string;
    pinExpired: boolean;
    pinUsed: boolean;
    email: string;
    notes: string;
    /**
     * Ignored for JSON requests
     */
    replaceConfirmed: boolean;
    properties: Record<DeviceProperty, string>;
    loggingConfiguration: CustomerDeviceLoggingConfiguration;
    certificateConfiguration: CustomerDeviceCertificateConfiguration;
}

// ../Nebula.Api.Admin/Models/OrleansHealth.cs
export interface OrleansHealth {
    isInitialized: boolean;
    clusterId: string;
}

// ../Nebula.Api.Admin/Models/SslInfo.cs
export interface SslInfo {
    protocol: string;
    cipher: string;
}

// ../Nebula.Api.Admin/Models/Formatters.cs
export interface Formatters {
}

// ../Nebula.Api.Admin/Models/GetInterfacesCurrentlyDisconnectedResponse.cs
export interface GetInterfacesCurrentlyDisconnectedResponse {
    interfaces: CustomerInterfaceDisconnectionsMetadata[];
}

// ../Nebula.Api.Admin/Models/CustomerAdminViewModelExtensions.cs
export interface CustomerAdminViewModelExtensions {
}

// ../Nebula.Api.Admin/Models/CustomerInterfaceViewModel.cs
export interface CustomerInterfaceViewModel {
    customerInterfaceId?: string;
    /**
     * @deprecated Deprecated when moving to overarching Customer Agency / Licensed Agencies
     */
    interfaceType?: InterfaceType;
    customerAgencyId?: string;
    name: string;
    notes: string;
    interfaceRegistration: InterfaceRegistration;
    errorMessage: string;
    healthState?: InterfaceHealthColor;
    certificateRevocationStatus?: CertificateRevocationStatus;
    certificateHistory: CustomerCertificateViewModel[];
}

// ../Nebula.Api.Admin/Models/CustomerDeviceViewModelExtensions.cs
export interface CustomerDeviceViewModelExtensions {
}

// ../Nebula.Api.Admin/Models/CustomerCertificateSigningRequestViewModel.cs
export interface CustomerCertificateSigningRequestViewModel {
    customerInterfaceId: string;
    customerAgencyId: string;
    csrPem: string;
}

// ../Nebula.Api.Admin/Models/AgencySettingsViewModel.cs
export interface AgencySettingsViewModel {
    allowedAgencies: LicensedAgency[];
    currentAgencyId: string;
    currentAgency: LicensedAgency;
    agencyUsers: UserViewModel[];
    licensedAgencyAdminUsers: CustomerUserViewModel[];
}

// ../Nebula.Api.Admin/Models/UserManagementViewModel.cs
export interface UserManagementViewModel {
    allowedAgencies: LicensedAgency[];
    currentAgencyId: string;
    currentAgency: LicensedAgency;
    agencyUsers: UserViewModel[];
    licensedAgencyUsers: CustomerUserViewModel[];
}

// ../Nebula.Api.Admin/Models/CustomerAgencyModelExtensions.cs
export interface CustomerAgencyModelExtensions {
}

// ../Nebula.Api.Admin/Models/EmergencyResourceDataSearchResultsViewModel.cs
export interface EmergencyResourceDataSearchResultsViewModel extends DataSearchResultsViewModel {
    avlLastUpdatedFromCad?: string;
    avlLastUpdatedFromFoDevice?: string;
    deviceAvlMonitoringStartedAt?: string;
    deviceAvlMonitoringEnabled: boolean;
}

// ../Nebula.Api.Admin/Models/GetInterfacesWithDisconnectionsSinceResponse.cs
export interface GetInterfacesWithDisconnectionsSinceResponse {
    interfaces: CustomerInterfaceDisconnectionsMetadata[];
}

// ../Nebula.Api.Admin/Models/ActivateLicenseViewModel.cs
export interface ActivateLicenseViewModel {
    agencyId: string;
    email: string;
    phoneNumber: string;
}

// ../Nebula.Api.Admin/Models/CustomerInterfaceDisconnectionsMetadata.cs
export interface CustomerInterfaceDisconnectionsMetadata {
    customerAgencyId: string;
    customerAgencyName: string;
    customerInterfaceId: string;
    customerInterfaceName: string;
    lastDisconnected: string;
    disconnectCount?: number;
}

// ../Nebula.Api.Admin/Models/CustomerViewModel.cs
export interface CustomerViewModel {
    customerId: string;
    name: string;
    notes: string;
    productLine: CustomerProductLine;
    connectorType: ConnectorType;
}

// ../Nebula.Api.Admin/Models/CustomerDetailsViewModel.cs
export interface CustomerDetailsViewModel {
    customer: CustomerAgency;
    connectors: CustomerInterfaceModel[];
    agencies: LicensedAgencyViewModel[];
    agencyDocuments: Agency[];
    hasOwnerAgencies: boolean;
    showConnectorDataSearch: boolean;
    enabledFeatures: InterfaceFeature[];
}

// ../Nebula.Api.Admin/Models/CertificateInfo.cs
export interface CertificateInfo {
    fingerprint: string;
    issuedOn?: string;
    expiresOn?: string;
    revocationStatus: string;
}

// ../Nebula.Api.Admin/Models/AgencyAdminViewModel.cs
export interface AgencyAdminViewModel {
    id: string;
}

// ../Nebula.Api.Admin/Models/DeviceManagementViewModel.cs
export interface DeviceManagementViewModel {
    allowedAgencies: CustomerAgency[];
    currentAgencyId: string;
    currentAgency: LicensedAgency;
    customerDevices: CustomerDeviceViewModel[];
    /**
     * When redirected to device management after a user is added, this is the ID for the added user.
     */
    customerUserId: string;
}

// ../Nebula.Api.Admin/Models/InterfaceHealth.cs
export interface InterfaceHealth {
    name: string;
    customerAgencyId: string;
    customerInterfaceId: string;
    timeDelta?: any;
    state: string;
    connectionId?: string;
    updatedAt?: string;
    operationState: Record<InterfaceOperations, OperationStatus>;
    detailedStats: string;
    version: string;
    apiVersion: string;
    abstractionVersion: string;
    certificate: CertificateInfo;
    registration: InterfaceRegistration;
    sslInfo: SslInfo;
    healthCheckStatus: InterfaceHealthStatus;
    status: string;
    statusMessage: string;
    connectionStateHistory: InterfaceStateChange[];
    enabledFeatures: string[];
}

// ../Nebula.Api.Admin/Models/Status/Version.cs
export interface Version {
    semanticVersion: string;
    informationalVersion: string;
}

// ../Nebula.Api.Admin/Models/DiagnosticConfiguration/DiagnosticConfigurationViewModel.cs
export interface DiagnosticConfigurationViewModel {
    globalRuntimeLogLevelOverride?: any;
    nebulaApiRuntimeLogLevelOverride?: any;
    nebulaOrleansHostRuntimeLogLevelOverride?: any;
    nebulaIdentityServerRuntimeLogLevelOverride?: any;
    nebulaWebAdminRuntimeLogLevelOverride?: any;
}

// ../Nebula.Api.Admin/Models/DiagnosticConfiguration/LocationSharingConfigurationViewModel.cs
export interface LocationSharingConfigurationViewModel {
    deviceLocationSharingIntervalInSeconds: number;
    forceOverrideAllDeviceConfigurations: boolean;
}

// ../Nebula.Api.Admin/Models/DiagnosticConfiguration/ImportConfigurationViewModel.cs
export interface ImportConfigurationViewModel {
    enableLegacyDocumentImportDeduping?: boolean;
    enableDocumentImportDeduping?: boolean;
    enableLambdaThumbnailGeneration?: boolean;
    enableS3FileDocumentCleanup?: boolean;
    enableStatusingDocumentDebugLogs?: boolean;
    importLockAcquisitionDelayInSeconds?: number;
    enableDeviceAvlPushMonitoring?: boolean;
}

// ../Nebula.Api.Admin/Models/DiagnosticConfiguration/SupportFederatedAuthenticationConfiguration.cs
export interface SupportFederatedAuthenticationConfiguration {
    authorizeEndpoint: string;
    tokenEndpoint: string;
    userInfoEndpoint: string;
    logoutEndpoint: string;
    scope: string;
    tokenKeys: string;
    clientId: string;
    grantType: string;
    supportsPkce: boolean;
}

// ../Nebula.Api.Admin/Models/UserLicenseMigration/CustomerDataMigrationViewModel.cs
export interface CustomerDataMigrationViewModel {
    customerAgencyId: string;
    customerAgencyName: string;
    migrationContext: MigrationContext;
    migrationResult: MigrationResult;
    hasPushedExpectedDocuments: boolean;
}

// ../Nebula.Api.Admin/Models/UserLicenseMigration/CustomerDataMigrationViewModel.cs
export interface StringExtensions {
}

// ../Nebula.Api.Admin/Models/UserLicenseMigration/CustomerDataMigrationViewModel.cs
export interface ViewModelExtensions {
}

// ../Nebula.Api.Admin/Models/Audit/AuditEventType.cs
export interface AuditEventType {
    id: string;
    isSupport: boolean;
}

// ../Nebula.Api.Admin/Models/Audit/Snapshots/CustomerAgencyDocumentQueryAuditSnapshotV1.cs
export interface CustomerAgencyDocumentQueryAuditSnapshotV1 extends IAuditEventData {
    type: string;
    customerAgencyId: string;
    documentType: string;
    searchField: string;
    searchValue: string;
}

// ../Nebula.Api.Admin/Models/Audit/Snapshots/MapLayerAuditSnapshotV2.cs
export interface MapLayerAuditSnapshotV2 extends IAuditEventData {
    type: string;
    name: string;
    displayName: string;
    fileName: string;
    sharedLicensedAgencies: Record<string, string>;
    notes: string;
}

// ../Nebula.Api.Admin/Models/Audit/Snapshots/SupportUserAuditSnapshotV1.cs
export interface SupportUserAuditSnapshotV1 extends IAuditEventData {
    type: string;
    id: string;
    username: string;
    isDisabled: boolean;
    permissions: string[];
}

// ../Nebula.Api.Admin/Models/Audit/Snapshots/CustomerDeviceMediaRecoveryCasesSearchedAuditSnapshotV1.cs
export interface CustomerDeviceMediaRecoveryCasesSearchedAuditSnapshotV1 extends IAuditEventData {
    type: string;
    caseNumber: string;
    incidentStartDate: string;
    incidentEndDate: string;
}

// ../Nebula.Api.Admin/Models/Audit/Snapshots/CustomerInterfaceLoggingConfigBulkEditAuditSnapshotV1.cs
export interface CustomerInterfaceLoggingConfigBulkEditAuditSnapshotV1 extends IAuditEventData {
    type: string;
    productLine: string;
    configuration: CustomerInterfaceLoggingConfigAuditSnapshotV1;
}

// ../Nebula.Api.Admin/Models/Audit/Snapshots/CustomerCustomMapLayerAuditSnapshotV1.cs
export interface CustomerCustomMapLayerAuditSnapshotV1 extends IAuditEventData {
    type: string;
    name: string;
    displayName: string;
    fileName: string;
    notes: string;
    fileChanged: boolean;
}

// ../Nebula.Api.Admin/Models/Audit/Snapshots/CustomerAgencyDataSearchAuditSnapshotV1.cs
export interface CustomerAgencyDataSearchAuditSnapshotV1 extends IAuditEventData {
    type: string;
    customerAgencyId: string;
    searchType: string;
    searchValue: string;
}

// ../Nebula.Api.Admin/Models/Audit/Snapshots/CustomerDeviceLoggingConfigAuditSnapshotV1.cs
export interface CustomerDeviceLoggingConfigAuditSnapshotV1 extends IAuditEventData {
    type: string;
    verboseLoggingEnabled: boolean;
    enabledTypes: string[];
}

// ../Nebula.Api.Admin/Models/Audit/Snapshots/CustomerInterfaceImportConfigurationAuditSnapshotV1.cs
export interface CustomerInterfaceImportConfigurationAuditSnapshotV1 extends IAuditEventData {
    type: string;
    enableLegacyDocumentImportDeduping?: boolean;
    enableDocumentImportDeduping?: boolean;
    enableLambdaThumbnailGeneration?: boolean;
    enableS3FileDocumentCleanup?: boolean;
    enableStatusingDocumentDebugLogs?: boolean;
    enableDeviceAvlPushMonitoring?: boolean;
    importLockAcquisitionDelayInSeconds?: number;
}

// ../Nebula.Api.Admin/Models/Audit/Snapshots/MapLayerAuditSnapshotV3.cs
export interface MapLayerAuditSnapshotV3 extends IAuditEventData {
    type: string;
    name: string;
    displayName: string;
    sharedLicensedAgencies: Record<string, string>;
    notes: string;
}

// ../Nebula.Api.Admin/Models/Audit/Snapshots/CertificateConfigurationAuditSnapshotV1.cs
export interface CertificateConfigurationAuditSnapshotV1 extends IAuditEventData {
    type: string;
    deviceCertificateDurationSecondsOverride?: number;
    connectorCertificateDurationSecondsOverride?: number;
    identityCertificateDurationSecondsOverride?: number;
    forceCertificateGenerationError: boolean;
}

// ../Nebula.Api.Admin/Models/Audit/Snapshots/UserSignInAuditSnapshotV1.cs
export interface UserSignInAuditSnapshotV1 extends IAuditEventData {
    type: string;
    userId?: string;
    username: string;
    userFullName: string;
    agencyId: string;
    domain: string;
    result: string;
    isFedAuth: boolean;
}

// ../Nebula.Api.Admin/Models/Audit/Snapshots/CustomerDeviceAuditSnapshotV1.cs
export interface CustomerDeviceAuditSnapshotV1 extends IAuditEventData {
    type: string;
    customerDeviceId: string;
    name: string;
    notes: string;
    assignedUserId?: string;
    assignedUserName: string;
    loggingConfig: CustomerDeviceLoggingConfigAuditSnapshotV1;
    certificateConfig: CustomerDeviceCertificateConfigAuditSnapshotV1;
}

// ../Nebula.Api.Admin/Models/Audit/Snapshots/LicensedAgencyAuditSnapshotV1.cs
export interface LicensedAgencyAuditSnapshotV1 extends IAuditEventData {
    type: string;
    id: string;
    name: string;
    uniqueIdentifier: string;
    licenseCount: number;
    authenticationType: string;
    notes: string;
}

// ../Nebula.Api.Admin/Models/Audit/Snapshots/ApiTokenInvocationAuditSnapshotV1.cs
export interface ApiTokenInvocationAuditSnapshotV1 extends IAuditEventData {
    type: string;
    tokenId: string;
    url: string;
    ipAddress: string;
    httpMethod: string;
    userId: string;
    username: string;
}

// ../Nebula.Api.Admin/Models/Audit/Snapshots/PermissionCheckAuditSnapshotV1.cs
export interface PermissionCheckAuditSnapshotV1 extends IAuditEventData {
    type: string;
    customerAgencyId: string;
    customerUserId: string;
    customerUsername: string;
    customerUserFullname: string;
    documentType: string;
    targetField: string;
    targetFieldValue: string;
}

// ../Nebula.Api.Admin/Models/Audit/Snapshots/CustomerUserAuditSnapshotV1.cs
export interface CustomerUserAuditSnapshotV1 extends IAuditEventData {
    type: string;
    id: string;
    fullName: string;
    username: string;
    phone: string;
    email: string;
}

// ../Nebula.Api.Admin/Models/Audit/Snapshots/CustomerDeviceCertificateConfigAuditSnapshotV1.cs
export interface CustomerDeviceCertificateConfigAuditSnapshotV1 extends IAuditEventData {
    type: string;
    allowManualCertificateRenewal: boolean;
    certificateRenewalWindowInSecondsOverride?: number;
}

// ../Nebula.Api.Admin/Models/Audit/Snapshots/CustomerAdminAuditSnapshotV1.cs
export interface CustomerAdminAuditSnapshotV1 extends IAuditEventData {
    type: string;
    id: string;
    fullName: string;
    username: string;
    phone: string;
    email: string;
    authorizedLicensedAgencies: Record<string, string>;
}

// ../Nebula.Api.Admin/Models/Audit/Snapshots/MediaUploadsReportAccessAuditSnapshotV1.cs
export interface MediaUploadsReportAccessAuditSnapshotV1 extends IAuditEventData {
    type: string;
    beginDate: string;
    endDate: string;
}

// ../Nebula.Api.Admin/Models/Audit/Snapshots/DiagnosticConfigurationAuditSnapshotV1.cs
export interface DiagnosticConfigurationAuditSnapshotV1 extends IAuditEventData {
    type: string;
    runtimeGlobalLogLevelOverride: string;
    runtimeSystemComponentLogLevelOverrides: Record<string, string>;
}

// ../Nebula.Api.Admin/Models/Audit/Snapshots/CustomerInterfaceAuditMinSnapshotV1.cs
export interface CustomerInterfaceAuditMinSnapshotV1 extends IAuditEventData {
    type: string;
    customerInterfaceId: string;
    customerInterfaceName: string;
}

// ../Nebula.Api.Admin/Models/Audit/Snapshots/BlueGreenConfigurationAuditSnapshotV1.cs
export interface BlueGreenConfigurationAuditSnapshotV1 extends IAuditEventData {
    type: string;
    isBlueGreenRunning: boolean;
    isImportLockDrainingActive: boolean;
}

// ../Nebula.Api.Admin/Models/Audit/Snapshots/CustomerInterfaceAuditSnapshotV1.cs
export interface CustomerInterfaceAuditSnapshotV1 extends IAuditEventData {
    type: string;
    id: string;
    name: string;
    notes: string;
    loggingConfig: CustomerInterfaceLoggingConfigAuditSnapshotV1;
    configurationOverrides: Record<string, string>;
    importConfig: CustomerInterfaceImportConfigurationAuditSnapshotV1;
}

// ../Nebula.Api.Admin/Models/Audit/Snapshots/MapLayerFileUploadedAuditSnapshotV1.cs
export interface MapLayerFileUploadedAuditSnapshotV1 extends IAuditEventData {
    type: string;
    layerName: string;
    fileName: string;
    result: string;
    details: string;
}

// ../Nebula.Api.Admin/Models/Audit/Snapshots/CustomerDeviceAuditMinSnapshotV1.cs
export interface CustomerDeviceAuditMinSnapshotV1 extends IAuditEventData {
    type: string;
    customerDeviceId: string;
    name: string;
}

// ../Nebula.Api.Admin/Models/Audit/Snapshots/DatabaseShardConfigurationAuditSnapshotV1.cs
export interface DatabaseShardConfigurationAuditSnapshotV1 extends IAuditEventData {
    type: string;
    defaultShardId: string;
}

// ../Nebula.Api.Admin/Models/Audit/Snapshots/CustomerAgencyDatabaseShardAuditSnapshotV1.cs
export interface CustomerAgencyDatabaseShardAuditSnapshotV1 extends IAuditEventData {
    type: string;
    customerAgencyId: string;
    shardId: string;
}

// ../Nebula.Api.Admin/Models/Audit/Snapshots/AuditChangeSetV1.cs
export interface AuditChangeSetV1 extends IAuditEventData {
    type: string;
    dataType: string;
    original: unknown;
    updated: unknown;
}

// ../Nebula.Api.Admin/Models/Audit/Snapshots/SupportFedAuthConfigurationAuditSnapshotV1.cs
export interface SupportFedAuthConfigurationAuditSnapshotV1 extends IAuditEventData {
    type: string;
    authorizeEndpoint: string;
    tokenEndpoint: string;
    userInfoEndpoint: string;
    logoutEndpoint: string;
    scope: string;
    tokenKeys: string;
    clientId: string;
    grantType: string;
    supportsPkce: boolean;
}

// ../Nebula.Api.Admin/Models/Audit/Snapshots/MapLayerAuditSnapshotV4.cs
export interface MapLayerAuditSnapshotV4 extends IAuditEventData {
    type: string;
    name: string;
    displayName: string;
    sharedLicensedAgencies: Record<string, string>;
    notes: string;
    fileUploaded: boolean;
    fileName: string;
    fileSize: string;
}

// ../Nebula.Api.Admin/Models/Audit/Snapshots/CustomerDeviceMediaRecoveryAttemptAuditSnapshotV1.cs
export interface CustomerDeviceMediaRecoveryAttemptAuditSnapshotV1 extends IAuditEventData {
    type: string;
    deviceId: string;
    deviceName: string;
    recoveryResult: string;
    mediaCounts: Record<string, number>;
}

// ../Nebula.Api.Admin/Models/Audit/Snapshots/NpgsqlConfigurationAuditSnapshotV1.cs
export interface NpgsqlConfigurationAuditSnapshotV1 extends IAuditEventData {
    type: string;
    eventListenerEnabled: boolean;
    eventListenerIntervalInSeconds: number;
    sqlDiagnosticLoggingEnabled: boolean;
    sqlParameterLoggingEnabled: boolean;
}

// ../Nebula.Api.Admin/Models/Audit/Snapshots/ApiTokenAuditSnapshotV1.cs
export interface ApiTokenAuditSnapshotV1 extends IAuditEventData {
    type: string;
    id: string;
    expiresOn: string;
    scopes: string[];
    userId: string;
    username: string;
}

// ../Nebula.Api.Admin/Models/Audit/Snapshots/CustomerAgencyAuditSnapshotV1.cs
export interface CustomerAgencyAuditSnapshotV1 extends IAuditEventData {
    type: string;
    id: string;
    name: string;
    notes: string;
    productLine: string;
    connectorType: string;
    databaseShardId?: number;
}

// ../Nebula.Api.Admin/Models/Audit/Snapshots/UserPageViewAuditSnapshotV1.cs
export interface UserPageViewAuditSnapshotV1 extends IAuditEventData {
    type: string;
    title: string;
    url: string;
    startedTimestamp: string;
    completedTimestamp?: string;
    duration?: any;
    durationDescription: string;
}

// ../Nebula.Api.Admin/Models/Audit/Snapshots/LocationSharingConfigurationAuditSnapshotV1.cs
export interface LocationSharingConfigurationAuditSnapshotV1 extends IAuditEventData {
    type: string;
    forceOverrideAllDeviceConfigurations: boolean;
    deviceLocationSharingIntervalInSeconds: number;
}

// ../Nebula.Api.Admin/Models/Audit/Snapshots/LicensedAgencyConfigurationAuditSnapshotV1.cs
export interface LicensedAgencyConfigurationAuditSnapshotV1 extends IAuditEventData {
    type: string;
    deviceSessionLockTimeoutInMinutes?: number;
    incidentNotificationSignificantUnitsAssigned?: number;
    incidentNotificationHighPriorities: Record<string, string>;
    incidentNotificationStatuses: Record<string, string>;
    emergencyResourceNotificationStatuses: Record<string, string>;
}

// ../Nebula.Api.Admin/Models/Audit/Snapshots/CustomerInterfaceLoggingConfigAuditSnapshotV1.cs
export interface CustomerInterfaceLoggingConfigAuditSnapshotV1 extends IAuditEventData {
    type: string;
    loggingEnabled: boolean;
    logLevel: string;
    awsEnabled: boolean;
}

// ../Nebula.Api.Admin/Models/Audit/Search/AuditSearchOption.cs
export interface AuditSearchOption {
    id: string;
    name: string;
}

// ../Nebula.Api.Admin/Models/Audit/Search/AuditSearchSortValue.cs
export interface AuditSearchSortValueExtensions {
}

// ../Nebula.Api.Admin/Models/Audit/Search/AuditSearchSortValue.cs
export enum AuditSearchSortValue {
    EventTimestamp,
    EventType,
    CustomerAgencyName,
    LicensedAgencyName,
    ByUsername,
}

// ../Nebula.Api.Admin/Models/Audit/Search/AuditSearchRequest.cs
export interface AuditSearchRequest {
    startDate: string;
    endDate: string;
    eventTypeDescription: string;
    customerAgencyId?: string;
    licensedAgencyId?: string;
    userId?: string;
    sortValue: AuditSearchSortValue;
    sortDirection: SortDirection;
    pageIndex: number;
    pageSize: number;
}

// ../Nebula.Api.Admin/Models/Audit/Search/AuditEventSearchResult.cs
export interface AuditEventSearchResult {
    eventId: number;
    eventTimestamp: string;
    eventType: string;
    eventTypeDescription: string;
    customerAgencyName: string;
    licensedAgencyName: string;
    byUsername: string;
}

// ../Nebula.Api.Admin/Models/Api/CustomerDataMigrationApiModels.cs
export interface CustomerDataMigrationModel extends CustomerDataMigrationViewModel {
}

// ../Nebula.Api.Admin/Models/Api/ApiCreateCertificateResponse.cs
export interface ApiCreateCertificateResponse {
    success: boolean;
    payload: string;
}

// ../Nebula.Api.Admin/Models/Api/BlueGreenConfigurationViewModel.cs
export interface BlueGreenConfigurationViewModel {
    isBlueGreenRunning: boolean;
    isImportLockDrainingActive: boolean;
}

// ../Nebula.Api.Admin/Models/Api/ApiCreateDeviceViewModel.cs
export interface ApiCreateDeviceViewModel {
    customerDeviceId: string;
    activationCode: string;
}

// ../Nebula.Api.Admin/Models/Api/AuthStatus.cs
export interface AuthStatus {
    isAuthenticated: boolean;
    isAssisting: boolean;
    assistingLicensedAgencyName: string;
    assistingCustomerAgencyName: string;
    assistingLicensedAgencyId?: string;
    assistingCustomerAgencyId?: string;
    user: WebAdminUser;
    loginSessionTimeoutInMinutes: number;
}

// ../Nebula.Api.Admin/Models/Api/ApiUpdateEmailAddressViewModel.cs
export interface ApiUpdateEmailAddressViewModel {
    emailAddress: string;
}

// ../Nebula.Api.Admin/Models/Api/CustomerInterfaceApiModels.cs
export interface CustomerInterfaceModel extends CustomerInterfaceViewModel {
    createdOn: string;
    downloadedOn?: string;
    configurationOverrides: Record<CustomerInterfaceConfigurationKey, string>;
    importConfiguration: ImportConfigurationViewModel;
}

// ../Nebula.Api.Admin/Models/Api/CustomerInterfaceApiModels.cs
export interface AddCustomerInterfaceResult extends ApiResult<CustomerInterfaceModel> {
}

// ../Nebula.Api.Admin/Models/Api/CustomerInterfaceApiModels.cs
export interface UpdateCustomerInterfaceResult extends ApiResult<CustomerInterfaceModel> {
}

// ../Nebula.Api.Admin/Models/Api/ApiEmailAddressOptionsViewModel.cs
export interface ApiEmailAddressOptionsViewModel {
    emailAddresses: string[];
}

// ../Nebula.Api.Admin/Models/Api/ApiLicensedAgenciesViewModel.cs
export interface ApiLicensedAgenciesRequestViewModel {
    documentId?: string;
    name: string;
    licenseCount: number;
    agencyIdentifier: string;
    advancedAuthentication?: AdvancedAuthenticationType;
    notes: string;
}

// ../Nebula.Api.Admin/Models/Api/ApiLicensedAgenciesViewModel.cs
export interface ApiLicensedAgenciesResponseViewModel {
    licensedAgencyId: string;
    customerAgencyId: string;
    documentId: string;
    name: string;
    licenseCount: number;
    agencyIdentifier: string;
    advancedAuthentication: AdvancedAuthenticationType;
    notes: string;
}

// ../Nebula.Api.Admin/Models/Api/LicensedUserApiModels.cs
/**
 * Used only for auto-provisioning - don't confuse with CustomerUser.
 */
export interface LicensedUserModel extends ApiLicensedUsersResponseViewModel {
}

// ../Nebula.Api.Admin/Models/Api/LicensedUserApiModels.cs
export interface AddLicensedUserResult extends ApiResult<LicensedUserModel> {
}

// ../Nebula.Api.Admin/Models/Api/LicensedUserApiModels.cs
export interface UpdateLicensedUserResult extends ApiResult<LicensedUserModel> {
}

// ../Nebula.Api.Admin/Models/Api/ApiLicensedUsersViewModel.cs
export interface ApiLicensedUsersRequestViewModel {
    userId?: string;
}

// ../Nebula.Api.Admin/Models/Api/ApiLicensedUsersViewModel.cs
export interface ApiLicensedUsersResponseViewModel {
    licensedUserId: string;
    name: string;
    fullName: string;
}

// ../Nebula.Api.Admin/Models/Api/AuthResult.cs
export interface AuthResult {
    isAuthenticated: boolean;
    requiresTwoFactorAuthentication: boolean;
    errorMessage: string;
}

// ../Nebula.Api.Admin/Models/Api/PermissionCheckApiModels.cs
export interface PermissionCheckResultsModel {
    type: string;
    id: string;
    targetField: string;
    canAccess: boolean;
    missingPermissions: Record<PermissionScenarios, PermissionRule>;
}

// ../Nebula.Api.Admin/Models/Api/PermissionCheckApiModels.cs
export interface PermissionCheckResponse extends ApiResult<PermissionCheckResultsModel> {
}

// ../Nebula.Api.Admin/Models/Api/PermissionCheckApiModels.cs
export interface PermissionCheckRequest {
    customerAgencyId: string;
    customerUserId: string;
    target: DocumentType;
    targetField: PermissionCheckTargetField;
    targetFieldValue: string;
}

// ../Nebula.Api.Admin/Models/Api/ApiCreateLicensedAgencyViewModel.cs
export interface ApiCreateLicensedAgencyViewModel {
    licensedAgencyId: string;
}

// ../Nebula.Api.Admin/Models/Api/SupportUserApiModels.cs
export interface SupportUserMutableModel {
    id?: string;
    username: string;
    permissions: SupportUserPermission[];
    lastLoggedInOn?: string;
    isDisabled?: boolean;
}

// ../Nebula.Api.Admin/Models/Api/SupportUserApiModels.cs
export interface SupportUserModel extends SupportUser {
}

// ../Nebula.Api.Admin/Models/Api/SupportUserApiModels.cs
export interface GetSupportUserResult extends ApiResult<SupportUserModel> {
}

// ../Nebula.Api.Admin/Models/Api/SupportUserApiModels.cs
export interface GetSupportUserListResult extends ApiResult<IEnumerable<SupportUserModel>> {
}

// ../Nebula.Api.Admin/Models/Api/SupportUserApiModels.cs
export interface UpdateSupportUserResult extends ApiResult<SupportUserModel> {
}

// ../Nebula.Api.Admin/Models/Api/ApiPermissionsConfigurationViewModel.cs
export interface ApiPermissionsConfigurationViewModel {
    permissionScopeMap: Record<SupportUserPermission, Scope[]>;
}

// ../Nebula.Api.Admin/Models/Api/ApiUsersViewModel.cs
export interface ApiUsersResponseViewModel {
    id: string;
    name: string;
    fullName: string;
    emailAddresses: EmailAddress[];
    phones: Phone[];
}

// ../Nebula.Api.Admin/Models/Api/ApiConnectorCertificateViewModel.cs
export interface ApiConnectorCertificateViewModel {
    url: object;
    expiration: string;
}

// ../Nebula.Api.Admin/Models/Api/ApiDeviceViewModel.cs
export interface ApiDevicesRequestViewModel {
    name: string;
    notes: string;
}

// ../Nebula.Api.Admin/Models/Api/ApiDeviceViewModel.cs
export interface ApiDevicesResponseViewModel {
    name: string;
    pin: string;
    pinExpiresOn?: string;
}

// ../Nebula.Api.Admin/Models/Api/CustomerAdminApiModels.cs
export interface CustomerAdminModel extends CustomerAdminViewModel {
}

// ../Nebula.Api.Admin/Models/Api/CustomerAdminApiModels.cs
export interface CustomerAdminManagementModel extends AgencySettingsViewModel {
}

// ../Nebula.Api.Admin/Models/Api/CustomerAdminApiModels.cs
export interface AddCustomerAdminResult extends ApiResult<CustomerUserModel> {
}

// ../Nebula.Api.Admin/Models/Api/CustomerAdminApiModels.cs
export interface UpdateCustomerAdminResult extends ApiResult<CustomerUserModel> {
}

// ../Nebula.Api.Admin/Models/Api/ApiCustomerViewModel.cs
export interface ApiCustomerRequestViewModel {
    productLine: CustomerProductLine;
    connectorType: ConnectorType;
    name: string;
    notes: string;
}

// ../Nebula.Api.Admin/Models/Api/ApiCustomerViewModel.cs
export interface ApiCustomerResponseViewModel {
    agencyId: string;
    productLine: CustomerProductLine;
    connectorType: ConnectorType;
    name: string;
    notes: string;
}

// ../Nebula.Api.Admin/Models/Api/DiagnosticConfigurationApiModels.cs
export interface DiagnosticConfigurationModel extends DiagnosticConfigurationViewModel {
}

// ../Nebula.Api.Admin/Models/Api/ShardApiModels.cs
export interface GetShardsResult extends ApiResult<IEnumerable<ShardMetadata>> {
}

// ../Nebula.Api.Admin/Models/Api/ShardApiModels.cs
export interface ShardMetadata {
    shardId: string;
    rdsInstanceId: string;
    totalAgencies: number;
    totalLicensedUsers: number;
    totalConnectedInterfaces: number;
    totalConnectedDevices: number;
    agencies: CustomerAgencyShardMetadata[];
}

// ../Nebula.Api.Admin/Models/Api/ShardApiModels.cs
export interface CustomerAgencyShardMetadata {
    customerAgencyId: string;
    name: string;
    uniqueId: string;
    productLine: CustomerProductLine;
    connectorType: ConnectorType;
    totalLicensedUsers: number;
    totalConnectedInterfaces: number;
    totalConnectedDevices: number;
}

// ../Nebula.Api.Admin/Models/Api/ShardApiModels.cs
export interface CustomerAgencyConnectionsMetadata {
    customerAgencyId: string;
    totalConnectedInterfaces: number;
    totalConnectedDevices: number;
}

// ../Nebula.Api.Admin/Models/Api/DatabaseConfigurationViewModel.cs
export interface DatabaseConfigurationViewModel {
    shards: string[];
}

// ../Nebula.Api.Admin/Models/Api/ApiConnectorStatusViewModel.cs
export interface ApiConnectorStatusResponseViewModel {
    customerAgencyId: string;
    customerInterfaceId: string;
    name: string;
    state: ConnectorStatus;
    comment: string;
    lastKnownGoodTimestamp?: string;
    productLine: CustomerProductLine;
    connectorType: ConnectorType;
    healthStatus: InterfaceHealthStatus;
}

// ../Nebula.Api.Admin/Models/Api/ApiConnectorStatusViewModel.cs
export enum ConnectorStatus {
    Unknown = 0,
    Ok = 1,
    Disconnected = 2,
    Error = 3,
    Warning = 4,
}

// ../Nebula.Api.Admin/Models/Api/ApiCreateUserViewModel.cs
export interface ApiCreateUserViewModel {
    licensedUserId: string;
    name: string;
}

// ../Nebula.Api.Admin/Models/Api/NewCustomerAgencyShardMappingViewModel.cs
export interface NewCustomerAgencyShardMappingViewModel {
    shardId: string;
    connectorTypeGroups: ShardConnectorTypeGroupViewModel[];
}

// ../Nebula.Api.Admin/Models/Api/NewCustomerAgencyShardMappingViewModel.cs
export interface ShardConnectorTypeGroupViewModel {
    connectorType: ConnectorType;
    productLines: CustomerProductLine[];
}

// ../Nebula.Api.Admin/Models/Api/ApiConnectorConfigurationViewModel.cs
export interface ApiConnectorConfigurationRequestViewModel {
    overrideValues: Record<CustomerInterfaceConfigurationKey, string>;
    overridesToRemove: CustomerInterfaceConfigurationKey[];
}

// ../Nebula.Api.Admin/Models/Api/ApiResult.cs
export interface EmptyApiResult {
    isSuccess: boolean;
    errorMessage: string;
    fieldValidationErrors: Record<string, string[]>;
}

// ../Nebula.Api.Admin/Models/Api/ApiResult.cs
export interface ApiResult<TData> extends EmptyApiResult {
    data: TData;
}

// ../Nebula.Api.Admin/Models/Api/ApiResult.cs
export interface ApiResult extends EmptyApiResult {
}

// ../Nebula.Api.Admin/Models/Api/ApiErrorDto.cs
export interface ApiErrorDto {
    message: string;
    stackInfo: string;
    url: string;
}

// ../Nebula.Api.Admin/Models/Api/ApiConnectorViewModel.cs
export interface ApiConnectorRequestViewModel {
    type?: InterfaceType;
    name: string;
    notes: string;
}

// ../Nebula.Api.Admin/Models/Api/ApiConnectorViewModel.cs
export interface ApiConnectorMainentanceInfoUpdateRequest {
    begin: string;
    durationInSeconds: number;
}

// ../Nebula.Api.Admin/Models/Api/ApiConnectorViewModel.cs
export interface ApiConnectorMaintenanceInfoResponse {
    begin: string;
    end: string;
    durationInSeconds: number;
    inProgress: boolean;
}

// ../Nebula.Api.Admin/Models/Api/ApiConnectorViewModel.cs
export interface ApiConnectorResponseViewModel {
    agencyId: string;
    connectorId: string;
    type: InterfaceType;
    name: string;
    notes: string;
}

// ../Nebula.Api.Admin/Models/Api/CustomerUserApiModels.cs
export interface CustomerUserModel extends CustomerUserViewModel {
}

// ../Nebula.Api.Admin/Models/Api/CustomerUserApiModels.cs
export interface CustomerUserSlimModel extends ApiLicensedUsersResponseViewModel {
}

// ../Nebula.Api.Admin/Models/Api/CustomerUserApiModels.cs
export interface CustomerUserManagementModel extends UserManagementViewModel {
}

// ../Nebula.Api.Admin/Models/Api/CustomerUserApiModels.cs
export interface AddCustomerUserResult extends ApiResult<CustomerUserModel> {
}

// ../Nebula.Api.Admin/Models/Api/CustomerUserApiModels.cs
export interface UpdateCustomerUserResult extends ApiResult<CustomerUserModel> {
}

// ../Nebula.Api.Admin/Models/Api/CustomerAgencyInterfaceHealthStatuses.cs
/**
 * Returned by /api/customerAgencies/health for the customer agency-wide health color
 */
export interface CustomerAgencyInterfaceHealthStatuses {
    [key: string]: InterfaceHealthStatus[];
}

// ../Nebula.Api.Admin/Models/Api/DataSearchApiModels.cs
export interface DataSearchModel extends DataSearchViewModel {
}

// ../Nebula.Api.Admin/Models/Api/DataSearchApiModels.cs
export interface DataSearchResult extends DataSearchResultsViewModel {
}

// ../Nebula.Api.Admin/Models/Api/CustomerAgencyApiModels.cs
export interface CustomerAgencyModel extends CustomerAgencyViewModel {
    mostRecentInterfaceCreatedOn?: string;
    hasOverriddenConnectorConfigurations: Boolean;
}

// ../Nebula.Api.Admin/Models/Api/CustomerAgencyApiModels.cs
export interface CustomerAgencyManagementModel extends CustomerManagementViewModel {
}

// ../Nebula.Api.Admin/Models/Api/CustomerAgencyApiModels.cs
export interface CustomerAgencyDetailModel extends CustomerDetailsViewModel {
}

// ../Nebula.Api.Admin/Models/Api/CustomerAgencyApiModels.cs
export interface AddCustomerAgencyResult extends ApiResult<CustomerAgencyModel> {
}

// ../Nebula.Api.Admin/Models/Api/CustomerAgencyApiModels.cs
export interface UpdateCustomerAgencyResult extends ApiResult<CustomerAgencyModel> {
}

// ../Nebula.Api.Admin/Models/Api/LicensedAgencyApiModels.cs
export interface LicensedAgencyModel extends LicensedAgencyViewModel {
}

// ../Nebula.Api.Admin/Models/Api/LicensedAgencyApiModels.cs
export interface AddLicensedAgencyResult extends ApiResult<LicensedAgencyModel> {
}

// ../Nebula.Api.Admin/Models/Api/LicensedAgencyApiModels.cs
export interface UpdateLicensedAgencyResult extends ApiResult<LicensedAgencyModel> {
}

// ../Nebula.Api.Admin/Models/Api/ApiAgenciesViewModel.cs
export interface ApiAgenciesResponseViewModel {
    id: string;
    name: string;
    ori: string;
    abbreviation: string;
}

// ../Nebula.Api.Admin/Models/Api/CustomerDeviceApiModels.cs
export interface CustomerDeviceManagementModel extends DeviceManagementViewModel {
}

// ../Nebula.Api.Admin/Models/Api/CustomerDeviceApiModels.cs
export interface CustomerDeviceModel extends CustomerDeviceViewModel {
}

// ../Nebula.Api.Admin/Models/Api/CustomerDeviceApiModels.cs
export interface DeleteCustomerDeviceResult extends ApiResult<System.Guid> {
}

// ../Nebula.Api.Admin/Models/Api/CustomerDeviceApiModels.cs
export interface AddCustomerDeviceResult extends ApiResult<CustomerDeviceModel> {
}

// ../Nebula.Api.Admin/Models/Api/CustomerDeviceApiModels.cs
export interface UpdateCustomerDeviceResult extends ApiResult<CustomerDeviceModel> {
}

// ../Nebula.Api.Admin/Models/Api/CustomerDeviceApiModels.cs
export interface ReplaceCustomerDeviceResult extends ApiResult<CustomerDeviceModel> {
}

// ../Nebula.Api.Admin/Models/Api/CustomerDeviceApiModels.cs
export interface GenerateCustomerDevicePinResult extends ApiResult<CustomerDeviceModel> {
}

// ../Nebula.Api.Admin/Models/Account/FederationViewModel.cs
export interface FederationViewModel {
    authProviderUrl: string;
    logoutUrl: string;
    useFederatedAuthentication: boolean;
    agencyId: string;
    agencyExternalId: string;
    codeVerifier: string;
    tokenKeys: string[];
}

// ../Nebula.Api.Admin/Models/Account/VerifyCodeViewModel.cs
export interface VerifyCodeViewModel {
    provider: string;
    code: string;
    returnUrl: string;
    rememberBrowser: boolean;
    rememberMe: boolean;
}

// ../Nebula.Api.Admin/Models/Account/FedLoginViewModel.cs
export interface FedLoginViewModel {
    agencyName: string;
    originatingUrl: string;
}

// ../Nebula.Api.Admin/Models/Account/SendFedCodeViewModel.cs
export interface SendFedCodeViewModel {
    agencyExternalId: string;
    originatingUrl: string;
    savedState: string;
    fedKeyValues: Record<string, string>;
    codeVerifier: string;
}

// ../Nebula.Api.Admin/Models/Account/ApiTokenConfigurationViewModel.cs
export interface ApiTokenConfigurationViewModel {
    defaultScopes: Scope[];
    defaultExpirationDays: number;
    maxExpirationDays: number;
}

// ../Nebula.Api.Admin/Models/Account/SendCodeViewModel.cs
export interface SendCodeViewModel {
    selectedProvider: string;
    providers: any[];
    returnUrl: string;
    rememberMe: boolean;
}

// ../Nebula.Api.Admin/Models/Account/LoginViewModel.cs
export interface LoginViewModel {
    agencyId: string;
    domainName: string;
    username: string;
    password: string;
    rememberMe: boolean;
}

// ../Nebula.Api.Admin/Models/Account/CreateApiTokenViewModel.cs
export interface CreateApiTokenViewModel {
    expiresOn: string;
}

// ../Nebula.Api.Admin/Models/Account/ApiTokenViewModel.cs
export interface ApiTokenViewModel {
    apiTokenId: string;
    token: string;
    expiresOn: string;
    supportUserId?: string;
    scopes: Scope[];
}

// ../Nebula.Api.Admin/Models/Account/UpgradeApiTokenViewModel.cs
export interface UpgradeApiTokenViewModel {
    scopes: Scope[];
}

// ../Nebula.Api.Admin/Models/Reports/InvalidCustomerUserInfo.cs
export interface InvalidCustomerUserInfo {
    customerAgencyId: string;
    productLine: CustomerProductLine;
    connectorType: ConnectorType;
    customerName: string;
    agencyName: string;
    agencyId: string;
    licensedAgencyId: string;
    licensedAgencyDocumentId: string;
    userId: string;
    name: string;
    isLicensed: boolean;
    isAdmin: boolean;
    shardId: number;
}

// ../Nebula.Api.Admin/Models/Reports/DeviceInfo.cs
export interface DeviceInfo {
    productLine: CustomerProductLine;
    connectorType: ConnectorType;
    customerName: string;
    agencyName: string;
    agencyId: string;
    deviceName: string;
    deviceManufacturer: string;
    deviceModel: string;
    deviceOs: string;
    foVersion: string;
    lastLogin?: string;
}

// ../Nebula.Api.Admin/Models/Reports/CustomerContactInfo.cs
export interface CustomerContactInfo {
    contactName: string;
    email: string;
    cadEmail: string;
    customerName: string;
    productLine: CustomerProductLine;
    connectorType: ConnectorType;
}

// ../Nebula.Api.Admin/Models/Reports/LicenseUsageInfo.cs
export interface LicenseUsageInfo {
    productLine: CustomerProductLine;
    connectorType: ConnectorType;
    customerAgencyId: string;
    customerName: string;
    agencyId: string;
    agencyName: string;
    agencyIdentifier: string;
    totalLicensesAvailable: number;
    totalLicensesUsed: number;
    totalDevicesActivated: number;
}

// ../Nebula.Api.Admin/Models/Reports/MediaUploadInfo.cs
export interface MediaUploadInfo {
    productLine: CustomerProductLine;
    connectorType: ConnectorType;
    customerName: string;
    agency: string;
    user: string;
    deviceName: string;
    deviceId: string;
    mediaType: MediaType;
    mediaCount: number;
    mediaSizeBytes: number;
    mediaSizeMb: number;
    mediaSizeGb: number;
    mediaUploadDate: string;
    isCustomerAgencyActive: boolean;
    isLicensedAgencyActive: boolean;
    isUserLicensed: boolean;
    isDeviceActive: boolean;
}

// ../Nebula.Api.Admin/Models/Reports/MediaUploadReportOptionsViewModel.cs
export interface MediaUploadReportOptionsViewModel {
    beginDate?: string;
    endDate?: string;
}

// ../Nebula.Api.Admin/MapLayers/Types.cs
export interface MapLayer {
    id: string;
    ownerAgency: string;
    name: string;
    displayName: string;
    fileName: string;
    notes: string;
    uploadedOn?: string;
    sharedAgencies: string[];
}

// ../Nebula.Api.Admin/MapLayers/Types.cs
export interface GetMapLayersResult {
    mapLayers: MapLayer[];
}

// ../Nebula.Api.Admin/MapLayers/Types.cs
export interface MapLayerFile {
    /**
     * File name with extension
     */
    name: string;
    /**
     * Base-64 encoded file
     */
    content: string;
}

// ../Nebula.Api.Admin/MapLayers/Types.cs
export interface AddMapLayerRequest {
    name: string;
    displayName: string;
    notes: string;
    sharedAgencies: string[];
    file: MapLayerFile;
}

// ../Nebula.Api.Admin/MapLayers/Types.cs
export interface AddMapLayerResult {
    mapLayer: MapLayer;
}

// ../Nebula.Api.Admin/MapLayers/Types.cs
export interface EditMapLayerRequest {
    name: string;
    displayName: string;
    notes: string;
    sharedAgencies: string[];
    file: MapLayerFile;
}

// ../Nebula.Api.Admin/MapLayers/Types.cs
export interface EditMapLayerResult {
    mapLayer: MapLayer;
}

// ../Nebula.Client.Lib/Communication/ISourcedPayload.cs
export interface ISourcedPayload {
    payloadSource: FieldOpsSources;
}

// ../Nebula.Client.Lib/Communication/FieldOpsSources.cs
export enum FieldOpsSources {
    User = 0,
    Client = 1,
    Nebula = 2,
    Connector = 3,
}

// ../Nebula.Lib/Patching/DocumentPatch.cs
/**
 * A document patch using for patching documents from one version to another.
 */
export interface DocumentPatch {
    /**
     * The document patch strategy.
     */
    strategy: DocumentPatchStrategy;
    /**
     * The document patch
     */
    patch: any;
    sourceHash: string;
    resultHash: string;
}

// ../Nebula.Lib/Patching/DocumentPatch.cs
export enum DocumentPatchStrategy {
    /**
     * Basic strategy that relies on converting objects to JObjects and leveraging
     * the AspNetCore JsonPatchDocument implementation for patching.
     */
    DotNetCoreJsonPatchV1 = 0,
}

// ../NebulaAPI.Data/Models/PagingOptions.cs
export interface PagingOptions {
    pageIndex: number;
    pageSize: number;
}

// ../NebulaAPI.Data/Models/MessageDocumentDto.cs
export interface MessageDocumentDto extends DocumentDto {
    isActive: boolean;
    lastRead?: string;
}

// ../NebulaAPI.Data/Models/DocumentDto.cs
export interface DocumentDto {
    id: string;
    externalId: string;
    customerAgencyId: string;
    customerId: string;
    interfaceId: string;
    modifiedBy: string;
    createdBy: string;
    modifiedDate: string;
    createdDate: string;
    jsonDocument: string;
}

// ../NebulaAPI.Data/Models/GrainStateDto.cs
export interface GrainStateDto {
    id: string;
    state: string;
}

// ../NebulaAPI.Data/Models/SortDirection.cs
export enum SortDirection {
    Asc,
    Desc,
}

// ../NebulaAPI.Data/Models/PagedResult.cs
export interface PagedResult<T> {
    results: T[];
    pageIndex: number;
    pageSize: number;
    totalPages: number;
    totalResults: number;
}

// ../NebulaAPI.Data/Models/SortOptions.cs
export interface SortOptions<T> {
    value: T;
    direction: SortDirection;
}

// ../NebulaAPI.Data/Models/Audit/AuditEventType.cs
export interface AuditEventType {
    eventTypeId: string;
    eventTypeDescription: string;
    isSupport: boolean;
}

// ../NebulaAPI.Data/Models/Audit/AuditEvent.cs
export interface AuditEvent {
    eventId: number;
    eventTimestamp: string;
    eventType: string;
    eventTypeDescription: string;
    resourceId: string;
    resourceName: string;
    customerAgencyId?: string;
    customerAgencyName: string;
    licensedAgencyId?: string;
    licensedAgencyName: string;
    bySupportUserId?: string;
    byCustomerUserId?: string;
    byUsername: string;
    byUserFullname: string;
}

// ../NebulaAPI.Data/Models/Audit/AuditEvent.cs
export interface IAuditEventData {
    type: string;
}

// ../NebulaAPI.Domain/Models/DocumentSubscriptionAction.cs
export enum DocumentSubscriptionAction {
    None = 0,
    Add = 1,
    Update = 2,
    Remove = 3,
    Link = 4,
}

// ../NebulaAPI.Domain/Models/MediaProcessRequest.cs
export interface MediaProcessRequest {
    mediaId: string;
    customerInterfaceId?: string;
    linkedMediaId?: string;
    externalAgencyId: string;
    documentType: DocumentType;
    externalDocumentId: string;
    externalUserId: string;
    deviceId?: string;
    deviceName: String;
    description: String;
    comments: String;
    encryptionKey: Int8Array;
    md5Checksum: Int8Array;
    mediaType: MediaType;
    fileExtension: string;
    hasFile: boolean;
    timeEntered: string;
}

// ../NebulaAPI.Domain/Models/UnitLoginOption.cs
export interface UnitLoginOption {
    id: string;
    unitIdentification: string;
}

// ../NebulaAPI.Domain/Models/CertificateChallenge.cs
export interface ServerCertificateChallenge {
    publicKey: Int8Array;
    challenge: string;
}

// ../NebulaAPI.Domain/Models/CertificateChallenge.cs
export interface ServerCertificateChallengeResponse extends ServerCertificateChallenge {
    confirmedChallenge: string;
}

// ../NebulaAPI.Domain/Models/CertificateChallenge.cs
export interface ClientCertificateChallengeResponse {
    confirmedChallenge: string;
}

// ../NebulaAPI.Domain/Models/HistoricalEmergencyResource.cs
export interface HistoricalEmergencyResource {
    emergencyResourceId: string;
    emergencyResourceExternalId: string;
    unitIdentification: string;
    type: string;
    disciplineType: DisciplineType;
    personnel: HistoricalPersonnel[];
}

// ../NebulaAPI.Domain/Models/InterfaceMetadata.cs
export interface InterfaceMetadataCatalog {
    metadata: InterfaceMetadata[];
}

// ../NebulaAPI.Domain/Models/InterfaceMetadata.cs
export interface InterfaceMetadata {
    customerInterfaceId: string;
    enabledFeatures: InterfaceFeature[];
    properties: Record<InterfaceProperty, string>;
}

// ../NebulaAPI.Domain/Models/InterfaceMetadata.cs
export interface InterfaceMetadataExtensions {
}

// ../NebulaAPI.Domain/Models/StyleField.cs
export enum StyleField {
    BackgroundColor,
    ForegroundColor,
}

// ../NebulaAPI.Domain/Models/DeAuthenticationRequest.cs
/**
 * This is the request object that requests a user to be deauthenticated.
 */
export interface DeAuthenticationRequest {
    userExternalId: string;
    deviceId: string;
    userAssignmentRequest: UserAssignmentRequest;
}

// ../NebulaAPI.Domain/Models/AuthenticationTokenType.cs
export enum AuthenticationTokenType {
    /**
     * Used by old clients
     */
    JWT = 0,
    /**
     * Used by new device clients specifying the {@link DeviceFeature.SupportsReferenceAuthenticationTokens}
     * supported feature.
     */
    Reference = 1,
}

// ../NebulaAPI.Domain/Models/UserAssignmentResponse.cs
export interface UserAssignmentResponse extends SourcedPayload {
    assignmentResult: boolean;
    message: string;
    userExternalId: string;
    emergencyResourceExternalId: string;
    userId: string;
    emergencyResourceId?: string;
}

// ../NebulaAPI.Domain/Models/CustomerInterface.cs
export interface CustomerInterface {
    customerInterfaceId: string;
    interfaceType: InterfaceType;
    customerAgencyId: string;
    name: string;
    notes: string;
    customerCertificateId: string;
}

// ../NebulaAPI.Domain/Models/CustomerInterface.cs
export interface CustomerInterfaceMaintenanceInfo {
    begin: string;
    end: string;
    duration: any;
    inProgress: boolean;
}

// ../NebulaAPI.Domain/Models/CustomerInterface.cs
export enum InterfaceType {
    ProSuite = 0,
    Enterprise = 1,
    System = 2,
}

// ../NebulaAPI.Domain/Models/DataPair.cs
export interface DataPair {
    label: string;
    value: string;
}

// ../NebulaAPI.Domain/Models/CustomerAgency.cs
export interface CustomerAgency {
    customerAgencyId: string;
    name: string;
    productLine: CustomerProductLine;
    connectorType: ConnectorType;
    notes: string;
    supportCreatedOn?: string;
    supportUpdatedOn?: string;
    createdBySupportUserId?: string;
    updatedBySupportUserId?: string;
}

// ../NebulaAPI.Domain/Models/CloudMediaUploadKey.cs
/**
 * Response for {@link CloudMediaUploadRequest} containing
 * the corresponding CloudMedia ID and a valid Uri to upload the
 * media file to.
 */
export interface CloudMediaUploadKey {
    /**
     * An ID for the created CloudMedia upload.
     */
    cloudMediaId: string;
    /**
     * The cloud storage provider where the CloudMedia will be uploaded to.
     */
    storageProvider: CloudProvider;
    /**
     * A Uri the media can be uploaded to.
     */
    uploadUri: object;
    /**
     * The expiration date for the {@link UploadUri}.
     */
    expiresOn: string;
}

// ../NebulaAPI.Domain/Models/DeviceActivationResponse.cs
export interface DeviceActivationResponse extends SourcedPayload {
    activationReponseStatus: ActivationResponseStatus;
    deviceCertificate: CustomerCertificate;
    privateKeyDownloadUrl: string;
    downloadUrlExpiresOn?: string;
}

// ../NebulaAPI.Domain/Models/DeviceActivationResponse.cs
export enum ActivationResponseStatus {
    Successful,
    ExpiredActivationPin,
    InvalidActivationPin,
    UnexpectedException,
}

// ../NebulaAPI.Domain/Models/FileType.cs
export enum FileType {
    Image = 0,
    Video = 1,
    Audio = 2,
    Document = 3,
    MapLayer = 4,
}

// ../NebulaAPI.Domain/Models/DeviceMediaUploadStatus.cs
export enum DeviceMediaUploadStatus {
    Canceled = -2,
    Failed = -1,
    Unknown = 0,
    Uploading = 1,
    Uploaded = 2,
}

// ../NebulaAPI.Domain/Models/DisciplineType.cs
/**
 * Identifies the type discipline
 */
export enum DisciplineType {
    Unknown = 0,
    Law = 1,
    Fire = 2,
    Ems = 3,
}

// ../NebulaAPI.Domain/Models/AuthenticationResponse.cs
export interface AuthenticationResponse extends SourcedPayload {
    authenticationResult: AuthenticationResultType;
    claims: Record<string, string[]>;
    message: string;
    userExternalId: string;
    user: User;
    accessToken: string;
    deviceId: string;
    authenticationProviderType: AuthenticationProviderType;
    authenticatedCustomerInterfaceIds: string[];
}

// ../NebulaAPI.Domain/Models/MediaSyncRequest.cs
export interface MediaSyncRequest {
    customerAgencyId: string;
    deviceId: string;
    mediaUploadStatus: Record<string, DeviceMediaUploadStatus>;
}

// ../NebulaAPI.Domain/Models/QueryableTargetField.cs
export enum QueryableTargetField {
    DocumentId,
    DocumentExternalId,
    IncidentNumber,
    IncidentCode,
    UnitIdentification,
}

// ../NebulaAPI.Domain/Models/Vehicle.cs
export interface Vehicle {
    externalId: string;
    recordExternalId: string;
    year: string;
    make: string;
    model: string;
    licensePlate: string;
    plateState: string;
    color: string;
    involvement: string;
    vin: string;
    registeredOwner: NameRecord;
    additionalData: AdditionalData[];
}

// ../NebulaAPI.Domain/Models/LocationHistory.cs
export interface LocationHistory {
    occurredAt: string;
    type: string;
    sourceDescription: string;
    additionalData: AdditionalData[];
    permissions: Record<PermissionScenarios, PermissionRule>;
}

// ../NebulaAPI.Domain/Models/CustomerDataMigration.cs
export interface CustomerDataMigration {
    id: string;
    customerAgencyId: string;
    status: CustomerDataMigrationStatus;
    createdOn: string;
    updatedOn: string;
    documentMappingUpdatedOn?: string;
}

// ../NebulaAPI.Domain/Models/CustomerDataMigration.cs
export interface CustomerDataMigrationResult {
    errors: string[];
    log: string[];
}

// ../NebulaAPI.Domain/Models/CustomerDataMigration.cs
export interface CustomerDataMigrationMapping {
    users: CustomerDataMigrationDocumentMapping[];
    agencies: CustomerDataMigrationDocumentMapping[];
}

// ../NebulaAPI.Domain/Models/CustomerDataMigration.cs
export interface CustomerDataMigrationDocumentMapping {
    licensedId: string;
    documentId: string;
    oldExternalId: string;
    newExternalId: string;
}

// ../NebulaAPI.Domain/Models/CustomerDataMigration.cs
export interface CustomerDataMigrationMappingResult {
    errors: string[];
}

// ../NebulaAPI.Domain/Models/CustomerDataMigration.cs
export enum CustomerDataMigrationStatus {
    Created = 1,
    Running = 2,
    Faulted = 3,
    Complete = 4,
}

// ../NebulaAPI.Domain/Models/InterfaceState.cs
export enum InterfaceState {
    Disconnected = 0,
    Connected = 1,
}

// ../NebulaAPI.Domain/Models/EmergencyResourceType.cs
export interface EmergencyResourceType extends BaseDocumentModel, INebulaAddressableEntity<Guid> {
    ownerAgencyId?: string;
    ownerAgencyExternalId: string;
    description: string;
    sortOrder: number;
}

// ../NebulaAPI.Domain/Models/Narrative.cs
export interface Narrative {
    timeEntered: string;
    userId?: string;
    userExternalId: string;
    editedBy: string;
    message: string;
}

// ../NebulaAPI.Domain/Models/PushApplicationPlatform.cs
export interface PushApplicationPlatform {
    platformType: AmazonSnsPushPlatformTypes;
    applicationArn: string;
}

// ../NebulaAPI.Domain/Models/PermissionScenarios.cs
export enum PermissionScenarios {
    Read = 0,
    AddNarrativeToCfs = 1,
    InvolvementHistoryOpen = 2,
    InvolvementHistoryOpenDetails = 3,
    ExecuteQuery = 4,
    AddMediaToIncident = 5,
}

// ../NebulaAPI.Domain/Models/SupportUserPermission.cs
export enum SupportUserPermission {
    ViewDiagnosticConfiguration = 0,
    ManageDiagnosticConfiguration = 1,
    ViewSupportUsers = 2,
    ManageSupportUsers = 3,
    ManageApiToken = 4,
    ViewApiToken = 5,
    ManageInterfaceConfiguration = 6,
    ViewInterfaceConfiguration = 7,
    ViewDatabaseConfiguration = 8,
    ManageDatabaseConfiguration = 9,
    ExecuteGrainApi = 10,
    ViewDocuments = 11,
    ViewShards = 12,
    ViewBlueGreenConfiguration = 13,
    ManageBlueGreenConfiguration = 14,
    ManageAllApiTokens = 15,
    RenewCertificate = 16,
}

// ../NebulaAPI.Domain/Models/CloudMediaRecovery.cs
/**
 * Used to track elements of the cloud media recovery process
 */
export interface CloudMediaRecovery {
    /**
     * Whether or not cloud media recovery has completed
     */
    recoveryComplete: boolean;
    /**
     * The last time a recovery was completed
     */
    lastRecoveryCompleteDateTime?: string;
    /**
     * A collection of CloudMediaIds and their associated MediaId, along with the upload status
     */
    mediaUploadStatuses: Record<string, CloudMediaRecoveryUploadStatus>;
    /**
     * A collection of CloudMediaIds and their associated MediaId that failed to recover. It 
     * is possible that the failure happened before a MediaId was even created.
     */
    failedItems: Record<string, string>;
}

// ../NebulaAPI.Domain/Models/Personnel.cs
export interface Personnel {
    name: string;
    userId?: string;
    externalUserId: string;
}

// ../NebulaAPI.Domain/Models/ClientNotificationType.cs
export enum ClientNotificationType {
    CentralSquare = 4,
    CentralSquareDev = 5,
}

// ../NebulaAPI.Domain/Models/CloudMediaEncryptionKeys.cs
/**
 * Contains the encryption keys necessary for interface and device decryption.
 */
export interface CloudMediaEncryptionKeys {
    /**
     * Map of Customer Interface IDs to Interface encryption keys.
     */
    interfaceKeys: Record<string, Int8Array>;
    /**
     * An optional encryption key from the device (should be encrypted using the
     * devices certificate).
     */
    deviceKey: Int8Array;
}

// ../NebulaAPI.Domain/Models/CloudMedia.cs
export interface CloudMedia {
    /**
     * The Cloud Media ID.
     */
    id: string;
    /**
     * The source User's Agency ID. Populated by Nebula.
     */
    agencyId: string;
    /**
     * The source User's Customer Agency ID. Populated by Nebula.
     */
    customerAgencyId: string;
    /**
     * The source's type. Populated by Nebula.
     */
    sourceType: CloudMediaSourceType;
    /**
     * The source's ID. Populated by Nebula.
     */
    sourceId: string;
    /**
     * Metadata for the source. Populated by Nebula.
     */
    sourceMetadata: CloudMediaSourceMetadata;
    /**
     * The source User's ID. Populated by Nebula.
     */
    userId?: string;
    /**
     * The type of media to be uploaded.
     */
    mediaType: MediaType;
    /**
     * The creation date for the CloudMedia row.
     */
    createdOn: string;
    /**
     * The last updated date for the CloudMedia row.
     */
    updatedOn: string;
    /**
     * When the media file was uploaded to the cloud storage provider.
     */
    uploadedOn?: string;
    /**
     * When the media file was deleted from the cloud storage provider or
     * when the CloudMedia row was deleted. In any event, this should
     * correspond to the media file no longer being accessible.
     */
    deletedOn?: string;
    /**
     * Whether the media file is encrypted.
     */
    isEncrypted: boolean;
    /**
     * An encrypted symmetric key that can be used to decrypt the media
     * file. The key needs to first be decrypted using, presumably, the
     * agency's private key.
     */
    encryptionKeys: CloudMediaEncryptionKeys;
    /**
     * The MD5 checksum of the encrypted file.
     */
    md5Checksum: Int8Array;
    /**
     * The media's original file name.
     */
    fileName: string;
    /**
     * The media's file extension.
     */
    fileExtension: string;
    /**
     * The media's file size.
     */
    fileSize: number;
    /**
     * The media file's original creation date.
     */
    fileCreatedOn: string;
    /**
     * User controlled JSON blob for general media data.
     * Specifically JObject as we don't want to allow
     * JValue/JContainer/JArray's.
     */
    mediaData: unknown;
    /**
     * Nebula controlled JSON blob for general cloud storage data.
     * Specifically JObject as we don't want to allow
     * JValue/JContainer/JArray's.
     */
    storageData: unknown;
    /**
     * Media file metadata.
     */
    metadata: CloudMediaMetadata;
    /**
     * Indicates where the source is at in uploading the file to cloud storage.
     */
    uploadStatus: DeviceMediaUploadStatus;
    /**
     * Indicates the upload progress level, as an integer between 0 to 100, of source's upload
     * to cloud storage.
     */
    uploadProgress: number;
}

// ../NebulaAPI.Domain/Models/CloudMediaMetadata.cs
/**
 * Specify CloudMedia metadata.
 */
export interface CloudMediaMetadata {
    /**
     * A user entered description of the cloud media
     */
    description: string;
    /**
     * User entered comments of the cloud media
     */
    comments: string;
}

// ../NebulaAPI.Domain/Models/LinkType.cs
export enum LinkType {
    Record = 0,
    Incident = 1,
    External = 2,
}

// ../NebulaAPI.Domain/Models/MediaUploadRequest.cs
export interface NoFileMediaRequest {
    id?: string;
    agencyId: string;
    customerAgencyId: string;
    customerInterfaceId?: string;
    documentType: DocumentType;
    documentId: string;
    userId?: string;
    deviceName: string;
    mediaType: MediaType;
    createdOn: string;
    updatedOn: string;
    uploadedOn?: string;
    description: string;
    comments: string;
    mediaData: string;
    deviceId?: string;
    /**
     * Used to "replace" a previous request with a new one.
     */
    previousMediaId?: string;
}

// ../NebulaAPI.Domain/Models/MediaUploadRequest.cs
export interface MediaUploadRequest extends NoFileMediaRequest {
    encryptionKey: Int8Array;
    md5Checksum: Int8Array;
    fileExtension: string;
    linkedMediaId?: string;
}

// ../NebulaAPI.Domain/Models/CloudStorageKey.cs
/**
 * Abstraction around cloud storage provider file systems (ie. S3).
 */
export interface CloudStorageKey {
    /**
     * The cloud provider
     */
    provider: CloudProvider;
    /**
     * The cloud storage bucket
     */
    bucket: string;
    /**
     * The cloud storage key
     */
    key: string;
}

// ../NebulaAPI.Domain/Models/AuthenticationProviderType.cs
export enum AuthenticationProviderType {
    Interface = 1,
    ActiveDirectory = 2,
}

// ../NebulaAPI.Domain/Models/CertificateDownloadKey.cs
export interface CertificateDownloadKey {
    agencyId: string;
    interfaceId: string;
    downloadUri: object;
    expiresOn: string;
}

// ../NebulaAPI.Domain/Models/SecureConnectionResponse.cs
export interface SecureConnectionResponse {
    success: boolean;
    challenge: Int8Array;
}

// ../NebulaAPI.Domain/Models/Link.cs
export interface Link {
    externalId: string;
    refId: string;
    type: LinkType;
    displayedType: string;
    description: string;
    customerAgencyId: string;
    customerId: string;
    interfaceId: string;
    createDateTime: string;
    location: string;
    assignment: string;
    numberDescription: string;
    incidentNumber: string;
    ownerAgencyId: string;
    ownerAgencyExternalId: string;
    permissions: Record<PermissionScenarios, PermissionRule>;
}

// ../NebulaAPI.Domain/Models/CloudMediaKey.cs
/**
 * Defines the segments and their locations for {@link CloudMediaKey}.
 */
export interface CloudMediaKeyPathSegment {
}

// ../NebulaAPI.Domain/Models/CloudMediaKey.cs
/**
 * Defines the structure for a cloud storage bucket Key for Cloud Media.
 */
export interface CloudMediaKey {
    customerAgencyId: string;
    agencyId: string;
    sourceType: CloudMediaSourceType;
    sourceId: string;
    mediaType: MediaType;
    cloudMediaId: string;
}

// ../NebulaAPI.Domain/Models/CloudMediaDownloadKey.cs
/**
 * Response for a CloudMedia download request.
 */
export interface CloudMediaDownloadKey {
    /**
     * The CloudMedia row ID being requested to be downloaded.
     */
    cloudMediaId: string;
    /**
     * The cloud storage provider where the media exists.
     */
    storageProvider: CloudProvider;
    /**
     * A valid download Uri for the media.
     */
    downloadUri: object;
    /**
     * When the download Uri expires.
     */
    expiresOn: string;
}

// ../NebulaAPI.Domain/Models/Phone.cs
export interface Phone {
    number: string;
    type: string;
}

// ../NebulaAPI.Domain/Models/InterfacePublicKeyChangedResponse.cs
export interface InterfacePublicKeyChangedResponse {
    customerAgencyId: string;
    customerInterfaceId: string;
    publicKey: Int8Array;
}

// ../NebulaAPI.Domain/Models/FileDownloadRequest.cs
export interface FileDownloadRequest {
    fileDocumentId: string;
    preferThumbnail: boolean;
}

// ../NebulaAPI.Domain/Models/UserAuditActionDetails.cs
export interface UserActionAuditDetails {
}

// ../NebulaAPI.Domain/Models/TimePeriod.cs
export interface TimePeriod {
    start?: string;
    end?: string;
}

// ../NebulaAPI.Domain/Models/AlertSourceType.cs
export enum AlertSourceType {
    Phone = 0,
    Name = 1,
    Vehicle = 2,
    Location = 3,
}

// ../NebulaAPI.Domain/Models/InterfaceHealthConstants.cs
export interface InterfaceHealthConstants {
}

// ../NebulaAPI.Domain/Models/Media.cs
export interface Media {
    id: string;
    agencyId: string;
    customerAgencyId: string;
    customerId: string;
    documentType: DocumentType;
    documentId: string;
    userId?: string;
    deviceName: string;
    mediaType: MediaType;
    createdOn: string;
    updatedOn: string;
    uploadedOn?: string;
    transmittedRequestOn?: string;
    interfaceReceivedOn?: string;
    interfaceProcessedOn?: string;
    processingResult?: boolean;
    processingError: string;
    encryptionKey: Int8Array;
    md5Checksum: Int8Array;
    fileExtension: string;
    description: string;
    comments: string;
    mediaData: string;
    storageData: string;
    attemptedCount: number;
    deviceId?: string;
    /**
     * Indicates where Exeo is at in uploading the file to cloud storage. Not guaranteed
     * to be set by legacy Exeo devices.
     */
    deviceMediaUploadStatus: DeviceMediaUploadStatus;
    /**
     * Indicates the upload progress level, as an integer between 0 to 100, of Exeo's upload
     * to cloud storage. Not guaranteed to be set by legacy Exeo devices.
     */
    deviceMediaUploadProgress?: number;
    customerInterfaceId?: string;
    linkedMediaId?: string;
}

// ../NebulaAPI.Domain/Models/HistoricalPersonnel.cs
export interface HistoricalPersonnel {
    userId: string;
    externalUserId: string;
    name: string;
    fullName: string;
}

// ../NebulaAPI.Domain/Models/GeoCoordinate.cs
export interface GeoCoordinate {
    latitude: number;
    longitude: number;
    validationType: GeoCoordinateValidationType;
}

// ../NebulaAPI.Domain/Models/CloudMediaUploadRequest.cs
export interface CloudMediaUploadRequest {
    /**
     * If not null, this request is treated like requesting a new
     * {@link CloudMediaUploadKey} and will update
     * {@link MediaData"/> and <see cref="Metadata}.
     */
    id?: string;
    agencyId: string;
    customerAgencyId: string;
    sourceType: CloudMediaSourceType;
    sourceId: string;
    sourceMetadata: CloudMediaSourceMetadata;
    userId?: string;
    /**
     * The type of media being uploaded.
     */
    mediaType: MediaType;
    /**
     * Source controlled JSON bag of data to associate with the upload.
     */
    mediaData: unknown;
    /**
     * Whether the media file is encrypted or not.
     */
    isEncrypted: boolean;
    /**
     * An set of encrypted keys used to decrypt the media file.
     */
    encryptionKeys: CloudMediaEncryptionKeys;
    /**
     * An MD5 checksum for the encrypted file.
     */
    md5Checksum: Int8Array;
    /**
     * The source media file's original name.
     */
    fileName: string;
    /**
     * The source media file's extension.
     */
    fileExtension: string;
    /**
     * The source media file's size before encryption.
     */
    fileSize: number;
    /**
     * The source media file's creation data.
     */
    fileCreatedOn: string;
    /**
     * Metadata associated with the media.
     */
    metadata: CloudMediaMetadata;
}

// ../NebulaAPI.Domain/Models/InterfaceHealthStatus.cs
export interface InterfaceHealthStatus {
    occurredOn: string;
    healthColor: InterfaceHealthColor;
    message: string;
}

// ../NebulaAPI.Domain/Models/CloudMediaRecoveryUploadStatus.cs
/**
 * The status of the media upload that started as cloud media
 *
 * @remarks
 * Cloud media is media that is backed up automatically from the device. In order
 * to recover cloud media, we first create a media record from the cloud media record
 * and the send that media record to the connector.
 */
export interface CloudMediaRecoveryUploadStatus {
    mediaId: string;
    status: DeviceMediaUploadStatus;
}

// ../NebulaAPI.Domain/Models/CloudMediaStatusRequest.cs
/**
 * A request for CloudMedia upload statuses.
 */
export interface CloudMediaStatusRequest {
    /**
     * The source User's Agency ID. Provided by Nebula.
     */
    agencyId: string;
    /**
     * The source User's Customer Agency ID. Provided by Nebula.
     */
    customerAgencyId: string;
    /**
     * The source User's ID. Provided by Nebula.
     */
    userId: string;
    /**
     * The source type. Provided by Nebula.
     */
    sourceType: CloudMediaSourceType;
    /**
     * The source ID. Provided by Nebula.
     */
    sourceId: string;
    /**
     * A dictionary of CloudMedia IDs and their corresponding upload status in Nebula.
     */
    cloudMediaIds: string[];
}

// ../NebulaAPI.Domain/Models/SupportUser.cs
export interface SupportUser {
    id: string;
    username: string;
    permissions: SupportUserPermission[];
    lastLoggedInOn: string;
    isDisabled: boolean;
}

// ../NebulaAPI.Domain/Models/CustomerDevice.cs
export interface CustomerDevice {
    customerDeviceId: string;
    name: string;
    assignedUserId?: string;
    customerAgencyId: string;
    licensedAgencyId: string;
    activeCustomerUserId?: string;
    activeUserAccessToken: string;
    pin: string;
    pinExpiresOn?: string;
    pinUsed: boolean;
    notes: string;
    email: string;
    createdOn: string;
    updatedOn: string;
    lastAuthenticatedWithUserAt?: string;
    properties: Record<DeviceProperty, string>;
    /**
     * Superseded by {@link PushNotificationToken}.
     */
    pushEndpointTopicArn: string;
    /**
     * Superseded by {@link RegisteredForPushNotificationsV2}.
     */
    registeredForPushNotifications: boolean;
    pushNotificationToken: string;
    registeredForPushNotificationsV2: boolean;
}

// ../NebulaAPI.Domain/Models/EmailAddress.cs
export interface EmailAddress {
    email: string;
    type: string;
}

// ../NebulaAPI.Domain/Models/BulkFileDownloadKeysResponse.cs
export interface BulkFileDownloadKeysResponse {
    downloadKeys: MediaDownloadKey[];
    failedFileDocumentIds: string[];
}

// ../NebulaAPI.Domain/Models/MediaDownloadKey.cs
export interface MediaDownloadKey {
    mediaId: string;
    downloadUri: object;
    expiresOn: string;
}

// ../NebulaAPI.Domain/Models/MediaSyncResponse.cs
export interface MediaSyncResponse {
    /**
     * A list of media IDs Nebula does not have media requests
     * for. These should be very rare and indicate a situation
     * where Nebula was likely purged of data.
     */
    unknownMediaIds: string[];
    /**
     * A list of media IDs Nebula associated with the Customer
     * Agency ID and the device ID that wasn't included in the
     * sync request. These IDs would have been treated as having
     * been canceled.
     */
    missingMediaIds: string[];
    /**
     * A list of {@link MediaStatusResponse} objects for each
     * valid and tracked media object.
     */
    statuses: Record<string, MediaStatusResponse>;
}

// ../NebulaAPI.Domain/Models/NameRecord.cs
export interface NameRecord {
    externalId: string;
    recordExternalId: string;
    name: string;
    type: string;
    sex: string;
    dateOfBirth: string;
    age: string;
    height: string;
    weight: string;
    hairColor: string;
    eyeColor: string;
    involvement: string;
    phone: string;
    location: string;
    driversLicense: string;
    additionalData: AdditionalData[];
}

// ../NebulaAPI.Domain/Models/CloudMediaStatusElement.cs
export interface CloudMediaStatusElement {
    id: string;
    sourceType: CloudMediaSourceType;
    sourceId: string;
    status: DeviceMediaUploadStatus;
}

// ../NebulaAPI.Domain/Models/DocumentPatchErrorContext.cs
export enum DocumentPatchErrorContext {
    DscSource,
    Patch,
    Result,
}

// ../NebulaAPI.Domain/Models/LicensedAgency.cs
export interface LicensedAgency {
    licensedAgencyId: string;
    customerAgencyId: string;
    documentId: string;
    name: string;
    licenseCount: number;
    uniqueIdentifier: string;
    advancedAuthType: AdvancedAuthenticationType;
    notes: string;
    /**
     * @deprecated Use DocumentId to lookup the External ID instead.
     */
    externalId: string;
}

// ../NebulaAPI.Domain/Models/LicensedAgency.cs
export enum AdvancedAuthenticationType {
    AdvancedAuthentication,
    CompensatingControls,
}

// ../NebulaAPI.Domain/Models/LicensedAgency.cs
export enum AgencyIdentityType {
    Identity,
    EfficiencyCloud,
}

// ../NebulaAPI.Domain/Models/DocumentType.cs
export interface DocumentTypeExtensions {
}

// ../NebulaAPI.Domain/Models/DocumentType.cs
export enum DocumentTypeAttributes {
    TableName,
    RuntimeType,
    ExeoEndpoint,
    ConnectorRuntimeType,
}

// ../NebulaAPI.Domain/Models/DocumentType.cs
export enum DocumentType {
    Incident = 0,
    EmergencyResource = 1,
    Jurisdiction1 = 2,
    Jurisdiction2 = 3,
    Jurisdiction3 = 4,
    Position = 5,
    Agency = 6,
    User = 7,
    FilterConfiguration = 8,
    Status = 9,
    Priority = 10,
    EmergencyResourceType = 11,
    Case = 12,
    Command = 13,
    Transition = 14,
    GlobalConfiguration = 15,
    Message = 16,
    QuerySpecification = 17,
    InterconnectedCadConfiguration = 18,
    File = 19,
    SignatureCollectionRequest = 20,
    MapLayer = 21,
}

// ../NebulaAPI.Domain/Models/IsActivatable.cs
export interface IActivatableDocument {
    isActive: boolean;
}

// ../NebulaAPI.Domain/Models/Customer.cs
export interface Customer {
    customerId: string;
    name: string;
    notes: string;
    productLine: CustomerProductLine;
    connectorType: ConnectorType;
    createdOn: string;
    updatedOn: string;
    customerAgencies: CustomerAgency[];
}

// ../NebulaAPI.Domain/Models/Customer.cs
export enum CustomerProductLine {
    None = 0,
    Pro = 1,
    Enterprise = 2,
    TotalCommand = 3,
    LETG = 4,
    IMC = 5,
}

// ../NebulaAPI.Domain/Models/Customer.cs
export enum ConnectorType {
    None = 0,
    Internal = 1,
    Production = 2,
    Training = 3,
}

// ../NebulaAPI.Domain/Models/NebulaClientType.cs
export enum NebulaClientType {
    Interface = 0,
    Exeo = 1,
}

// ../NebulaAPI.Domain/Models/ADUser.cs
export interface ADUser {
    displayName: string;
    username: string;
    isSupport: boolean;
    isAdmin: boolean;
}

// ../NebulaAPI.Domain/Models/FederatedAuthenticationProviderRequest.cs
export interface FederatedAuthenticationProviderRequest {
    customerAgencyId: string;
    deviceId: string;
    deviceName: string;
    redirectUri: string;
    state: string;
}

// ../NebulaAPI.Domain/Models/ResponseTime.cs
export interface ResponseTime {
    description: string;
    timestamp: string;
}

// ../NebulaAPI.Domain/Models/CloudMediaSourceType.cs
/**
 * Enumeration for CloudMedia upload sources.
 */
export enum CloudMediaSourceType {
    /**
     * An Exeo device.
     */
    ExeoDevice = 0,
}

// ../NebulaAPI.Domain/Models/Call.cs
export interface Call {
    callDateTime: string;
    originatingPhoneNumber: string;
    location: Location;
}

// ../NebulaAPI.Domain/Models/DocumentSubscriptionChange.cs
export interface DocumentSubscriptionChange<T> {
    document: T;
    action: DocumentSubscriptionAction;
}

// ../NebulaAPI.Domain/Models/FileUploadStatus.cs
export enum FileUploadStatus {
    Pending = 0,
    Failed = 1,
    Uploaded = 2,
}

// ../NebulaAPI.Domain/Models/EmergencyResourceLocation.cs
/**
 * Superseded by {@link Position}.
 */
export interface EmergencyResourceLocation {
    description: string;
    coordinate: GeoCoordinate;
}

// ../NebulaAPI.Domain/Models/CloudMediaUpdateRequest.cs
/**
 * A request to update a CloudMedia upload. This can be used to update
 * media data associated with the CloudMedia as well as CloudMedia
 * metadata.
 */
export interface CloudMediaUpdateRequest {
    /**
     * The source User's Agency ID. Populated by Nebula.
     */
    agencyId: string;
    /**
     * The source User's Customer Agency ID. Populated by Nebula.
     */
    customerAgencyId: string;
    /**
     * The CloudMedia ID to update
     */
    cloudMediaId: string;
    /**
     * The source User's ID. Populated by Nebula.
     */
    userId: string;
    /**
     * The source's type. Populated by Nebula.
     */
    sourceType: CloudMediaSourceType;
    /**
     * The source's ID. Populated by Nebula.
     */
    sourceId: string;
    /**
     * Metadata associated with the source. Populated by Nebula.
     */
    sourceMetadata: CloudMediaSourceMetadata;
    /**
     * The updated MediaData
     */
    mediaData: unknown;
    /**
     * The updated CloudMedia metadata.
     */
    metadata: CloudMediaMetadata;
}

// ../NebulaAPI.Domain/Models/CloudMediaStatusResponse.cs
/**
 * Response for a {@link CloudMediaStatusRequest} containing the
 * statuses for all requested CloudMedia uploads.
 */
export interface CloudMediaStatusResponse extends SourcedPayload {
    /**
     * A dictionary of CloudMedia IDs to upload statuses.
     */
    statuses: Record<string, CloudMediaStatusElement>;
}

// ../NebulaAPI.Domain/Models/CustomerCertificate.cs
export interface CustomerCertificate {
    customerCertificateId: string;
    certificateType: CertificateType;
    fingerprint: Int8Array;
    publicKey: Int8Array;
    issuedOn: string;
    expiresOn: string;
    downloadedOn?: string;
    principal?: string;
    revoked: boolean;
    replacementCustomerCertificateId?: string;
    pendingReplacement: boolean;
}

// ../NebulaAPI.Domain/Models/CustomerCertificate.cs
export enum CertificateType {
    Interface,
    Device,
    IdentityServer,
}

// ../NebulaAPI.Domain/Models/CertificateRevocationStatus.cs
export enum CertificateRevocationStatus {
    Ok,
    Revoked,
}

// ../NebulaAPI.Domain/Models/Scope.cs
export interface InherentScopes {
}

// ../NebulaAPI.Domain/Models/Scope.cs
export enum Scope {
    Unknown = 0,
    ListCustomers = 1,
    ReadCustomer = 2,
    WriteCustomer = 3,
    RemoveCustomer = 4,
    ListConnectors = 5,
    ReadConnector = 6,
    WriteConnector = 7,
    RemoveConnector = 8,
    RenewConnectorCertificate = 9,
    ReadConnectorConfiguration = 10,
    WriteConnectorConfiguration = 11,
    ListLicensedAgencies = 12,
    ReadLicensedAgency = 13,
    WriteLicensedAgency = 14,
    RemoveLicensedAgency = 15,
    ListCustomerUsers = 16,
    ReadCustomerUser = 17,
    WriteCustomerUser = 18,
    RemoveCustomerUser = 19,
    ListCustomerDevices = 20,
    ReadCustomerDevice = 21,
    WriteCustomerDevice = 22,
    RemoveCustomerDevice = 23,
    ListCustomerAdmins = 24,
    ReadCustomerAdmin = 25,
    WriteCustomerAdmin = 26,
    RemoveCustomerAdmin = 27,
    ListApiTokens = 28,
    ReadApiToken = 29,
    WriteApiToken = 30,
    RemoveApiToken = 31,
    ManageAllApiTokens = 32,
    ReadStackHealth = 33,
    ReadBlueGreenStatus = 34,
    WriteBlueGreenStatus = 35,
    ReadDiagnosticConfiguration = 36,
    WriteDiagnosticConfiguration = 37,
    ReadDatabaseConfiguration = 38,
    WriteDatabaseConfiguration = 39,
    ExecuteGrainApi = 40,
    QueryDocuments = 41,
    ReadSupportUsers = 42,
    WriteSupportUsers = 43,
    RemoveSupportUsers = 44,
    ReadReport = 45,
    ReadDocumentPermissions = 46,
    DataSearch = 47,
    SearchAuditEvents = 48,
    RecoverCustomerDeviceMedia = 49,
}

// ../NebulaAPI.Domain/Models/DeviceMediaRecoveryStartResult.cs
export interface DeviceMediaRecoveryStartResult {
}

// ../NebulaAPI.Domain/Models/DatabaseMigration.cs
export interface DatabaseMigration {
    migration: string;
    appliedAt: string;
}

// ../NebulaAPI.Domain/Models/AuthenticationResultType.cs
export enum AuthenticationResultType {
    Success = 1,
    InvalidLoginAttempt = -1,
    AccountDisabled = -2,
    AccountLocked = -3,
    PasswordExpired = -4,
    MultiFactorRequired = -5,
    UnknownUser = -6,
}

// ../NebulaAPI.Domain/Models/DeviceActivationRenewalResult.cs
export interface DeviceActivationRenewalResult {
}

// ../NebulaAPI.Domain/Models/FederatedAuthenticationProviderResponse.cs
export interface FederatedAuthenticationProviderResponse extends SourcedPayload {
    /**
     * True if an error occurred while retrieving identity provider info
     */
    isError: boolean;
    /**
     * Error message
     */
    message: string;
    /**
     * Endpoint users with authenticate against
     */
    endpoint: string;
    /**
     * Single logout endpoint for users to end session on IdP (optional)
     */
    logoutEndpoint: string;
    /**
     * The parameter keys for the response (i.e. code, access_token, id_token, etc.)
     * included in the redirect URI.  These will be parsed and sent back as part of
     * the authentication request.
     * Typically the authorization flow will use "code" and implicit flow
     * would use "access_token".
     */
    tokenKeys: string[];
    /**
     * Customer interface that responded
     */
    customerInterfaceId?: string;
    /**
     * Represents the code challenge prior to being SHA256 hashed. This is Base64 encoded.
     * Only valid for authorization code flow with PKCE.
     * The SHA256 base64 encoded version is part of the endpoint.
     */
    codeVerifier: string;
    /**
     * Determines whether the identity provider supports Authorization Code flow with PKCE.
     * If true, auth requests will be made with "code_challenge" and "code_challenge_method" of S256
     * when requesting a code.  The code will then we sent with a "code_verifier" to get a token.
     */
    supportsPkce: boolean;
}

// ../NebulaAPI.Domain/Models/FederatedAuthenticationProviderResponse.cs
export interface FederatedAuthenticationProviderResponseExtensions {
}

// ../NebulaAPI.Domain/Models/InterfaceAuthenticationResponse.cs
export interface InterfaceAuthenticationResponse extends SourcedPayload {
    customerInterfaceId: string;
    authenticationResult: AuthenticationResultType;
    user: User;
    userExternalId: string;
    message: string;
}

// ../NebulaAPI.Domain/Models/DeviceLocation.cs
export interface DeviceLocation {
    timestamp: string;
    latitude: number;
    longitude: number;
    altitude: number;
    accuracy: number;
    altitudeAccuracy?: number;
    heading: number;
    speed: number;
}

// ../NebulaAPI.Domain/Models/Alert.cs
export interface Alert {
    sourceType: AlertSourceType;
    sourceDescription: string;
    description: string;
    alertType: string;
    additionalData: AdditionalData[];
    permissions: Record<PermissionScenarios, PermissionRule>;
}

// ../NebulaAPI.Domain/Models/MediaStatusResponse.cs
export interface MediaStatusResponse extends SourcedPayload {
    mediaId?: string;
    status: MediaStatus;
    transmittedToInterface: boolean;
    failureReason: String;
}

// ../NebulaAPI.Domain/Models/MediaStatusResponse.cs
export enum MediaStatus {
    Unknown = -2,
    Failed = -1,
    Pending = 0,
    Processed = 1,
}

// ../NebulaAPI.Domain/Models/HistoricalAssignment.cs
export interface HistoricalAssignment {
    emergencyResource: HistoricalEmergencyResource;
    assignedOn: string;
}

// ../NebulaAPI.Domain/Models/ClosedIncidentStatusesSet.cs
export interface ClosedIncidentStatusesSet extends HashSet<Guid> {
}

// ../NebulaAPI.Domain/Models/UserCapabilities.cs
export enum UserCapabilities {
    OptionalUserAssignment = 1,
    RequiredUserAssignment = 2,
    AllowMedia = 3,
    AllowMessages = 4,
    AllowDispatch = 5,
    /**
     * @deprecated Use AllowNcicQuery or AllowRecordQuery instead.
     */
    AllowQuery = 6,
    AllowNcicQuery = 7,
    AllowRecordQuery = 8,
    AllowIncidentCreation = 9,
    AllowIncidentMedia = 10,
    AllowSignatureCollection = 11,
}

// ../NebulaAPI.Domain/Models/GeoCoordinateValidationType.cs
export enum GeoCoordinateValidationType {
    None = 0,
    Agency = 1,
    Google = 2,
}

// ../NebulaAPI.Domain/Models/AuthenticationRequest.cs
export interface AuthenticationRequest {
    clientName: string;
    clientSecret: string;
    scope: string;
    username: string;
    password: string;
    domainName: string;
    customerAgencyId?: string;
}

// ../NebulaAPI.Domain/Models/MediaUploadLinkResponse.cs
export interface MediaUploadLinkResponse {
    mediaId: string;
}

// ../NebulaAPI.Domain/Models/PeriodicDocumentBaselineConfiguration.cs
export interface PeriodicDocumentBaselineConfiguration {
    refreshPeriodInMilliseconds: number;
}

// ../NebulaAPI.Domain/Models/MediaUploadFromCloudMediaRequest.cs
/**
 * Request to generate a new Media upload from a CloudMedia upload.
 */
export interface MediaUploadFromCloudMediaRequest {
    /**
     * The CloudMedia ID to create the Media upload from.
     */
    cloudMediaId: string;
    /**
     * The Customer Interface ID the Media upload is for.
     */
    customerInterfaceId?: string;
    /**
     * The DocumentType the Media should be associated with.
     */
    documentType: DocumentType;
    /**
     * The Document ID the Media should be associated with.
     */
    documentId: string;
}

// ../NebulaAPI.Domain/Models/FileDocumentKey.cs
export interface FileDocumentKey {
    customerAgencyId: string;
    fileId: string;
    fileType: FileType;
}

// ../NebulaAPI.Domain/Models/CloudUploadKey.cs
export interface CloudUploadKey {
    uploadUri: string;
    expiresOn: string;
}

// ../NebulaAPI.Domain/Models/PublicKeyResponse.cs
export interface PublicKeyResponse {
    nebula: Int8Array;
    agency: Int8Array;
    interfacePublicKeys: InterfacePublicKey[];
    supportedProtocols: SecureConnectionProtocol[];
    supportedConnectionProtocolFeatures: ConnectionProtocolFeature[];
}

// ../NebulaAPI.Domain/Models/CloudStorageSubscriptionStatus.cs
export interface CloudStorageSubscriptionStatus {
    resultType: CloudStorageSubscriptionResultTypes;
    message: string;
    subscriptionArn: string;
}

// ../NebulaAPI.Domain/Models/CloudStorageSubscriptionStatus.cs
export enum CloudStorageSubscriptionResultTypes {
    NotSubscribed,
    Subscribed,
    PendingConfirmation,
    FailedSubscription,
    FailedConfirmation,
}

// ../NebulaAPI.Domain/Models/CustomerInterfaceConnectionStatus.cs
export interface CustomerInterfaceConnectionStatus {
    customerInterfaceId: string;
    connectionStatus: ConnectionStatus;
}

// ../NebulaAPI.Domain/Models/InterfacePublicKey.cs
export interface InterfacePublicKey {
    customerInterfaceId: string;
    customerAgencyId: string;
    key: Int8Array;
    supportedProtocols: SecureConnectionProtocol[];
}

// ../NebulaAPI.Domain/Models/Location.cs
export interface Location {
    locationAddressExternalId: string;
    commonPlaceName: string;
    freeFormAddressText: string;
    city: string;
    state: string;
    crossStreets: string;
    nearestIntersection: string;
    notes: string;
    coordinate: GeoCoordinate;
    additionalData: AdditionalData[];
    parseError: boolean;
}

// ../NebulaAPI.Domain/Models/CloudProvider.cs
/**
 * An enumeration of valid cloud providers
 */
export enum CloudProvider {
    Unknown = 0,
    AWS = 1,
}

// ../NebulaAPI.Domain/Models/ConnectionStatus.cs
export enum ConnectionStatus {
    None = 0,
    Disconnected = 1,
    Connected = 2,
}

// ../NebulaAPI.Domain/Models/AuthenticationConfiguration.cs
export interface AuthenticationConfiguration {
    allowLdapAuthentication: boolean;
    requireLdapAuthentication: boolean;
    externalAgencyIdToLdapDomains: Record<string, string[]>;
    agencyIdToLdapDomains: Record<string, string[]>;
    requireFederatedAuthentication: boolean;
}

// ../NebulaAPI.Domain/Models/InterfaceHealthColor.cs
export enum InterfaceHealthColor {
    Black,
    Green,
    Yellow,
    Orange,
    Red,
}

// ../NebulaAPI.Domain/Models/RegisterAgencyResponse.cs
export interface RegisterAgencyResponse {
    customerAgency: CustomerAgency;
    customerInterfaceId: string;
    configurations: Record<CustomerInterfaceConfigurationKey, string>;
    /**
     * A delay in seconds the connector should delay before initiating the import run loop.
     */
    requestedDelayInMilliseconds?: number;
}

// ../NebulaAPI.Domain/Models/UserAssignmentRequest.cs
export interface UserAssignmentRequest {
    userExternalId: string;
    deviceId: string;
    emergencyResourceExternalId: string;
    customerAgencyId: string;
}

// ../NebulaAPI.Domain/Models/CustomerUser.cs
export interface CustomerUser {
    customerUserId: string;
    userId: string;
    customerAgencyId: string;
    licensedAgencyId: string;
    phone: string;
    email: string;
    isAdmin: boolean;
    isLicensed: boolean;
    authorizedLicensedAgencies: string[];
    securityStamp: string;
    createdOn: string;
    updatedOn: string;
}

// ../NebulaAPI.Domain/Models/CloudUploadMultiPartKeyValidation.cs
/**
 * Combines the two different objects needed to complete a multipart upload request into one DTO.
 */
export interface CloudUploadMultiPartKeyValidation {
    /**
     * The cloud upload key that owns the entire multipart upload.
     */
    uploadKey: CloudUploadMultiPartKey;
    /**
     * The collection of part numbers -> etag so that we can verify the various parts that were uploaded are correct.
     */
    partEtags: Record<number, string>;
}

// ../NebulaAPI.Domain/Models/FederatedAuthenticationRequest.cs
export interface FederatedAuthenticationRequest {
    requestId: string;
    customerAgencyId: string;
    agencyExternalId: string;
    deviceId: string;
    deviceName: string;
    /**
     * Represents the response tokens based on parsing keys
     * from {@link FederatedAuthenticationProviderResponse} 
     * For example for authorization code flow it would contain
     * "code":"1234-124-fake-code"
     * In the case of implicit flow this could represent an access token
     * "access_token":"1232asdf123faketoken"
     */
    tokens: Record<string, string>;
    /**
     * Represents the code challenge prior to being SHA256 hashed. This is Base64 encoded.
     * Only valid for authorization code flow with PKCE.
     * The SHA256 base64 encoded version is part of the endpoint.
     */
    codeVerifier: string;
    /**
     * According to RFC 6749, 4.1.3. the redirect_uri parameter is required if it was included in the
     * authorization request. Since the authorization server requires the redirect URL during the
     * authorization request it must be sent also during the token request.
     * This adds an extra layer of security as the authorization server will enforce that they match.
     * Only valid for authorization code flow.
     */
    redirectUri: string;
}

// ../NebulaAPI.Domain/Models/FederatedAuthenticationRequest.cs
export interface FederatedAuthenticationRequestExtensions {
}

// ../NebulaAPI.Domain/Models/PartnerAgencyRelationship.cs
/**
 * An agency to agency relationship connecting two agencies together for
 * the purposes of sharing data.
 */
export interface PartnerAgencyRelationship {
    id: string;
    createdOn: string;
    updatedOn: string;
    deletedOn?: string;
    sourceCustomerAgencyId: string;
    sourceAgencyId: string;
    recipientCustomerAgencyId: string;
    recipientAgencyId: string;
}

// ../NebulaAPI.Domain/Models/AdditionalData.cs
export interface AdditionalData {
    header: string;
    data: DataPair[];
}

// ../NebulaAPI.Domain/Models/CloudMediaSourceMetadata.cs
/**
 * Container for source metadata.
 */
export interface CloudMediaSourceMetadata {
    /**
     * Human readable name of the source. Eg. "Exeo - Something".
     */
    name: string;
    /**
     * Bag of source properties originating from
     * {@link ClientRegistration.Properties}.
     */
    properties: Record<string, string>;
}

// ../NebulaAPI.Domain/Models/MediaType.cs
export enum MediaType {
    Image = 0,
    Video = 1,
    Audio = 2,
    Note = 3,
    Narrative = 4,
    AppDataBackup = 5,
    Signature = 6,
}

// ../NebulaAPI.Domain/Models/DeAuthenticatedFromAgencyResponse.cs
/**
 * This is the response object that an agency uses to indicate that a user has been deauthenticated.
 */
export interface DeAuthenticatedFromAgencyResponse {
    userExternalId: string;
}

// ../NebulaAPI.Domain/Models/InterfaceHealthContributor.cs
export enum InterfaceHealthContributor {
    Operation = 0,
    Connectivity = 1,
    DocumentImports = 2,
}

// ../NebulaAPI.Domain/Models/ProcessDeviceLocationRequest.cs
export interface ProcessDeviceLocationRequest {
    lastLoginTimestamp: string;
    timestamp: string;
    latitude: number;
    longitude: number;
    altitude: number;
    accuracy: number;
    altitudeAccuracy?: number;
    heading: number;
    speed: number;
    userExternalId: string;
    emergencyResourceExternalId: string;
    deviceId: string;
}

// ../NebulaAPI.Domain/Models/PermissionCheckTargetField.cs
export enum PermissionCheckTargetField {
    DocumentId,
    DocumentExternalId,
    IncidentNumber,
    IncidentCode,
    UnitIdentification,
}

// ../NebulaAPI.Domain/Models/MediaProcessingConfiguration.cs
export interface MediaProcessingConfiguration {
    /**
     * The amount of time, in seconds, that can elapse until Nebula treats
     * a request for processing to the connector as having timed out. This
     * timeout will result in up to {@link MaxRetryCount} failures
     * until the media is deemed as having completely failed.
     *
     * @remarks
     * Defaults to 60 seconds
     */
    mediaAckTimeoutInSeconds: number;
    /**
     * The amount of time, in seconds, that a media upload can take until
     * Nebula treats the upload as having failed. A single failure for upload
     * results in the entire media as being marked as failing. This won't
     * disrupt any other media uploads for the associated document ID.
     *
     * @remarks
     * Defaults to 1 hour
     */
    mediaUploadTimeoutInSeconds: number;
    /**
     * The amount of time, in seconds, that Nebula will wait for a recently
     * uploaded media to transmit an SNS creation notification. This timeout
     * is special in that it only applies to Exeo devices that transmit upload
     * status during upload. It is specifically to allow for fail-fast short
     * circuiting during issues with SNS that don't block all other uploads
     * for {@link MediaUploadTimeoutInSeconds}; which is generally a longer
     * delay to account for large or long upload times.
     *
     * @remarks
     * Defaults to 5 minutes
     */
    mediaSnsCreationEventTimeoutInSeconds: number;
    /**
     * The number of times a media item is attempted to be transmitted to a connector
     * for processing. Events that count towards this retry limit are: transmission
     * issues to the connector, connector processing issues, and processing timeouts.
     * Uploading issues are fast-fail and will not be retried.
     *
     * @remarks
     * Defaults to 5 attempts
     */
    maxRetryCount: number;
}

// ../NebulaAPI.Domain/Models/MediaUploadKey.cs
/**
 * Response to a {@link MediaUploadRequest} with a valid upload
 * Uri and an expiration date for the Uri.
 */
export interface MediaUploadKey {
    /**
     * The associated Media row ID.
     */
    mediaId: string;
    /**
     * The cloud storage provider where the media will be uploaded to.
     */
    cloudStorageProvider: CloudProvider;
    /**
     * A valid upload Uri the media can be uploaded to.
     */
    uploadUri: object;
    /**
     * The expiration date for the upload Uri.
     */
    expiresOn: string;
}

// ../NebulaAPI.Domain/Models/NcicConfiguration.cs
export interface NcicConfiguration {
    /**
     * True if requires user to re-authenticate prior to making NCIC request.
     * If used in coordination with <seealso cref="AuthenticationDurationInSeconds"/>
     * then the user will only be required to re-authenticate if they haven't done so
     * within the defined duration.
     */
    requireAuthentication: boolean;
    /**
     * Duration in seconds before requiring user to re-authenticate prior to making NCIC request.
     */
    authenticationDurationInSeconds?: number;
}

// ../NebulaAPI.Domain/Models/MediaKey.cs
export interface MediaKeyPathSegment {
}

// ../NebulaAPI.Domain/Models/MediaKey.cs
export interface MediaKey {
    mediaType: MediaType;
    customerAgencyId: string;
    agencyId: string;
    documentType: DocumentType;
    documentId: string;
    mediaId: string;
}

// ../NebulaAPI.Domain/Models/PermissionRule.cs
export interface PermissionRule {
    all: string[];
    or: string[];
}

// ../NebulaAPI.Domain/Models/CloudUploadMultiPartKey.cs
export interface CloudUploadMultiPartKey {
    resourceId: string;
    contentType: string;
    expiresOn: string;
    uploadId: string;
}

// ../NebulaAPI.Domain/Models/NotificationType.cs
export enum NotificationType {
    RealTime = 0,
    High = 1,
    Low = 2,
}

// ../NebulaAPI.Domain/Models/FileDocumentConfiguration.cs
export interface FileDocumentConfiguration {
    /**
     * The default desired width for thumbnails.  The final width might be smaller since we maintain aspect ratio.
     *
     * @remarks
     * Defaults to 500 pixels
     */
    defaultDesiredThumbnailWidth: number;
    /**
     * The default desired height for thumbnails.  The final height might be smaller since we maintain aspect ratio.
     *
     * @remarks
     * Defaults to 500 pixels
     */
    defaultDesiredThumbnailHeight: number;
    /**
     * How frequently the reminder grain fires to cleanup S3 objects
     */
    cleanupIntervalMinutes: number;
    /**
     * Determines the max amount of threads to use when cleaning up S3 objects
     */
    cleanupMaxDegreesOfParallelism: number;
}

// ../NebulaAPI.Domain/Models/Statusing/CommandFlow.cs
export interface CommandFlow {
    id: string;
    externalId: string;
    commandIds: string[];
    commandExternalIds: string[];
    transitionIds: string[];
    transitionExternalIds: string[];
    statusExternalIdToDefaultCommandExternalId: Record<string, string>;
    statusIdToDefaultCommandId: Record<string, string>;
    isActive: boolean;
}

// ../NebulaAPI.Domain/Models/Statusing/FieldEntryType.cs
export enum FieldEntryType {
    /**
     * Text field style widget
     */
    Input = 0,
    /**
     * Some widget allowing the selection of a single item
     */
    Select = 1,
    /**
     * Some widget allowing free-form text entry or the
     * selection of items filtered by the entered text
     */
    FreeFormSelect = 2,
    /**
     * Widget requiring entry of a date and time
     */
    Date = 3,
    /**
     * Widget requiring entry of a date without the time component
     */
    DateWithoutTime = 4,
    /**
     * Widget that allows the client to send back a geocoordinate with the client's location
     * or allows the user to freeform enter an address
     */
    Location = 5,
}

// ../NebulaAPI.Domain/Models/Statusing/LocationValue.cs
/**
 * Defines a data type that can be either a geocoordinate or a free form address.
 */
export interface LocationValue {
    valueType: LocationValueType;
    geoCoordinateValue: GeoCoordinate;
    freeFormLocationValue: string;
}

// ../NebulaAPI.Domain/Models/Statusing/TransitionFieldResponse.cs
export interface TransitionFieldResponse extends SourcedPayload {
    transitionFieldIdentifier: string;
    transitionFieldName: string;
    dataType: FieldDataType;
    value: FieldValue;
}

// ../NebulaAPI.Domain/Models/Statusing/LocationValueType.cs
export enum LocationValueType {
    None = 0,
    FreeFormLocation = 1,
    Geocoordinate = 2,
}

// ../NebulaAPI.Domain/Models/Statusing/FieldDataType.cs
export enum FieldDataType {
    String = 0,
    Double = 1,
    Integer = 2,
    /**
     * Date field, JSON formatted with the settings:
     * DateParseHandling = DateParseHandling.DateTimeOffset
     * DateFormatHandling = DateFormatHandling.IsoDateFormat
     * DateTimeZoneHandling = DateTimeZoneHandling.Utc = 3
     */
    Date = 3,
    /**
     * Date field without a corresponding time component
     * and formatted according to ISO 8601, i.e. 2020-01-02
     */
    DateWithoutTime = 4,
    /**
     * Allows the client to send back a geocoordinate with the client's location
     * or allows the user to freeform enter an address
     */
    Location = 5,
}

// ../NebulaAPI.Domain/Models/Statusing/ExecuteCommandResponse.cs
export interface ExecuteCommandResponse extends SourcedPayload {
    responseType: ExecuteCommandResponseType;
    failureMessage: string;
    missingTransitionFields: TransitionField[];
    invalidTransitionFieldResponses: TransitionFieldResponse[];
    originalTransitionFieldResponses: TransitionFieldResponse[];
}

// ../NebulaAPI.Domain/Models/Statusing/ExecuteCommandResponseType.cs
export enum ExecuteCommandResponseType {
    RequestTimedOut = -7,
    InvalidIncident = -6,
    InvalidEmergencyResource = -5,
    InvalidUser = -4,
    InvalidCommand = -3,
    InvalidTransition = -2,
    InvalidOrMissingTransitionFields = -1,
    UnknownError = 0,
    Success = 1,
}

// ../NebulaAPI.Domain/Models/Statusing/ExecuteCommandRequest.cs
export interface ExecuteCommandRequest {
    userId: string;
    userExternalId: string;
    deviceId: string;
    emergencyResourceId?: string;
    emergencyResourceExternalId: string;
    incidentId?: string;
    incidentExternalId: string;
    commandId: string;
    commandExternalId: string;
    transitionId: string;
    transitionExternalId: string;
    fieldResponses: TransitionFieldResponse[];
    occurredAt: string;
    clientOccurredAtTimeZone: any;
    requireIncidentEnabled: boolean;
}

// ../NebulaAPI.Domain/Models/Statusing/TransitionField.cs
export interface TransitionField {
    identifier: string;
    name: string;
    isDisplayed: boolean;
    sortOrder: number;
    isRequired: boolean;
    entryType: FieldEntryType;
    dataType: FieldDataType;
    defaultValue: FieldValue;
    options: FieldValue[];
}

// ../NebulaAPI.Domain/Models/Statusing/CommandCategory.cs
export enum CommandCategory {
    AssignIncident = 0,
    /**
     * Not used by any implementer.
     */
    AssignEmergencyResource = 1,
    /**
     * Not used by any implementer.
     */
    RequiresIncident = 2,
    RequiresEmergencyResource = 3,
    RequiresAssignedIncident = 4,
    /**
     * Not used by any implementer.
     */
    RequiresAssignedEmergencyResource = 5,
}

// ../NebulaAPI.Domain/Models/Statusing/FieldValue.cs
export interface FieldValue {
    label: string;
    value: object;
}

// ../NebulaAPI.Domain/Models/Statusing/TransitionType.cs
export enum TransitionType {
    None = 0,
    Statusing = 1,
    ClientCreatedIncident = 2,
}

// ../NebulaAPI.Domain/Models/Statusing/StatusingConfiguration.cs
export interface StatusingConfiguration {
    defaultCommandFlowId: string;
    defaultCommandFlowExternalId: string;
    clientCreatedIncidentCommandFlowId?: string;
    clientCreatedIncidentCommandFlowExternalId: string;
    commandFlows: CommandFlow[];
}

// ../NebulaAPI.Domain/Models/Options/AmazonSnsPushPlatform.cs
export interface AmazonSnsPushPlatform {
    platform: AmazonSnsPushPlatformTypes;
    clientNotificationType: ClientNotificationType;
    principal: string;
    credential: string;
}

// ../NebulaAPI.Domain/Models/Options/SmtpOptions.cs
export interface SmtpOptions {
    server: string;
    authUser: string;
    authPassword: string;
    port: number;
    tls: boolean;
    fromAddress: string;
}

// ../NebulaAPI.Domain/Models/Options/AmazonSnsPushPlatformTypes.cs
export enum AmazonSnsPushPlatformTypes {
    APNS = 0,
    APNS_SANDBOX = 1,
    GCM = 2,
}

// ../NebulaAPI.Domain/Models/Options/AmazonSnsOptions.cs
export interface AmazonSnsOptions {
    senderId: string;
    maxPrice: string;
    s3BucketSnsTopicArn: string;
    mediaSqsArn: string;
    pushMessagingApplicationName: string;
    pushMessagingPlatforms: AmazonSnsPushPlatform[];
    smsAccessKeyId: string;
    smsSecretAccessKey: string;
    snsRegion: string;
    snsDeliveryStatusSuccessArn: string;
    snsDeliverStatusSuccessFeedbackRateInSeconds: number;
    snsDeliveryStatusFailureArn: string;
}

// ../NebulaAPI.Domain/Models/Options/AmazonS3Options.cs
export interface AmazonS3Options {
    deviceLogsBucket: string;
    mediaBucket: string;
    cloudMediaBucket: string;
    mediaV2Bucket: string;
    certificatesBucket: string;
    fileDocumentsBucket: string;
}

// ../NebulaAPI.Domain/Models/Options/AmazonLambdaOptions.cs
export interface AmazonLambdaOptions {
    imageThumbnailGenerationFunctionName: string;
}

// ../NebulaAPI.Domain/Models/Options/AwsSnsMessageAttributeKeys.cs
export interface AwsSnsMessageAttributeKeys {
}

// ../NebulaAPI.Domain/Models/Options/AwsSnsMessageAttributeKeys.cs
export enum AwsSnsSmsType {
    Promotional,
    Transactional,
}

// ../NebulaAPI.Domain/Models/Configuration/ConnectorFeatureConfiguration.cs
export interface ConnectorFeatureConfiguration {
    interfaceFeaturesRequiringUniqueConnector: InterfaceFeature[];
}

// ../NebulaAPI.Domain/Models/Configuration/SerilogConfiguration.cs
export interface SerilogConfiguration {
    watchDisposables: WatchDisposables;
}

// ../NebulaAPI.Domain/Models/Configuration/SerilogConfiguration.cs
export interface WatchDisposables {
    period: number;
}

// ../NebulaAPI.Domain/Models/Configuration/TelemetryConfiguration.cs
export interface TelemetryConfiguration {
    logGrainInvocationsToCloud: boolean;
    logGrainInvocationsToCloudIntervalInMilliseconds: number;
}

// ../NebulaAPI.Domain/Models/Configuration/IdentityServerAuthenticationConfiguration.cs
export interface IdentityServerAuthenticationConfiguration {
    jwtClientName: string;
    jwtClientSecret: string;
    referenceClientName: string;
    referenceClientSecret: string;
    apiResource: string;
    apiResourceSecret: string;
    scope: string;
    uri: string;
}

// ../NebulaAPI.Domain/Models/Configuration/FirebaseConfiguration.cs
export interface FirebaseConfiguration {
    fcmToken: string;
}

// ../NebulaAPI.Domain/Models/Configuration/ReminderConfiguration.cs
export interface ReminderConfiguration {
    cleanup: CleanupReminderConfiguration;
}

// ../NebulaAPI.Domain/Models/Configuration/ReminderConfiguration.cs
export interface CleanupReminderConfiguration {
    cleanupRunTimeHour: number;
    cleanupIntervalMinutes: number;
    incidentClosedDays: number;
}

// ../NebulaAPI.Domain/Models/Configuration/LicensedAgencyConfiguration.cs
export interface LicensedAgencyConfiguration {
    licensedAgencyId: string;
    deviceSessionLockTimeoutInMinutes?: number;
    incidentNotificationSignificantUnitsAssigned?: number;
    incidentNotificationHighPriorities: string[];
    incidentNotificationStatuses: string[];
    emergencyResourceNotificationStatuses: string[];
}

// ../NebulaAPI.Domain/Models/Configuration/LicensedAgencyConfiguration.cs
export interface LicensedAgencyConfigurationExtensions {
}

// ../NebulaAPI.Domain/Models/Configuration/OpenSearchConfiguration.cs
export interface OpenSearchConfiguration {
    baseUrl: string;
    connectorSupportDashboardId: string;
    nebulaLogsIndexPattern: string;
}

// ../NebulaAPI.Domain/Models/Configuration/AWSConfiguration.cs
export interface AWSConfiguration {
    profileName: string;
    regionEndpoint: string;
    mediaBucket: string;
    cloudMediaBucket: string;
    mediaV2Bucket: string;
    fileDocumentsBucket: string;
    certificatesBucket: string;
    deviceLogsBucket: string;
    sns: AmazonSnsOptions;
    cloudWatchMetricsNamespace: string;
    imageThumbnailGenerationFunctionName: string;
    ssm: SSMConfiguration;
}

// ../NebulaAPI.Domain/Models/Configuration/AuthenticationConfiguration.cs
export interface AuthenticationConfiguration {
    interfaceResponseTimeoutInMilliseconds: number;
}

// ../NebulaAPI.Domain/Models/Configuration/OrleansDetails.cs
export interface OrleansClusterDetails {
    serviceId: string;
    clusterId: string;
}

// ../NebulaAPI.Domain/Models/Configuration/EnvironmentInfo.cs
export interface IEnvironmentInfo {
    server: string;
    stackId: string;
    stackName: string;
}

// ../NebulaAPI.Domain/Models/Configuration/EnvironmentInfo.cs
export interface EnvironmentInfo extends IEnvironmentInfo {
    server: string;
    stackId: string;
    stackName: string;
}

// ../NebulaAPI.Domain/Models/Configuration/KibanaConfiguration.cs
export interface KibanaConfiguration {
    connectorSupportDashboard: KibanaDashboardConfiguration;
    connectorImplementerLog: KibanaLogConfiguration;
    deviceLog: KibanaLogConfiguration;
    nebulaLog: KibanaLogConfiguration;
}

// ../NebulaAPI.Domain/Models/Configuration/KibanaConfiguration.cs
export interface KibanaLogConfiguration {
    baseUrl: string;
    index: string;
}

// ../NebulaAPI.Domain/Models/Configuration/KibanaConfiguration.cs
export interface KibanaDashboardConfiguration {
    baseUrl: string;
    dashboardId: string;
}

// ../NebulaAPI.Domain/Models/Configuration/SSMConfiguration.cs
export interface SSMConfiguration {
    reloadIntervalInSeconds: number;
    enableReloadOnChange: boolean;
}

// ../NebulaAPI.Domain/Models/Configuration/SigningCertificateConfiguration.cs
export interface SigningCertificateConfiguration {
    issuer: string;
    certificate: string;
    passphrase: string;
}

// ../NebulaAPI.Domain/Models/Synchronization/PeriodicBaselineValidationResult.cs
export interface PeriodicBaselineValidationResult {
    documentType: DocumentType;
    unknownDocumentExternalIds: string[];
    staleDocumentIds: string[];
    staleDocumentExternalIds: string[];
}

// ../NebulaAPI.Domain/Models/Synchronization/DocumentBaselineManifest.cs
export interface DocumentBaselineManifest {
    documentType: DocumentType;
    manifest: Record<string, number>;
}

// ../NebulaAPI.Domain/Models/ApiTokens/ApiToken.cs
export interface ApiToken {
    apiTokenId: string;
    token: string;
    expiresOn: string;
    supportUserId?: string;
    scopes: Scope[];
}

// ../NebulaAPI.Domain/Models/Queries/QueryAutoFillType.cs
/**
 * Indicates different auto fill data types to facilitate specifying specific
 * {@link QuerySpecification} data to use when the client accesses
 * the query functionality via scanning functionality.
 */
export enum QueryAutoFillType {
    None = 0,
    /**
     * Used for data that comes from a Drivers License scan.
     */
    DriversLicense = 1,
    /**
     * Used for data that comes from a Vehicle Identification Number scan.
     */
    VehicleIdentification = 2,
}

// ../NebulaAPI.Domain/Models/Queries/QueryCategory.cs
/**
 * Used to categorize {@link QuerySpecification} as fielding certain
 * types of requests.
 */
export enum QueryCategory {
    Invalid = 0,
    NCIC = 1,
    NameLookup = 2,
    Record = 3,
}

// ../NebulaAPI.Domain/Models/Queries/QueryRequest.cs
export interface QueryRequest {
    /**
     * A user-defined request ID that is used to track individual query
     * requests.
     */
    requestId: string;
    /**
     * The originating connection ID that initiated the query.
     */
    connectionId: string;
    /**
     * The timezone that the client's (usually the field ops app) clock was set to when the request was made.
     * This is sometimes necessary for reconstructing the actual exact time a date was chosen by a user.
     */
    clientTimeZone: any;
    /**
     * The date the request was created on.
     */
    createdOn: string;
    /**
     * The date the request was last updated.
     */
    updatedOn: string;
    /**
     * The date the request was transmitted to the interface for execution.
     */
    issuedInterfaceRequestOn?: string;
    /**
     * The date the interface replied with a response.
     */
    receivedInterfaceResponseOn?: string;
    /**
     * The date the response was transmitted back to the requester.
     */
    transmittedRequestResponseOn?: string;
    /**
     * A correlating {@link QuerySpecification} ID identifying which
     * query the request is intended for.
     */
    specificationId: string;
    /**
     * A correlating {@link QuerySpecification} External ID identifying
     * which query the request is intended for.
     */
    specificationExternalId: string;
    /**
     * The {@link CustomerAgency} ID of the requesting user.
     */
    customerAgencyId: string;
    /**
     * The {@link CustomerUser} ID of the requesting user.
     */
    customerUserId: string;
    /**
     * The Owner Agency ID of the requesting user.
     */
    ownerAgencyId: string;
    /**
     * The Owner Agency External ID of the requesting user.
     */
    ownerAgencyExternalId: string;
    /**
     * The {@link User} ID of the requester.
     */
    userId: string;
    /**
     * The {@link User} External ID of the requester.
     */
    userExternalId: string;
    /**
     * The public key of the requesting user used to encrypt the query's
     * response payload.
     */
    userPublicKey: Int8Array;
    /**
     * The {@link Incident} ID associated with the requester.
     */
    incidentId?: string;
    /**
     * The {@link Incident} External ID associated with the requester.
     */
    incidentExternalId: string;
    /**
     * The {@link EmergencyResource} ID associated with the requester.
     */
    emergencyResourceId?: string;
    /**
     * The {@link EmergencyResource} External ID associated with the
     * requester.
     */
    emergencyResourceExternalId: string;
    /**
     * The {@link CustomerDevice} ID the requesting user used to
     * initiate the query.
     */
    deviceId: string;
    /**
     * The {@link CustomerDevice} name the requesting user used to
     * initiate the query.
     */
    deviceName: string;
    /**
     * A collection of {@link QueryParameterResponse}'s fulfilling
     * the associated {@link QuerySpecification}'s parameter requirements.
     */
    parameters: QueryParameterResponse[];
    /**
     * The required response type from the query fielding interface. Must match
     * one of the defined {@link QueryResponseType} in the associated
     * {@link QuerySpecification.SupportedResponseTypes}s.
     */
    requestedResponseType: QueryResponseType;
    /**
     * The category of the query.
     */
    category: QueryCategory;
    /**
     * The sub category of the query.
     */
    subCategory: QuerySubCategory;
}

// ../NebulaAPI.Domain/Models/Queries/QueryResponseType.cs
export enum QueryResponseType {
    Invalid = 0,
    /**
     * Unformatted, raw text.
     */
    Raw = 1,
    RecordPersonSummaryQueryResponseV1 = 2,
    RecordPersonDetailQueryResponseV1 = 3,
    RecordOrganizationSummaryQueryResponseV1 = 4,
    RecordOrganizationDetailQueryResponseV1 = 5,
    RecordVehicleSummaryQueryResponseV1 = 6,
    RecordVehicleDetailQueryResponseV1 = 7,
    RecordAddressSummaryQueryResponseV1 = 8,
    RecordAddressDetailQueryResponseV1 = 9,
    RecordCaseSummaryQueryResponseV1 = 10,
    RecordCaseDetailQueryResponseV1 = 11,
}

// ../NebulaAPI.Domain/Models/Queries/QueryResponse.cs
export interface QueryResponse extends SourcedPayload {
    /**
     * The {@link QueryRequest.RequestId} the response is associated
     * with.
     */
    queryRequestId: string;
    /**
     * The {@link QueryResponseType} format the payload adheres to. In
     * most, if not all, cases this should match the {@link QueryRequest.RequestedResponseType}.
     */
    responseType: QueryResponseType;
    /**
     * An encrypted payload containing some, or all, of the query request
     * as indicated by {@link Status}. May be null depending on the
     * status of the response.
     */
    payload: object;
    /**
     * Indicates the current state of the response, progress, whether this is
     * the final response, or whether an error occurred.
     */
    status: QueryResponseStatus;
}

// ../NebulaAPI.Domain/Models/Queries/AutoFillValue.cs
export enum AutoFillValue {
    None = 0,
    /**
     * The license number of a scanned drivers license.
     */
    DriversLicenseNumber = 1,
    /**
     * The VIN number from a VIN scan.
     */
    VehicleIdentificationNumber = 2,
    /**
     * Concatenated/joined full name consisting of, if available,
     * last name, first name, middle name.
     */
    FullName = 3,
    /**
     * Only the first name.
     */
    FirstName = 4,
    /**
     * Only the last name.
     */
    LastName = 5,
    /**
     * Only the middle name.
     */
    MiddleName = 6,
    /**
     * State, probably the two letter abreviation
     */
    State = 7,
    /**
     * Sex
     */
    Sex = 8,
    /**
     * Date of birth
     */
    DateOfBirth = 9,
}

// ../NebulaAPI.Domain/Models/Queries/QueryParameter.cs
/**
 * Defines a parameter for a {@link QuerySpecification}.
 */
export interface QueryParameter {
    /**
     * An identifier that can be used by requesters and interfaces.
     */
    identifier: string;
    /**
     * The name of the parameter - intended for display to users.
     */
    name: string;
    /**
     * Whether the parameter is meant to be visible or treated as
     * hidden.
     */
    isDisplayed: boolean;
    /**
     * The sort order for the parameter.
     */
    sortOrder: number;
    /**
     * Whether the parameter is required or optional.
     */
    isRequired: boolean;
    /**
     * The entry type to be used by the client UX when requesting
     * a value for the parameter.
     */
    entryType: FieldEntryType;
    /**
     * The data type for the parameter.
     */
    dataType: FieldDataType;
    /**
     * Optional declaration of what data should be used to auto fill
     * this field. Multiple values can be specified and will be tried
     * in enumeration order until a valid value is found to populate.
     *
     * @remarks
     * The collection of values will be tried, in order, until a value that can
     * be filled in is found. Ensure you use an ordered collection such as 
     * {@link SortedSet{T}"/> or <see cref="SortedList{TKey, TValue}} if exploiting
     * this behavior.
     */
    autoFillValues: AutoFillValue[];
    /**
     * A default value for the parameter. Providing a default value may
     * cause a required field to be treated as optional.
     */
    defaultValue: FieldValue;
    /**
     * A collection of acceptable values to be provided and validated against
     * user input.
     */
    options: FieldValue[];
    /**
     * Indicates that the parameter supports phonetic matching.
     */
    isPhoneticsSupported: boolean;
}

// ../NebulaAPI.Domain/Models/Queries/QueryParameter.cs
export interface QueryParameterExtensions {
}

// ../NebulaAPI.Domain/Models/Queries/QueryResponseStatus.cs
/**
 * Used to transmit periodic {@link QueryResponse} status updates
 * for a given {@link QueryRequest}.
 */
export interface QueryResponseStatus {
    /**
     * An optional progress field indicating the current progress for the
     * response.
     */
    progress?: number;
    /**
     * Indicates if the state of the response's progress cannot be determined.
     */
    indeterminate: boolean;
    /**
     * Whether this is the final response for the request.
     */
    isFinished: boolean;
    /**
     * Indicates the overall success of the response. True indicates the
     * request was fielded wholly and successfully while false covers all
     * other cases.
     */
    result?: boolean;
    /**
     * An optional, associated error message that may be returned in the
     * event the response's {@link Result} indicates a failure.
     */
    error: string;
}

// ../NebulaAPI.Domain/Models/Queries/QueryParameterResponse.cs
export interface QueryParameterResponse extends SourcedPayload {
    /**
     * The {@link QueryParameter.Identifier} this response is
     * associated with.
     */
    queryParameterIdentifier: string;
    /**
     * The {@link QueryParameter.Name} this response is associated
     * with.
     */
    queryParameterName: string;
    /**
     * The data type this parameter response contains.
     */
    dataType: FieldDataType;
    /**
     * The value of the parameter response.
     */
    value: FieldValue;
    /**
     * Indicates that the parameter should be matched by phonetics.
     */
    usePhonetics: boolean;
}

// ../NebulaAPI.Domain/Models/Queries/QuerySubCategory.cs
export enum QuerySubCategory {
    Invalid = 0,
    /**
     * Record query for a list of person summaries.
     */
    RecordPersonSummary = 1,
    /**
     * Record query for a single person's details.
     */
    RecordPersonDetail = 2,
    /**
     * Record query for a list of organization summaries.
     */
    RecordOrganizationSummary = 3,
    /**
     * Record query for a single organization's details.
     */
    RecordOrganizationDetail = 4,
    /**
     * Record query for a list of vehicle summaries.
     */
    RecordVehicleSummary = 5,
    /**
     * Record query for a single vehicle's details.
     */
    RecordVehicleDetail = 6,
    /**
     * Record query for a list of address summaries.
     */
    RecordAddressSummary = 7,
    /**
     * Record query for a single address's details.
     */
    RecordAddressDetail = 8,
    /**
     * Record query for a list of case summaries.
     */
    RecordCaseSummary = 9,
    /**
     * Record query for a single cases's details.
     */
    RecordCaseDetail = 10,
}

// ../NebulaAPI.Domain/Models/Permissions/IPermissionAuthorizer.cs
export interface IPermissionAuthorizer<TDocument> {
}

// ../NebulaAPI.Domain/Models/Permissions/IncidentAuthorizer.cs
export interface IncidentAuthorizer extends IPermissionAuthorizer<Incident> {
}

// ../NebulaAPI.Domain/Models/Permissions/FileAuthorizer.cs
export interface FileAuthorizer extends IPermissionAuthorizer<File> {
}

// ../NebulaAPI.Domain/Models/Permissions/DefaultPermissionManager.cs
/**
 * Default {@link IPermissionManager} implementation that relies on
 * dependency injection to delegate calls to typed {@link IPermissionAuthorizer{TDocument}}s.
 */
export interface DefaultPermissionManager extends IPermissionManager {
}

// ../NebulaAPI.Domain/Models/Permissions/QuerySpecificationAuthorizer.cs
export interface QuerySpecificationAuthorizer extends IPermissionAuthorizer<QuerySpecification> {
}

// ../NebulaAPI.Domain/Models/Permissions/IPermissionManager.cs
export interface IPermissionManager {
}

// ../NebulaAPI.Domain/Models/Permissions/PositionAuthorizer.cs
export interface PositionAuthorizer extends IPermissionAuthorizer<Position> {
}

// ../NebulaAPI.Domain/Models/Permissions/EmergencyResourceAuthorizer.cs
export interface EmergencyResourceAuthorizer extends IPermissionAuthorizer<EmergencyResource> {
}

// ../NebulaAPI.Domain/Models/Permissions/UserPermissions.cs
export interface UserPermissions extends HashSet<string> {
}

// ../NebulaAPI.Domain/Models/BlueGreen/BlueGreenImportLockDrainEvent.cs
export interface BlueGreenImportLockDrainEvent {
    isDrainingActive: boolean;
}

// ../NebulaAPI.Domain/Models/BlueGreen/BlueGreenConfiguration.cs
export interface BlueGreenConfiguration {
    isBlueGreenRunning: boolean;
    isImportLockDrainingActive: boolean;
}

// ../NebulaAPI.Domain/Models/System/CertificateConfigurationOverrides.cs
export interface CertificateConfigurationOverrides {
    deviceCertificateDurationSecondsOverride?: number;
    connectorCertificateDurationSecondsOverride?: number;
    identityCertificateDurationSecondsOverride?: number;
    forceCertificateGenerationError: boolean;
}

// ../NebulaAPI.Domain/Models/System/ImportConfiguration.cs
export interface ImportConfiguration {
    enableLegacyDocumentImportDeduping?: boolean;
    enableDocumentImportDeduping?: boolean;
    enableLambdaThumbnailGeneration?: boolean;
    enableS3FileDocumentCleanup?: boolean;
    enableStatusingDocumentDebugLogs?: boolean;
    enableDeviceAvlPushMonitoring?: boolean;
    importLockAcquisitionDelayInSeconds?: number;
}

// ../NebulaAPI.Domain/Models/System/NebulaSystemComponent.cs
export enum NebulaSystemComponent {
    Unknown,
    NebulaAPI,
    OrleansHost,
    IdentityServer,
    WebAdmin,
}

// ../NebulaAPI.Domain/Models/System/LocationSharingConfiguration.cs
export interface LocationSharingConfiguration {
    forceOverrideAllDeviceConfigurations: boolean;
    deviceLocationSharingIntervalInSeconds: number;
}

// ../NebulaAPI.Domain/Models/System/DiagnosticConfiguration.cs
export interface DiagnosticConfiguration {
    runtimeSystemComponentLogLevelOverrides: Record<NebulaSystemComponent, any>;
    runtimeGlobalLogLevelOverride?: any;
}

// ../NebulaAPI.Domain/Models/System/ImportLockConfiguration.cs
/**
 * Configuration for customer agency level locking during document imports.
 */
export interface ImportLockConfiguration {
    /**
     * Configures how long to wait for a document import lock be obtained before timing out.
     */
    lockTimeoutInMilliseconds: number;
    lockStalenessInMilliseconds: number;
    lockTimeoutPollIntervalInMilliseconds: number;
}

// ../NebulaAPI.Domain/Models/Connections/SubjectType.cs
/**
 * Indicates what the type of MorseL/SignalR connection is.
 */
export enum SubjectType {
    /**
     * The connection is from an Exeo client
     */
    Exeo = 0,
    /**
     * The connection is from an Interface
     */
    Interface = 1,
}

// ../NebulaAPI.Domain/Models/MediaV2/RecoverableMedia.cs
export interface RecoverableMedia {
    id: string;
    mediaType: MediaType;
}

// ../NebulaAPI.Domain/Models/MediaV2/MediaRecordFilter.cs
export interface MediaRecordFilter {
    userId?: string;
    associatedMediaTimePeriod: TimePeriod;
    includeUnassociatedMedia: boolean;
    ids?: ImmutableHashSet<string>;
    resultLimit: number;
}

// ../NebulaAPI.Domain/Models/MediaV2/MediaRecordAssociation.cs
export interface MediaRecordAssociation {
    id: string;
    mediaId: string;
    associatedDocumentId: string;
    associatedDocumentType: DocumentType;
    processingAttempts: number;
    processingLastAttempted?: string;
    destinationReceivedRequest?: string;
    processingSucceeded?: string;
    processingError?: string;
    createdOn: string;
}

// ../NebulaAPI.Domain/Models/MediaV2/MediaRecord.cs
export interface MediaRecord {
    id: string;
    customerAgencyId: string;
    agencyId: string;
    userId: string;
    deviceId: string;
    s3Bucket: string;
    encryptedFileS3Key: string;
    encryptedFileMd5Checksum: string;
    encryptedFileEncryptionKey: Int8Array;
    originatedOn: string;
    updatedOn: string;
    deletedOn?: string;
    isActive: boolean;
}

// ../NebulaAPI.Domain/Models/MediaV2/MediaRecoveryStatus.cs
export enum MediaRecoveryStatus {
    NotStarted = 0,
    InProgress = 1,
    Succeeded = 2,
    Failed = 3,
}

// ../NebulaAPI.Domain/Models/MediaV2/MediaRecoveryDetails.cs
export interface MediaRecoveryDetails {
    finished?: string;
    status: MediaRecoveryStatus;
}

// ../NebulaAPI.Domain/Models/AdHoc/AdHocInterfaceConfiguration.cs
export interface AdHocInterfaceConfiguration {
    registration: InterfaceRegistration;
    properties: Record<InterfaceProperty, string>;
}

// ../NebulaAPI.Domain/Models/Avl/DeviceAvlPushStats.cs
export interface DeviceAvlPushStats {
    pushMetadata: DeviceAvlPushMetadata;
    avlRecordingStartedAt: string;
}

// ../NebulaAPI.Domain/Models/Avl/DeviceAvlPushMetadata.cs
export interface DeviceAvlPushMetadata {
    lastCustomerDeviceIdToPushAvl: string;
    lastAvlPushedAt?: string;
    positionGeneratedAt?: string;
}

// ../NebulaAPI.Domain/Models/Files/FileUploadLocationResponse.cs
export interface FileUploadLocationResponse {
    uploadLocation: object;
    requestHeaders: FileUploadLocationResponseHeader[];
    contentHeaders: FileUploadLocationResponseHeader[];
}

// ../NebulaAPI.Domain/Models/Files/FileStatusUpdateRequest.cs
export interface FileStatusUpdateRequest {
    externalId: string;
    uploadStatus: FileUploadStatus;
    reason: string;
    md5Checksum: Int8Array;
}

// ../NebulaAPI.Domain/Models/Files/FileUploadLocationRequest.cs
export interface FileUploadLocationRequest {
    externalId: string;
    type: FileType;
    md5Checksum: Int8Array;
}

// ../NebulaAPI.Domain/Models/Files/IncidentFilePushNotificationGrainState.cs
export interface IncidentFilePushNotificationGrainState {
}

// ../NebulaAPI.Domain/Models/Files/FileUploadLocationResponseHeader.cs
export interface FileUploadLocationResponseHeader {
    key: string;
    value: string;
}

// ../NebulaAPI.Domain/Models/Documents/IPermissibleDocument.cs
export interface IPermissibleDocument {
    permissions: Record<PermissionScenarios, PermissionRule>;
}

// ../NebulaAPI.Domain/Models/Documents/QuerySpecification.cs
/**
 * Defines a query an interface is capable of responding to.
 */
export interface QuerySpecification extends BaseDocumentModel, INebulaAddressableEntity<Guid> {
    /**
     * The name of the query.
     */
    name: string;
    /**
     * The description of the query.
     */
    description: string;
    /**
     * The category (type) of the query. Used for grouping queries in client
     * UX.
     */
    category: QueryCategory;
    /**
     * The sub category of the query. Used to indicate the specific type of query.
     */
    subCategory: QuerySubCategory;
    /**
     * The collection of required and optional fields for the query.
     */
    fields: QueryParameter[];
    /**
     * A collection of permission scenarios and associated permission rules
     * that define the permissions necessary for executing this query.
     */
    permissions: Record<PermissionScenarios, PermissionRule>;
    /**
     * A collection of response types the sourcing interface is capable of
     * responding with. Query requests may indicate a response type that the
     * sourcer respond with from this list.
     */
    supportedResponseTypes: QueryResponseType[];
    /**
     * The connector system ID for grouping a set of customer defined queries, such as
     * "Gun Queries", "Vehicle Queries", "Boat Queries" or any other customer defined
     * grouping of queries.
     */
    queryTypeGroupKey: string;
    /**
     * The customer facing name of a set of grouped queries.
     * Queries in the same group must have the same {@link QueryTypeGroupKey}
     */
    queryTypeName: string;
    /**
     * The sort order for a query type.
     */
    queryTypeSortOrder?: number;
    /**
     * The name for a query subtype, for instance, a QueryType of "Vehicle Queries" may have 
     * a query subtype of "VIN" and a query subtype of "License Plate"
     */
    querySubTypeName: string;
    /**
     * The key to represent a group of query sub types.
     */
    querySubTypeGroupKey: string;
    /**
     * The sort order for a query sub type.
     */
    querySubTypeSortOrder?: number;
}

// ../NebulaAPI.Domain/Models/Documents/Incident.cs
export interface Incident extends BaseDocumentModel, INebulaAddressableEntity<Guid>, IActivatableDocument, IPermissibleDocument {
    statusId: string;
    statusExternalId: string;
    priorityId?: string;
    priorityExternalId: string;
    incidentCode: string;
    incidentCodeDescription: string;
    disposition: string;
    jurisdiction1Id?: string;
    jurisdiction1ExternalId: string;
    jurisdiction2Id?: string;
    jurisdiction2ExternalId: string;
    jurisdiction3Id?: string;
    jurisdiction3ExternalId: string;
    responderEmergencyResourceIds: string[];
    responderEmergencyResourceExternalIds: string[];
    responderAgenciesIds: string[];
    responderAgenciesExternalIds: string[];
    dispatchAgencyId: string;
    dispatchAgencyExternalId: string;
    abbreviatedIncidentNumber: string;
    incidentNumber: string;
    disciplineType: DisciplineType;
    incidentDateTime: string;
    call: Call;
    location: Location;
    narratives: Narrative[];
    names: NameRecord[];
    vehicles: Vehicle[];
    alerts: Alert[];
    links: Link[];
    locationHistory: LocationHistory[];
    assignmentHistory: HistoricalAssignment[];
    responseTimes: ResponseTime[];
    additionalData: AdditionalData[];
    permissions: Record<PermissionScenarios, PermissionRule>;
    /**
     * List of File external IDs associated with the incident as "Media" This is purposefully a list to
     * account for...whatever weird situation might necessitate duplicate media on the incident.
     */
    mediaExternalIds: string[];
    /**
     * List of File IDs associated with the incident as "Media". This is purposefully a list to account
     * for...whatever weird situation might necessitate duplicate media on the incident.
     */
    mediaIds: string[];
    history: IncidentEvent[];
}

// ../NebulaAPI.Domain/Models/Documents/SignatureCollectionRequest.cs
export interface SignatureCollectionRequest extends BaseDocumentModel, INebulaAddressableEntity<Guid>, IActivatableDocument {
    documentTitle: string;
    documentType: string;
    documentDescription: string;
    signerName: string;
    ownerAgencyId: string;
    ownerAgencyExternalId: string;
    recipientUserExternalId: string;
    recipientUserId: string;
}

// ../NebulaAPI.Domain/Models/Documents/Jurisdiction1.cs
export interface Jurisdiction1 extends BaseDocumentModel, INebulaAddressableEntity<Guid> {
    ownerAgencyId?: string;
    ownerAgencyExternalId: string;
    name: string;
    abbreviation: string;
    sortOrder: number;
}

// ../NebulaAPI.Domain/Models/Documents/Transition.cs
export interface Transition extends BaseDocumentModel, INebulaAddressableEntity<Guid> {
    description: string;
    sortOrder: number;
    nextStatusId?: string;
    nextStatusExternalId: string;
    fields: TransitionField[];
    styling: Record<StyleField, string>;
    transitionType: TransitionType;
}

// ../NebulaAPI.Domain/Models/Documents/Priority.cs
export interface Priority extends BaseDocumentModel, INebulaAddressableEntity<Guid> {
    ownerAgencyId?: string;
    ownerAgencyExternalId: string;
    description: string;
    color: string;
    sortOrder: number;
}

// ../NebulaAPI.Domain/Models/Documents/BaseDocumentModel.cs
export interface BaseDocumentModel extends INebulaAddressableEntity<Guid> {
    id: string;
    externalId: string;
    customerAgencyId: string;
    customerId: string;
    createdOn: string;
    updatedAt: string;
    deactivatedOn?: string;
    pushIndex?: number;
    isActive: boolean;
}

// ../NebulaAPI.Domain/Models/Documents/User.cs
export interface User extends BaseDocumentModel, INebulaAddressableEntity<Guid> {
    name: string;
    fullName: string;
    emailAddresses: EmailAddress[];
    phones: Phone[];
    ownerAgencyId: string;
    ownerAgencyExternalId: string;
    emergencyResourceId?: string;
    emergencyResourceExternalId: string;
    previouslyAssignedEmergencyResourceId?: string;
    permissions: string[];
    capabilities: UserCapabilities[];
}

// ../NebulaAPI.Domain/Models/Documents/EmergencyResource.cs
export interface EmergencyResource extends BaseDocumentModel, INebulaAddressableEntity<Guid>, IActivatableDocument, IPermissibleDocument {
    unitIdentification: string;
    typeId: string;
    typeExternalId: string;
    statusId: string;
    statusExternalId: string;
    statusUpdatedOn?: string;
    incidentId?: string;
    incidentExternalId: string;
    ownerAgencyId: string;
    ownerAgencyExternalId: string;
    jurisdiction1Id?: string;
    jurisdiction1ExternalId: string;
    jurisdiction2Id?: string;
    jurisdiction2ExternalId: string;
    jurisdiction3Id?: string;
    jurisdiction3ExternalId: string;
    disciplineType: DisciplineType;
    incidentIdStacks: string[];
    incidentExternalIdStacks: string[];
    location: EmergencyResourceLocation;
    personnel: Personnel[];
    additionalData: AdditionalData[];
    permissions: Record<PermissionScenarios, PermissionRule>;
}

// ../NebulaAPI.Domain/Models/Documents/Command.cs
export interface Command extends BaseDocumentModel, INebulaAddressableEntity<Guid> {
    statusIds: string[];
    statusExternalIds: string[];
    name: string;
    description: string;
    sortOrder: number;
    categories: CommandCategory[];
    defaultTransitionId: string;
    defaultTransitionExternalId: string;
    transitionIds: string[];
    transitionExternalIds: string[];
    styling: Record<StyleField, string>;
}

// ../NebulaAPI.Domain/Models/Documents/Agency.cs
export interface Agency extends BaseDocumentModel, INebulaAddressableEntity<Guid> {
    ori: string;
    name: string;
    abbreviation: string;
    type: string;
    isDispatchAgency: boolean;
    isResponderAgency: boolean;
    sortOrder: number;
    productLine: string;
}

// ../NebulaAPI.Domain/Models/Documents/FilterConfiguration.cs
export interface FilterConfiguration extends BaseDocumentModel, INebulaAddressableEntity<Guid> {
    ownerAgencyId?: string;
    ownerAgencyExternalId: string;
    incidentFilterConfiguration: IncidentFilterConfiguration;
    emergencyResourceFilterConfiguration: EmergencyResourceFilterConfiguration;
}

// ../NebulaAPI.Domain/Models/Documents/FilterConfiguration.cs
export interface EmergencyResourceFilterConfiguration {
    agencyLabel: string;
    agencyEnabled: boolean;
    emergencyResourceTypeLabel: string;
    emergencyResourceTypeEnabled: boolean;
    statusLabel: string;
    statusEnabled: boolean;
    jurisdiction1Label: string;
    jurisdiction1Enabled: boolean;
    jurisdiction2Label: string;
    jurisdiction2Enabled: boolean;
    jurisdiction3Label: string;
    jurisdiction3Enabled: boolean;
}

// ../NebulaAPI.Domain/Models/Documents/FilterConfiguration.cs
export interface IncidentFilterConfiguration {
    dispatchAgencyLabel: string;
    dispatchAgencyEnabled: boolean;
    responderAgencyLabel: string;
    responderAgencyEnabled: boolean;
    statusLabel: string;
    statusEnabled: boolean;
    jurisdiction1Label: string;
    jurisdiction1Enabled: boolean;
    jurisdiction2Label: string;
    jurisdiction2Enabled: boolean;
    jurisdiction3Label: string;
    jurisdiction3Enabled: boolean;
    priorityLabel: string;
    priorityEnabled: boolean;
}

// ../NebulaAPI.Domain/Models/Documents/InterconnectedCadConfiguration.cs
export interface InterconnectedCadConfiguration extends BaseDocumentModel, INebulaAddressableEntity<Guid> {
    /**
     * Determines whether interconnected CAD is enabled for the
     * corresponding Owner Agency ID.
     */
    isEnabled: boolean;
    /**
     * Specifies the owner agency ID this configuration applies to. If
     * null this document is treated as the default configuration document
     * unless overridden by a document with a specified ID.
     */
    ownerAgencyId?: string;
    /**
     * Specifies the owner agency ID this configuration applies to. If
     * null this document is treated as the default configuration document
     * unless overridden by a document with a specified ID.
     */
    ownerAgencyExternalId: string;
    /**
     * An exhaustive list of Incident codes pertaining to this configuration.
     */
    incidentCodes: string[];
}

// ../NebulaAPI.Domain/Models/Documents/IncidentEvent.cs
export interface IncidentEvent {
    timestamp: string;
    title: string;
    description: string;
}

// ../NebulaAPI.Domain/Models/Documents/INebulaAddressableEntity.cs
export interface INebulaAddressableEntity<TKey> {
    id: TKey;
    externalId: string;
    customerAgencyId: string;
    customerId: string;
    createdOn: string;
    updatedAt: string;
    pushIndex?: number;
}

// ../NebulaAPI.Domain/Models/Documents/GlobalConfiguration.cs
export interface GlobalConfiguration extends BaseDocumentModel, INebulaAddressableEntity<Guid> {
    ownerAgencyExternalId: string;
    ownerAgencyId?: string;
    allowAddNarrativeToCfs: boolean;
    allowAddMediaToIncident: boolean;
    allowDispatch: boolean;
    allowMediaUpload: boolean;
    allowCaseSearch: boolean;
    allowUserAssignment: boolean;
    allowStatusing: boolean;
    allowPostionAvl: boolean;
    allowPositionPlt: boolean;
    allowUserLogoff: boolean;
    allowDeviceAvlTransmission: boolean;
    allowUserNotificationMessages: boolean;
    /**
     * @deprecated Use AllowNcicQuery or AllowRecordQuery instead.
     */
    allowQuery: boolean;
    allowNcicQuery: boolean;
    allowRecordQuery: boolean;
    allowRecordQueryFromDispatch: boolean;
    allowViewingClosedCalls: boolean;
    allowClientIncidentCreation: boolean;
    allowSignatureCollection: boolean;
    statusing: StatusingConfiguration;
    authentication: AuthenticationConfiguration;
    ncic: NcicConfiguration;
    incidentClosedStatusExternalIds: string[];
    incidentClosedStatusIds: string[];
    defaultQuerySpecificationExternalIds: Record<QueryAutoFillType, string>;
    defaultQuerySpecificationIds: Record<QueryAutoFillType, string>;
}

// ../NebulaAPI.Domain/Models/Documents/GlobalConfiguration.cs
export interface GlobalConfigurationExt {
}

// ../NebulaAPI.Domain/Models/Documents/Status.cs
export interface Status extends BaseDocumentModel, INebulaAddressableEntity<Guid> {
    ownerAgencyId?: string;
    ownerAgencyExternalId: string;
    description: string;
    sortOrder: number;
    color: string;
    type: DocumentType;
}

// ../NebulaAPI.Domain/Models/Documents/Jurisdiction3.cs
export interface Jurisdiction3 extends BaseDocumentModel, INebulaAddressableEntity<Guid> {
    ownerAgencyId?: string;
    ownerAgencyExternalId: string;
    name: string;
    abbreviation: string;
    sortOrder: number;
}

// ../NebulaAPI.Domain/Models/Documents/File.cs
export interface File extends BaseDocumentModel, INebulaAddressableEntity<Guid>, IPermissibleDocument {
    ownerAgencyId: string;
    ownerAgencyExternalId: string;
    fileType: FileType;
    uploadStatus: FileUploadStatus;
    md5Checksum: Int8Array;
    fileExtension: string;
    description: string;
    comments: string;
    fileSizeInBytes?: number;
    height?: number;
    width?: number;
    lengthInMilliseconds?: number;
    sortOrder: number;
    permissions: Record<PermissionScenarios, PermissionRule>;
}

// ../NebulaAPI.Domain/Models/Documents/Position.cs
export interface Position extends BaseDocumentModel, INebulaAddressableEntity<Guid>, IPermissibleDocument {
    ownerAgencyId: string;
    ownerAgencyExternalId: string;
    refId: string;
    type: DocumentType;
    /**
     * @deprecated EmergencyResourceTypeExternalId is deprecated. Use EmergencyResourceTypeExternalId on EmergencyResource
     */
    emergencyResourceTypeExternalId: string;
    /**
     * @deprecated EmergencyResourceTypeId is deprecated. Use TypeId on EmergencyResource
     */
    emergencyResourceTypeId: string;
    /**
     * @deprecated DisciplineType is deprecated. Use DisciplineType on EmergencyResource
     */
    disciplineType: DisciplineType;
    coordinate: GeoCoordinate;
    sortOrder: number;
    direction?: number;
    description: string;
    permissions: Record<PermissionScenarios, PermissionRule>;
}

// ../NebulaAPI.Domain/Models/Documents/Jurisdiction2.cs
export interface Jurisdiction2 extends BaseDocumentModel, INebulaAddressableEntity<Guid> {
    ownerAgencyId?: string;
    ownerAgencyExternalId: string;
    name: string;
    abbreviation: string;
    sortOrder: number;
}

// ../NebulaAPI.Domain/Models/Documents/Case.cs
export interface Case extends BaseDocumentModel, INebulaAddressableEntity<Guid>, IPermissibleDocument {
    description: string;
    caseNumber: string;
    location: string;
    assignment: string;
    incidentNumber: string;
    ownerAgencyId: string;
    ownerAgencyExternalId: string;
    permissions: Record<PermissionScenarios, PermissionRule>;
}

// ../NebulaAPI.Domain/Models/Communication/ConnectionProtocolFeature.cs
export enum ConnectionProtocolFeature {
    /**
     * Default connection protocol features.
     */
    Unknown = 0,
    /**
     * Supports gzip compression
     */
    GzipCompression = 1,
}

// ../NebulaAPI.Domain/Models/Communication/SecureConnectionProtocol.cs
export enum SecureConnectionProtocol {
    /**
     * Default secure connection protocol.
     */
    Unknown = 0,
    /**
     * Successor to the default, untracked protocol.
     * /// Supports:
     * - IV Rotation
     */
    v1_0_0 = 1,
}

// ../NebulaAPI.Domain/Models/Communication/RequestToken.cs
export interface RequestToken {
    requestId: string;
    /**
     * Used for requests intended for a response to a specific connection ID.
     */
    connectionId: string;
    /**
     * Used for requests intended for a response to a specific Device / User pair.
     */
    customerUserId?: string;
    /**
     * Used for requests intended for a response to a specific Device / User pair.
     */
    deviceId?: string;
}

// ../NebulaAPI.Domain/Models/Communication/SourcedPayload.cs
export interface SourcedPayload extends ISourcedPayload {
    payloadSource: FieldOpsSources;
}

// ../NebulaAPI.Domain/Models/Versioning/ClientVersion.cs
export interface VersionExtensions {
}

// ../NebulaAPI.Domain/Models/Versioning/ClientVersion.cs
export enum InterfaceApiVersion {
    v1_0_0 = 0,
    /**
     * v2 was introduced to support multi connector
     */
    v2_0_0 = 1,
}

// ../NebulaAPI.Domain/Models/Versioning/ClientVersion.cs
export enum DeviceApiVersion {
    v1_0_0 = 0,
}

// ../NebulaAPI.Domain/Models/Versioning/ClientVersion.cs
export enum InterfaceFeatureFlag {
    None = 0,
    GeneralAvailability = 1 << 0,
    AddNarrativeToCfs = 1 << 1,
    Statusing = 1 << 2,
    UserAssignToEmergencyResource = 1 << 3,
}

// ../NebulaAPI.Domain/Models/Versioning/ClientVersion.cs
export enum DeviceFeatureFlag {
    None = 0,
    GeneralAvailability = 1 << 0,
    AddNarrativeToCfs = 1 << 1,
    Statusing = 1 << 2,
    UserAssignToEmergencyResource = 1 << 3,
}

// ../NebulaAPI.Domain/Models/Versioning/ClientVersion.cs
export enum InterfaceFeature {
    None = 0,
    GeneralAvailability = 1,
    AddNarrativeToCfs = 2,
    Statusing = 3,
    UserAssignToEmergencyResource = 4,
    MediaUpload = 5,
    CaseSearch = 6,
    PositionsAvl = 7,
    UserLogoff = 8,
    DeviceAvlTransmission = 9,
    UserNotificationMessages = 10,
    /**
     * If present, means that assignment commands should be restricted to those
     * with the AssignIncident category.
     */
    RespectsCommandCategoryForAssignedIncident = 11,
    RespectsAllowDispatch = 12,
    Query = 13,
    /**
     * If present, declares that the interface supports the Interconnected CAD
     * interchange of data.
     */
    InterconnectedCad_Incidents = 14,
    AllowViewingClosedCalls = 15,
    /**
     * If present, declares that the interface supports receiving photo media uploads
     * to be added to a case
     */
    AcceptsPhotoMediaType = 16,
    /**
     * If present, declares that the interface supports receiving audio recording media uploads
     * to be added to a case
     */
    AcceptsAudioRecordingMediaType = 17,
    /**
     * If present, declares that the interface supports receiving note media uploads
     * to be added to a case
     */
    AcceptsNoteMediaType = 18,
    /**
     * If present, declares that the interface supports receiving pending narratives to be added to an incident
     */
    AcceptsNarrativeMediaType = 19,
    /**
     * If present, declares that the interface provides dispatch data for the device to use on the Calls for Service, Units, and Map screens
     */
    Dispatch = 20,
    /**
     * If present, declares that the connector abstraction supports accepting an encrypted password and decrypting it before handing it to the connector implementation.
     */
    EndToEndAuthenticationEncryption = 21,
    /**
     * If present, declares that the interface supports NCIC queries.
     */
    NcicQuery = 22,
    /**
     * If present, declares that the interface supports record queries.
     */
    RecordQuery = 23,
    /**
     * If present, declares that the interface supports allowing a client to create incidents.
     */
    ClientCreateIncident = 24,
    /**
     * If present, declares that the interface supports updating its configuration at runtime based on requests
     * from Nebula.
     */
    RuntimeConfigurationOverride = 25,
    Files = 26,
    AddMediaToIncident = 27,
    ProvidesFederatedAuthenticationDetails = 28,
    SignatureCollection = 29,
    PositionsPlt = 30,
}

// ../NebulaAPI.Domain/Models/Versioning/ClientVersion.cs
export enum DeviceFeature {
    None = 0,
    GeneralAvailability = 1,
    AddNarrativeToCfs = 2,
    Statusing = 3,
    UserAssignToEmergencyResource = 4,
    MediaUpload = 5,
    CaseSearch = 6,
    PositionsAvl = 7,
    UserLogoff = 8,
    AgencySpecificGlobalConfiguration = 9,
    /**
     * If present, means the device supports the new linked document
     * subscriptions as opposed to using the inverse filters.
     */
    LinkedDocumentRequests = 10,
    /**
     * If present, the device supports the push notification payload that contains platform specific UI keys.
     */
    UiNotificationPayload = 11,
    /**
     * If present, the device supports user notification messages issues from the connector.
     */
    UserNotificationMessages = 12,
    /**
     * Indicates the device supports the new reference type authentication
     * tokens (en lieu of JWT tokens).
     */
    SupportsReferenceAuthenticationTokens = 13,
    /**
     * Declares the device supports receiving interconnected CAD incidents
     */
    ReceiveInterconnectedCad_Incidents = 14,
    /**
     * Declares that the device knows how to support multiple connectors for the agency
     */
    SupportsMultipleConnectors = 15,
    /**
     * Declares that the device knows how to handle receiving device registration information
     */
    SupportsOnDeviceRegistration = 16,
    Files = 17,
    AddMediaToIncident = 18,
    SignatureCollection = 19,
}

// ../NebulaAPI.Domain/Models/Versioning/SslInfo.cs
export interface SslInfo {
    protocol: string;
    cipher: string;
}

// ../NebulaAPI.Domain/Models/Versioning/ClientProperties.cs
export enum InterfaceProperty {
    /**
     * Specifies the connector abstraction version used by the communicating
     * interface.
     */
    ConnectorAbstractionVersion = 1,
    /**
     * Specifies the OS the interface is running on.
     */
    OperatingSystem = 2,
    /**
     * Specifies the version of .NET the interface is using.
     */
    FrameworkDescription = 3,
    /**
     * Identifies the interface's host machine's local IP address.
     */
    LocalIpAddress = 4,
    /**
     * Identifies the interface's host machine's remote IP address.
     */
    RemoteIpAddress = 5,
    /**
     * Specifies the version of the product line's connector implementation.
     */
    ConnectorVersion = 6,
    /**
     * This is the version of the API that the connector abstraction is using.
     */
    ApiVersion = 7,
    /**
     * Specifies the vendor and version of software that the orginating system is using.
     * I.E. Enterprise CAD 1.4.5, Pro Suite CAD .4.5.6, Enterprise RMS 1.2.3 or Enterprise TTMS 1.8.9
     */
    VendorSoftwareVersion = 8,
}

// ../NebulaAPI.Domain/Models/Versioning/ClientProperties.cs
export enum DeviceProperty {
    AppVersion = 0,
    DeviceModel = 1,
    DeviceManufacturer = 2,
    OsVersion = 3,
    AppBrandingType = 4,
}

// ../NebulaAPI.Domain/Models/Versioning/ClientRegistration.cs
export interface InterfaceRegistration {
    customerInterfaceId?: string;
    apiVersion: InterfaceApiVersion;
    properties: Record<InterfaceProperty, string>;
    featureFlags: InterfaceFeatureFlag;
    enabledFeatures: InterfaceFeature[];
    supportsNewInterfaceFeatures: boolean;
}

// ../NebulaAPI.Domain/Models/Versioning/ClientRegistration.cs
export interface DeviceRegistration {
    customerDeviceId?: string;
    licensedAgencyId?: string;
    licensedAgencyName: string;
    customerAgencyId?: string;
    customerAgencyName: string;
    customerDeviceName: string;
    apiVersion: DeviceApiVersion;
    properties: Record<DeviceProperty, string>;
    featureFlags: DeviceFeatureFlag;
    capableFeatures: DeviceFeature[];
    supportsNewDeviceFeatures: boolean;
}

// ../NebulaAPI.Domain/Models/ClientConfiguration/CustomerDeviceConfigurationKey.cs
export enum CustomerDeviceConfigurationKey {
    CustomerDeviceLogging = 0,
    CustomerDeviceCertificate = 1,
}

// ../NebulaAPI.Domain/Models/ClientConfiguration/ClientConfigurationKey.cs
export interface ClientConfiguration {
}

// ../NebulaAPI.Domain/Models/ClientConfiguration/ClientConfigurationKey.cs
export enum ClientConfigurationKey {
    PushNotifications = 1,
}

// ../NebulaAPI.Domain/Models/ClientConfiguration/CustomerUserConfiguration.cs
export interface CustomerUserConfigurationKey {
    customerUserId: string;
    customerDeviceId?: string;
    key: string;
}

// ../NebulaAPI.Domain/Models/ClientConfiguration/CustomerUserConfiguration.cs
/**
 * A key/value pair for a customer user with optional device
 * target.
 */
export interface CustomerUserConfiguration {
    customerUserId: string;
    customerDeviceId?: string;
    key: string;
    value: unknown;
    createdOn: string;
    updatedOn: string;
}

// ../NebulaAPI.Domain/Models/ClientConfiguration/CustomerDeviceCertificateConfiguration.cs
export interface CustomerDeviceCertificateConfiguration {
    allowManualCertificateRenewal: boolean;
    certificateRenewalWindowInSecondsOverride?: number;
}

// ../NebulaAPI.Domain/Models/ClientConfiguration/CustomerDeviceLoggingConfigurations.cs
/**
 * Key: {@link CustomerDeviceConfigurationKey.CustomerDeviceLogging}
 * Tracks device logging configuration
 */
export interface CustomerDeviceLoggingConfiguration {
    verboseLoggingEnabled: boolean;
    enabledTypes: CustomerDeviceLoggingFeatureTypes[];
}

// ../NebulaAPI.Domain/Models/ClientConfiguration/CustomerDeviceLoggingConfigurations.cs
export enum CustomerDeviceLoggingFeatureTypes {
    AvlPlt = 0,
    Media = 1,
    NebulaConnection = 2,
    PushNotifications = 3,
    StateNcic = 4,
    RecordSearching = 5,
    DocumentSubscriptions = 6,
}

// ../NebulaAPI.Domain/Models/Reports/CustomerUserLicenseInfo.cs
export interface CustomerUserLicenseInfo {
    customerAgencyId: string;
    productLine: CustomerProductLine;
    connectorType: ConnectorType;
    customerName: string;
    agencyName: string;
    agencyId: string;
    licensedAgencyId: string;
    licensedAgencyDocumentId: string;
    userId: string;
    name: string;
    fullName: string;
    email: string;
    cadEmail: string;
    isLicensed: boolean;
    invalidLicense: boolean;
    isAdmin: boolean;
    shardId: number;
}

// ../NebulaAPI.Domain/Models/Reports/ConnectorReportItem.cs
export interface ConnectorReportItem {
    customerAgencyId: string;
    agencyName: string;
    customerInterfaceId?: string;
    customerInterfaceName: string;
    productLine: string;
    connectorVersion: string;
    connectorAbstractionVersion: string;
    connectorType: ConnectorType;
    certificateStatus: string;
    expiresOn?: string;
    notes: string;
    interfaceFeatures: string;
    supportCreatedOn?: string;
    supportUpdatedOn?: string;
    createdBySupportUserId?: string;
    createdBySupportUserName: string;
    updatedBySupportUserId?: string;
    updatedBySupportUserName: string;
}

// ../NebulaAPI.Domain/Models/Reports/CustomerAgencyReportItem.cs
export interface CustomerAgencyReportItem {
    customerAgencyId: string;
    name: string;
    productLine: CustomerProductLine;
    connectorType: ConnectorType;
    notes: string;
    active: boolean;
    supportCreatedOn?: string;
    supportUpdatedOn?: string;
    createdBySupportUserId?: string;
    createdBySupportUserName: string;
    updatedBySupportUserId?: string;
    updatedBySupportUserName: string;
    databaseShardId?: number;
    licensedAgencyCount: number;
    connectorCount: number;
}

// ../NebulaAPI.Domain/Models/InterfaceConfiguration/CustomerInterfaceConfigurationKey.cs
export enum CustomerInterfaceConfigurationKey {
    CustomerInterfaceLogging = 0,
    PushIntervalInMilliseconds = 1,
    CommunicationNebulaResponseTimeoutInMilliSeconds = 2,
    CommunicationMaxNebulaImportExecutionDelays = 3,
    CommunicationAllowNebulaToRequestMoreTime = 4,
    MorseLSupportMaxBatchByteSize = 5,
    MorseLMaxRecordBatch = 6,
    MorseLMaxRecordBatchByteSize = 7,
    MorseLTimeoutInMilliSeconds = 8,
    ExistenceCheckPeriodInSeconds = 9,
    PositionPushIntervalInSeconds = 10,
    InitialBackoffInSeconds = 11,
    MaxBackoffInSeconds = 12,
    MaxRecoverAttempts = 13,
    DefaultDocumentSourceCacheCapacity = 14,
    ImportFailureCacheCapacity = 15,
    ImportFailureThreshold = 16,
    DangerouslyEnableDebugLoggingForPatching = 17,
    DisableDocumentPatching = 18,
    FileUploadEnabled = 19,
    FileUploadImageMaxSizeBytes = 20,
    FileUploadAudioMaxSizeBytes = 21,
    FileUploadVideoMaxSizeBytes = 22,
    FileUploadDocumentMaxSizeBytes = 23,
    FileUploadFailureThreshold = 24,
    FileUploadQueueSecondsBetweenFailures = 25,
    FileUploadWorkers = 26,
}

// ../NebulaAPI.Domain/Models/InterfaceConfiguration/CustomerInterfaceLogLevel.cs
export enum CustomerInterfaceLogLevel {
    Verbose = 0,
    Debug = 1,
    Information = 2,
    Warning = 3,
    Error = 4,
    Fatal = 5,
}

// ../NebulaAPI.Domain/Models/Documents/Agency.cs
export interface Agency extends BaseDocumentModel, INebulaAddressableEntity<Guid> {
    ori: string;
    name: string;
    abbreviation: string;
    type: string;
    isDispatchAgency: boolean;
    isResponderAgency: boolean;
    sortOrder: number;
    productLine: string;
}

// ../NebulaAPI.Domain/DataMigration/MigrationContext.cs
export interface MigrationContext {
    id: string;
    customerAgencyId: string;
    stage: MigrationStage;
    stageStatus: MigrationStageStatus;
    documentMappingUpdatedOn?: string;
    error: string;
    createdOn: string;
    updatedOn: string;
    cancelledOn?: string;
}

// ../NebulaAPI.Domain/DataMigration/MigrationContext.cs
export interface MigrationResult {
    errors: ErrorEvent[];
    agencyLicenseEvents: AgencyLicenseEvents;
    userLicenseEvents: UserLicenseEvents;
    deviceLicenseEvents: DeviceLicenseEvents;
    createdAgencyLicenseCount: number;
    removedAgencyLicenseCount: number;
}

// ../NebulaAPI.Domain/DataMigration/MigrationContext.cs
export interface AgencyLicenseEvents {
    removedEvents: AgencyLicenseRemovedEvent[];
    createdEvents: AgencyLicenseCreatedEvent[];
    creationSkippedEvents: AgencyLicenseCreationSkippedEvent[];
    hasEvents: boolean;
}

// ../NebulaAPI.Domain/DataMigration/MigrationContext.cs
export interface UserLicenseEvents {
    removedEvents: UserLicenseRemovedEvent[];
    movedEvents: UserLicenseMovedEvent[];
    hasEvents: boolean;
}

// ../NebulaAPI.Domain/DataMigration/MigrationContext.cs
export interface DeviceLicenseEvents {
    removedEvents: DeviceLicenseRemovedEvent[];
    movedEvents: DeviceLicenseMovedEvent[];
    hasEvents: boolean;
}

// ../NebulaAPI.Domain/DataMigration/MigrationContext.cs
export enum MigrationStage {
    DataReset = 0,
    LicenseMigration = 1,
}

// ../NebulaAPI.Domain/DataMigration/MigrationContext.cs
export enum MigrationStageStatus {
    None = 0,
    Running = 1,
    Faulted = 2,
    Complete = 3,
}

// ../NebulaAPI.Domain/DataMigration/Events.cs
export interface ErrorEvent {
    message: string;
    exception: Error;
    event: any;
}

// ../NebulaAPI.Domain/DataMigration/Events.cs
/**
 * Occurs when a new agency license is created a mapped agency.
 */
export interface AgencyLicenseCreatedEvent {
    targetLicense: AgencyLicenseMetadata;
    originLicenses: AgencyLicenseMetadata[];
}

// ../NebulaAPI.Domain/DataMigration/Events.cs
/**
 * Occurs when a mapped agency's document is not pushed. In this case, a license cannot be created automatically.
 */
export interface AgencyLicenseCreationSkippedEvent {
    targetAgencyExternalId: string;
    originLicenses: AgencyLicenseMetadata[];
}

// ../NebulaAPI.Domain/DataMigration/Events.cs
/**
 * Occurs when a existing agency license is unmapped. In this case, the license must be removed.
 */
export interface AgencyLicenseRemovedEvent {
    license: AgencyLicenseMetadata;
}

// ../NebulaAPI.Domain/DataMigration/Events.cs
/**
 * Occurs when a existing user license is unmapped or when the user's target agency's license does not exist.
 */
export interface UserLicenseRemovedEvent {
    userLicense: UserLicenseMetadata;
    agencyLicense: AgencyLicenseMetadata;
}

// ../NebulaAPI.Domain/DataMigration/Events.cs
/**
 * Occurs when an existing user license is moved from one agency (origin) to another (target).
 */
export interface UserLicenseMovedEvent {
    userLicense: UserLicenseMetadata;
    originAgencyLicense: AgencyLicenseMetadata;
    targetAgencyLicense: AgencyLicenseMetadata;
}

// ../NebulaAPI.Domain/DataMigration/Events.cs
/**
 * Occurs when a existing device license's target agency cannot be determined.
 */
export interface DeviceLicenseRemovedEvent {
    deviceLicense: DeviceLicenseMetadata;
    agencyLicense: AgencyLicenseMetadata;
}

// ../NebulaAPI.Domain/DataMigration/Events.cs
/**
 * Occurs when an existing device license is moved from one agency (origin) to another (target).
 */
export interface DeviceLicenseMovedEvent {
    deviceLicense: DeviceLicenseMetadata;
    originAgencyLicense: AgencyLicenseMetadata;
    targetAgencyLicense: AgencyLicenseMetadata;
}

// ../NebulaAPI.Domain/DataMigration/Events.cs
/**
 * Metadata about an agency license at the time of data migration.
 */
export interface AgencyLicenseMetadata {
    licenseId?: string;
    agencyId?: string;
    externalId: string;
    name: string;
    licenseCount?: number;
}

// ../NebulaAPI.Domain/DataMigration/Events.cs
/**
 * Metadata about a user license at the time of data migration.
 */
export interface UserLicenseMetadata {
    licenseId: string;
    userId: string;
    externalId: string;
    name: string;
}

// ../NebulaAPI.Domain/DataMigration/Events.cs
/**
 * Metadata about a device license at the time of data migration.
 */
export interface DeviceLicenseMetadata {
    id: string;
    name: string;
    assignedUser: UserLicenseMetadata;
}

// ../NebulaAPI.Domain/Models/InterfaceGrainState.cs
export interface InterfaceGrainState {
    name: string;
    customerAgencyId: string;
    customerInterfaceId: string;
    updatedAt?: string;
    connectionId?: string;
    interfaceId?: string;
    state: InterfaceState;
    interfaceVersion: string;
    registration: InterfaceRegistration;
    certificateFingerprint: string;
    sslInfo: SslInfo;
}

// ../NebulaAPI.Domain/Models/InterfaceGrainState.cs
export interface InterfaceStatusHistoryGrainState {
    connectionHistory: InterfaceConnectionHistory[];
}

// ../NebulaAPI.Domain/Models/InterfaceGrainState.cs
export interface InterfaceConnectionHistory {
    state: InterfaceState;
    reason: string;
    occurredOn: string;
}

// ../NebulaAPI.Domain/Models/InterfaceGrainState.cs
export interface OperationStatus {
    lastOpAt: string;
    wasLastOpSuccessful: boolean;
    lastOpDuration: any;
    lastSuccessfulOpWasAt?: string;
    successCount: Record<string, OperationEvent>;
    failureCount: Record<string, OperationEvent>;
}

// ../NebulaAPI.Domain/Models/InterfaceGrainState.cs
export interface OperationEvent {
    averageTime: any;
    count: number;
}

// ../NebulaAPI.Domain/Models/InterfaceGrainState.cs
export enum InterfaceOperations {
    RegisterAgency = 0,
    Ping = 1,
    PushFilterConfigurations = 2,
    PushAgencies = 3,
    PushJurisdiction1s = 4,
    PushJurisdiction2s = 5,
    PushJurisdiction3s = 6,
    PushStatuses = 7,
    PushPriorities = 8,
    PushEmergencyResourceTypes = 9,
    PushEmergencyResources = 10,
    PushUsers = 11,
    PushIncidents = 12,
    PushPositions = 13,
    AuthenticateWithAgencyResponse = 14,
    RequestMediaDownload = 15,
    ConfirmMediaProcessed = 16,
    CaseSearchResponse = 17,
    AcknowlegeMediaRequest = 18,
    PushCommands = 19,
    PushTransitions = 20,
    PushGlobalConfiguration = 21,
    ExecuteCommandResponse = 22,
    UserAssignmentEmergencyResourceResponse = 23,
    PushUnknownIdentities = 24,
    PushIncidentIdentities = 25,
    PushEmergencyResourceIdentities = 26,
    PushMessageCenterDocuments = 27,
    PushQuerySpecifications = 28,
    ExecuteQueryResponse = 29,
    PushInterconnectedCadConfigurations = 30,
    DeAuthenticatedFromAgencyResponse = 31,
    PushFiles = 32,
    PushSignatureCollectionRequests = 33,
}

// ../NebulaAPI.Domain/Database/NewCustomerAgencyShardMappingRule.cs
export interface NewCustomerAgencyShardMappingRule {
    context: NewCustomerAgencyContext;
    shardId: string;
}

// ../NebulaAPI.Domain/Database/NewCustomerAgencyContext.cs
export interface NewCustomerAgencyContext {
    productLine: CustomerProductLine;
    connectorType: ConnectorType;
}

// ../NebulaAPI.Domain/Database/NewCustomerAgencyContext.cs
export interface NewCustomerAgencyContextExtensions {
}
